// ════════════════════════════════════════════════════════════
//  ieBeingModels.jsx — THE ILLEGAL EMPIRE BEING KIT
// ════════════════════════════════════════════════════════════
//  The four Anchor Beings and their three Ascensions, as code.
//  Twelve models. No .glb, no textures on disk, no asset
//  pipeline, no build step. Every surface is painted at runtime
//  onto a cached canvas and every shape is a three.js primitive.
//
//  ── WHAT THIS FILE IS FOR ───────────────────────────────────
//  Drop it into ANY Illegal Empire project and the beings look
//  EXACTLY the same as they do in Annihilation. Same silhouettes,
//  same palettes, same idle life, same everything. That is the
//  entire purpose: one file, one truth, no drift.
//
//  ── HOW TO USE IT ───────────────────────────────────────────
//    import { IEBeing } from "./ieBeingModels";
//
//    <IEBeing being="spectral_tiger" />                  // Beb
//    <IEBeing being="chaos_dragon" tier="champion" />
//    <IEBeing being="z4z" tier="lord" />
//    <IEBeing being="xulan" />
//
//  That is the whole API. It renders a being standing in its
//  neutral pose, alive and idling, at the origin, facing +Z.
//
//  What the HOST GAME does with it is the host game's business:
//  scale it, rotate it, park it in a kart, sit it on a throne,
//  pose it, animate it, light it. This file has no opinion about
//  any of that and never will. It renders the being. Nothing else.
//
//  ── REQUIREMENTS ────────────────────────────────────────────
//  react · three · @react-three/fiber
//  That is all. It imports NOTHING local — no palettes, no stats,
//  no game files — which is exactly what makes it portable.
//
//  ── THE ANTI-DRIFT LAW ──────────────────────────────────────
//  This file is CROSS-GAME CANONICAL, same standing as
//  achievements.js and TheMark.js:
//    1. Byte-identical in every IE repo. No exceptions.
//    2. No local edits, ever. Change it in one place, then
//       re-vendor it out to every repo.
//    3. New beings are an APPEND, never a refactor.
//    4. Bump BEING_KIT_VERSION on every change, and let each
//       game log it at boot so a stale copy announces itself.
//
//  If a being needs to look different in one game, that game is
//  wrong, not this file.
// ════════════════════════════════════════════════════════════
import { useRef, useEffect } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";

// Stamp this in your game's boot log. A repo that prints an old
// number did not take the last re-vendor.
// 1.1.0 — THE RIG DOOR. Additive only: rig slots now carry names
// (see useBeingRig below). No geometry, no transform, no colour
// changed; every body renders exactly what it rendered at 1.0.0.
export const BEING_KIT_VERSION = "1.1.0";

// ── THE ASCENT ──────────────────────────────────────────────
// Beb -> Champion -> Lord. Three tiers. There is no fourth.
export const TIERS = { BEB: "beb", CHAMPION: "champion", LORD: "lord" };

// ── THE ROSTER ──────────────────────────────────────────────
// Canonical being ids. These NEVER change (the vyren pattern) —
// display strings retune freely, ids do not.
export const BEINGS = {
  SPECTRAL_TIGER: "spectral_tiger",   // Phantom  · Beast    · S'aolesh
  CHAOS_DRAGON:   "chaos_dragon",     // Dragon   · Dragon   · Xerath'oun
  Z4Z:            "z4z",              // Time     · Machine  · Quan-7
  XULAN:          "xulan",            // Space    · Humanoid · Os'orun-Veth
  // 3D-23 — the other eight, transcribed from their Beb cards.
  SHADOW_FOX:       "shadow_fox",       // Phantom  · Beast    · Lyf 6 / Lyt 2
  INVERTED_SERPENT: "inverted_serpent", // Phantom  · Beast    · Lyf 4 / Lyt 4
  ANTDROID:         "antdroid",         // Time     · Robot    · Lyf 5 / Lyt 3
  DG0:              "dg0",              // Time     · Robot    · Lyf 6 / Lyt 2
  H01:              "h01",              // Astro    · Humanoid · Lyf 6 / Lyt 2
  H09:              "h09",              // Space    · Humanoid · Lyf 3 / Lyt 5
  LUCKY_DRAGON:     "lucky_dragon",     // Dragon   · Dragon   · Lyf 1 / Lyt 7
  PREMIER_DRAGON:   "premier_dragon",   // Dragon   · Dragon   · Lyf 4 / Lyt 4
};

// ── THE CARD FACTS ──────────────────────────────────────────
// Transcribed from Assets/terror-assets, not remembered. Any
// viewer reads THIS instead of restating a card, so the twelve
// stay one source of truth. Flavour is verbatim off the card.
export const LINEAGE_CARD = {
  spectral_tiger:   { name: "Spectral Tiger",    ilk: "Phantom", cls: "Beast",    lyf: 2, lyt: 6, built: "beb/champion/lord" },
  chaos_dragon:     { name: "Chaos Dragon",      ilk: "Dragon",  cls: "Dragon",   lyf: 0, lyt: 8, built: "beb/champion/lord" },
  z4z:              { name: "Z4Z",               ilk: "Time",    cls: "Machine",  lyf: 0, lyt: 8, built: "beb/champion/lord" },
  xulan:            { name: "Xulan",             ilk: "Space",   cls: "Humanoid", lyf: 2, lyt: 6, built: "beb/champion/lord" },
  shadow_fox:       { name: "Shadow Fox",        ilk: "Phantom", cls: "Beast",    lyf: 6, lyt: 2, built: "beb/champion/lord",
    flavor: "Mischievous pups from birth, they are born at the Bottomless Depths of the darkest Shadow Spheres... but where you will find them? Perhaps lurking in a nearby shadow..." },
  inverted_serpent: { name: "Inverted Serpent",  ilk: "Phantom", cls: "Beast",    lyf: 4, lyt: 4, built: "beb/champion/lord",
    flavor: "Although small, do not be fooled by their appearance; these snakes, born from the Shadow Gardens, are protecters to the entrance of the Shadow Forest." },
  antdroid:         { name: "Antdroid",          ilk: "Time",    cls: "Robot",    lyf: 5, lyt: 3, built: "beb/champion/lord",
    flavor: "The most common robot Beb in the Unstable World, they only seek Anima and will eat without bias. These little droids are responsible for the consumption of 1% of the entire Unstable World." },
  dg0:              { name: "DG-0 Angel",        ilk: "Time",    cls: "Robot",    lyf: 6, lyt: 2, built: "beb/champion/lord",
    flavor: "Through tremendous effort, these robots have, over time, healed 1% of dying beings in the Unstable World." },
  h01:              { name: "H-01 Blue Giant",   ilk: "Astro",   cls: "Humanoid", lyf: 6, lyt: 2, built: "beb/champion/lord",
    flavor: "A variant of the H-01 ilk, these beings choose diplomacy as their first resort." },
  h09:              { name: "H-09 Neutron Star", ilk: "Space",   cls: "Humanoid", lyf: 3, lyt: 5, built: "beb/champion/lord",
    flavor: "A variant of the H-0 ilk, these beings tend to choose militancy as their first option." },
  lucky_dragon:     { name: "Lucky Dragon",      ilk: "Dragon",  cls: "Dragon",   lyf: 1, lyt: 7, built: "beb/champion/lord",
    flavor: "Rarely seen, these Bebs are born in the Unstable World and have a fondness for Shade." },
  premier_dragon:   { name: "Premier Dragon",    ilk: "Dragon",  cls: "Dragon",   lyf: 4, lyt: 4, built: "beb/champion/lord",
    flavor: "The First & Only Leader of the Unstable World, when born, the surrounding area is briefly affected by Bloom, which can awaken the power of Nihil within a sentient being." },
};
export const LINEAGE_IDS = Object.keys(LINEAGE_CARD);

// ── THE PALETTES ────────────────────────────────────────────
// Inlined so this file depends on nothing. These are the canon
// colours; a host game must not override them.
const BEING_PALETTE = {
  spectral_tiger: { main: "#7fe8d8", deep: "#0f2b28", glow: "#b8fff2", accent: "#dffcf6" },
  chaos_dragon:   { main: "#e84a3a", deep: "#4a120c", glow: "#ff9b2e", accent: "#ffd08a" },
  z4z:            { main: "#d8b24a", deep: "#3a3f4c", glow: "#4ae8ff", accent: "#8a93a8" },
  xulan:          { main: "#7c5cff", deep: "#1c1038", glow: "#c9b8ff", accent: "#a88cff" },
  // 3D-23 — taken off each Beb card's own art, not invented.
  shadow_fox:       { main: "#ff8a3a", deep: "#0a0a10", glow: "#ffc46a", accent: "#e8e0ff" },
  inverted_serpent: { main: "#eae6dc", deep: "#241f28", glow: "#ffd6e0", accent: "#ff8aa0" },
  antdroid:         { main: "#4a5a6a", deep: "#12161c", glow: "#ffb02e", accent: "#d8b24a" },
  dg0:              { main: "#4a9a94", deep: "#16302e", glow: "#7fe8d8", accent: "#e8544a" },
  h01:              { main: "#2e6f9e", deep: "#12293c", glow: "#4ac8ff", accent: "#ff8a3a" },
  h09:              { main: "#f0ece0", deep: "#3a332a", glow: "#ffb04a", accent: "#ff7a2e" },
  lucky_dragon:     { main: "#9a6aa8", deep: "#3a1f42", glow: "#ffb02e", accent: "#ffe08a" },
  premier_dragon:   { main: "#b06a8a", deep: "#3a1830", glow: "#ffd24a", accent: "#4ad8d0" },
};
export const getBeingPalette = (beingId) =>
  BEING_PALETTE[beingId] || { main: "#c9a84c", deep: "#241c12", glow: "#f0d060", accent: "#e8d8a8" };

// ── Seeded PRNG + cached canvas textures ────────────────────
// NEVER Math.random() in a render path — variety must be seeded
// or the same being looks different on every mount.
export function mulberry32(a) {
  return function () {
    a |= 0; a = (a + 0x6D2B79F5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
export const rnd = (seed) => mulberry32((seed * 2654435761) % 2147483647);
export function hashId(str) {
  let h = 2166136261;
  for (let i = 0; i < String(str).length; i++) { h ^= String(str).charCodeAt(i); h = Math.imul(h, 16777619); }
  return (h >>> 0);
}

// ── Texture cache — cache by key or every mount uploads canvases.
export const TEX_CACHE = new Map();
export function canvasTex(key, w, h, draw) {
  if (TEX_CACHE.has(key)) return TEX_CACHE.get(key);
  const c = document.createElement("canvas");
  c.width = w; c.height = h;
  draw(c.getContext("2d"), w, h);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  t.anisotropy = 4;
  TEX_CACHE.set(key, t);
  return t;
}

// ════════════════════════════════════════════════════════════
//  THE BEING RIG  (Batch KIT-0 — the limb-grouping pass)
// ════════════════════════════════════════════════════════════
//  WHY THIS EXISTS
//  Every being's limbs used to be carved straight into the body
//  as bare <mesh> children — nothing to grab, nothing to bend.
//  A second pose (seated, gripping a wheel, a celebration jump)
//  therefore meant REWRITING each body. Twelve rewrites, twelve
//  chances to drift away from the models caP'n already signed off.
//
//  This pass puts every limb on a HANDLE instead. No geometry
//  moved, no material changed, no position touched.
//
//  ── THE ZERO-TRANSFORM LAW ──────────────────────────────────
//  Every group added by this pass sits at the ORIGIN with
//  IDENTITY ROTATION and UNIT SCALE. A zero-transform wrapper is
//  mathematically a no-op: reparenting a mesh under it cannot
//  move, rotate, or scale that mesh. That is what makes this pass
//  provably identical rather than hopefully identical — and it is
//  why the before/after screenshots must match pixel for pixel.
//
//  If a rig group ever needs a non-zero transform, it is no longer
//  a grouping pass — it is a pose, and poses live in the pose
//  layer, never baked into the body.
//
//  ── THE OPTIONAL-SLOT LAW ───────────────────────────────────
//  The roster is FOUR ANATOMIES, not four skins on one skeleton.
//  Most of these beings have no legs at all:
//
//    Spectral Tiger (Beast) — Beb/Champion are true quadrupeds.
//      The LORD is already seated (haunches + upright chest) and
//      its forelegs dissolve into pooled smoke — no ankle exists.
//    Chaos Dragon (Dragon) — the Beb is an already-seated
//      hatchling; the Champion is ROBED, shoulders to the ground,
//      with only clawed feet at the hem; the Lord is an
//      already-seated presiding mass.
//    Z4Z (Machine) — robe of plate / robe / faulds-skirt. NO LEGS
//      AT ANY TIER. It does not fold; it settles.
//    Xulan (Humanoid) — NO LEGS AT ANY TIER, and it never had
//      them: Xulan DRIFTS. All three tiers carry the void-ring
//      marked "kept exactly" in the source. It does not sit. It
//      hovers at seat height, still drifting.
//
//  So a rig slot is present only where that anatomy has one, and
//  a pose applies only to the slots it finds. A pose that assumes
//  legs will silently do nothing to nine of these twelve bodies —
//  which is correct, not a bug.
//
//  Slots (all optional): torso · head · armL · armR · legL · legR
//                        · tail · prop
//  `prop` is a held object that must survive posing — Z4Z
//  Champion's instrument, Xulan's branch-staff. Never taken away
//  to free a hand; the being drives around it.
// ════════════════════════════════════════════════════════════
export function useBeingRig() {
  const rig = {
    torso: useRef(),
    head: useRef(),
    armL: useRef(),
    armR: useRef(),
    legL: useRef(),
    legR: useRef(),
    tail: useRef(),
    prop: useRef(),
  };

  // ── THE RIG DOOR (kit v1.1.0) ──────────────────────────────
  // Additive, and deliberately the smallest possible change: every
  // rig slot that a body actually bound gets a NAME on the object
  // it points at. Nothing moves, nothing scales, no geometry is
  // touched — the zero-transform law holds absolutely, and
  // before/after renders match pixel for pixel.
  //
  // WHY IT LIVES HERE AND NOT AT THE ~100 CALL SITES: the slots are
  // bound in three different shapes across the 36 bodies —
  //     <group ref={rig.head}>
  //     <group ref={sd < 0 ? rig.legL : rig.legR}>
  //     const armSlots = [rig.armL, rig.armR]   (driven in useFrame)
  // — so stamping names by editing JSX would mean a hundred edits in
  // three shapes, every one a chance to name the wrong side. Doing it
  // once, here, covers every body ever written against this hook,
  // including bodies that do not exist yet.
  //
  // WHAT IT UNLOCKS: a host game can now find a limb from OUTSIDE the
  // kit — `g.getObjectByName("rig_armL")` — and pose it without the
  // kit knowing or caring. Ruin Rush uses this to close forepaws on a
  // steering wheel. Nothing in Annihilation reads these names, and
  // nothing here depends on anyone doing so.
  //
  // ⚑ `if (!o.name)` is load-bearing: a host that has already named
  // an object for its OWN imperative loop (Ruin Rush drives karts by
  // name) must never have that name overwritten by the kit.
  useEffect(() => {
    Object.keys(rig).forEach((slot) => {
      const o = rig[slot].current;
      if (o && !o.name) o.name = "rig_" + slot;
    });
  });

  return rig;
}


const STB = {
  coat: "#101d22",      // the card's near-black midnight blue
  stripe: "#2fa393",    // living stripe teal
  stripeHi: "#7fe8d8",  // canon main as the stripe's hot core
  muzzle: "#9ec4bc",
  sock: "#8fd8ca",      // pale forepaws
  innerEar: "#0b171b",
  nose: "#c98a92",
  whisker: "#eafff8",
};

// The coat — midnight base, mottled, wrapped in tapering
// flame-stripes (leaf-shaped strokes with a hot core, like the
// card's fur language). One 512 canvas, cached, shared by every
// furred part; sphere/capsule UVs wrap it organically.
function stbFurTex() {
  return canvasTex("stb_fur_v1", 512, 512, (g, w, h) => {
    const base = g.createLinearGradient(0, 0, 0, h);
    base.addColorStop(0, "#142329");
    base.addColorStop(0.55, "#0f1c21");
    base.addColorStop(1, "#0a1417");
    g.fillStyle = base; g.fillRect(0, 0, w, h);
    const R = mulberry32(929);
    // fur mottle
    for (let i = 0; i < 1100; i++) {
      g.fillStyle = R() > 0.5 ? "rgba(47,163,147,0.05)" : "rgba(3,9,11,0.09)";
      g.fillRect(R() * w, R() * h, 2 + R() * 4, 2 + R() * 4);
    }
    // flame-stripes: tapered leaf strokes + a brighter core pass
    const stripe = (x, y, len, wd, ang, fill, alpha) => {
      g.save(); g.translate(x, y); g.rotate(ang);
      g.beginPath();
      g.moveTo(0, -len / 2);
      g.quadraticCurveTo(wd, -len * 0.1, 0, len / 2);
      g.quadraticCurveTo(-wd, -len * 0.1, 0, -len / 2);
      g.closePath();
      g.globalAlpha = alpha; g.fillStyle = fill; g.fill();
      g.restore(); g.globalAlpha = 1;
    };
    for (let i = 0; i < 18; i++) {
      const x = R() * w, y = R() * h;
      const len = 70 + R() * 130, wd = 9 + R() * 11, ang = (R() - 0.5) * 1.0;
      stripe(x, y, len, wd, ang, STB.stripe, 0.85);
      stripe(x, y, len * 0.55, wd * 0.42, ang, STB.stripeHi, 0.45);
    }
  });
}

// The eyes — the card's whole face. Radial amber furnace with a
// soft slit pupil and a specular star, drawn once, glows via
// meshBasicMaterial + toneMapped={false}.
function stbEyeTex() {
  return canvasTex("stb_eye_v1", 128, 128, (g) => {
    const r = g.createRadialGradient(64, 58, 4, 64, 64, 62);
    r.addColorStop(0, "#fff6d0");
    r.addColorStop(0.35, "#ffd257");
    r.addColorStop(0.7, "#e89a2e");
    r.addColorStop(0.92, "#6b3c0c");
    r.addColorStop(1, "#170f05");
    g.fillStyle = r; g.beginPath(); g.arc(64, 64, 62, 0, 7); g.fill();
    // slit pupil, soft-edged
    g.fillStyle = "rgba(14,10,6,0.92)";
    g.beginPath(); g.ellipse(64, 66, 9, 30, 0, 0, 7); g.fill();
    g.fillStyle = "rgba(255,246,208,0.22)";
    g.beginPath(); g.ellipse(64, 48, 4, 9, 0, 0, 7); g.fill();
    // specular star
    g.fillStyle = "rgba(255,255,255,0.9)";
    g.beginPath(); g.arc(47, 43, 7, 0, 7); g.fill();
    g.fillStyle = "rgba(255,255,255,0.5)";
    g.beginPath(); g.arc(78, 82, 3.5, 0, 7); g.fill();
  });
}

// tail as a bead-chain along a question-mark curve (kitten tail-up).
// Relative to the tail root; radius tapers to a glow-tipped end.
const STB_TAIL = [
  [0, 0.00, -0.03, 0.075],
  [0, 0.10, -0.15, 0.068],
  [0, 0.22, -0.23, 0.060],
  [0, 0.34, -0.25, 0.053],
  [0, 0.45, -0.21, 0.046],
  [0, 0.53, -0.13, 0.040],
  [0, 0.58, -0.03, 0.034],
];

export function SpectralTigerBody({ pal }) {
  pal = pal || getBeingPalette("spectral_tiger");
  const fur = stbFurTex();
  const eye = stbEyeTex();
  const rig = useBeingRig();
  const tail = rig.tail;
  // forelegs become the arm slots (a quadruped reaches with them);
  // hind legs are the leg slots. Order matches the leg table below.
  const legSlots = [rig.armL, rig.armR, rig.legL, rig.legR];
  const earL = useRef();
  const earR = useRef();
  const wisps = useRef([]);

  useFrame((s) => {
    const t = s.clock.elapsedTime;
    if (tail.current) {
      tail.current.rotation.x = Math.sin(t * 1.5) * 0.10;
      tail.current.rotation.z = Math.sin(t * 1.05 + 1.3) * 0.14;
    }
    if (earL.current) earL.current.rotation.z = 0.24 + Math.sin(t * 0.9) * 0.045;
    if (earR.current) earR.current.rotation.z = -0.24 - Math.sin(t * 0.9 + 2.1) * 0.045;
    // the veil's wisps orbit slow and breathe their opacity
    wisps.current.forEach((m, i) => {
      if (!m) return;
      const p = t * 0.6 + i * 2.1;
      m.position.set(Math.cos(p) * 0.55, 0.55 + Math.sin(t * 1.3 + i) * 0.18, Math.sin(p) * 0.55 + 0.05);
      m.material.opacity = 0.32 + Math.sin(t * 2 + i) * 0.18;
    });
  });

  // shared coat material props (spectral: a whisper translucent,
  // stripes ember faintly in fog)
  const coat = { map: fur, roughness: 0.95, transparent: true, opacity: 0.97, emissive: STB.stripe, emissiveIntensity: 0.06 };

  return (
    <group>
      {/* ── torso: loaf + chest floof + haunches ──
           RIG: zero-transform torso slot. Head and tail ride inside
           it, so a torso pitch carries the whole upper body. */}
      <group ref={rig.torso}>
      <mesh position={[0, 0.42, -0.02]} scale={[0.85, 0.72, 1.42]} castShadow>
        <sphereGeometry args={[0.34, 18, 14]} />
        <meshStandardMaterial {...coat} />
      </mesh>
      <mesh position={[0, 0.40, 0.40]} scale={[1, 0.92, 0.9]}>
        <sphereGeometry args={[0.24, 14, 12]} />
        <meshStandardMaterial {...coat} emissiveIntensity={0.09} />
      </mesh>
      {[-0.11, 0.11].map((ox, i) => (
        <mesh key={"fluff" + i} position={[ox, 0.30, 0.47]}>
          <sphereGeometry args={[0.12, 10, 8]} />
          <meshStandardMaterial {...coat} emissiveIntensity={0.09} />
        </mesh>
      ))}
      {[-0.17, 0.17].map((ox, i) => (
        <mesh key={"haunch" + i} position={[ox, 0.34, -0.34]} scale={[0.8, 1, 1.15]}>
          <sphereGeometry args={[0.20, 12, 10]} />
          <meshStandardMaterial {...coat} />
        </mesh>
      ))}

      {/* ── the head — big, kitten-proportioned, the card's face ── */}
      <group ref={rig.head} position={[0, 0.68, 0.52]}>
        <mesh scale={[1, 0.94, 0.9]} castShadow>
          <sphereGeometry args={[0.30, 18, 14]} />
          <meshStandardMaterial {...coat} />
        </mesh>
        {/* cheek floof */}
        {[-1, 1].map((sd) => (
          <mesh key={"cheek" + sd} position={[sd * 0.24, -0.07, 0.05]} rotation={[0, 0, sd * -1.9]}>
            <coneGeometry args={[0.10, 0.22, 7]} />
            <meshStandardMaterial {...coat} />
          </mesh>
        ))}
        {/* muzzle + nose */}
        <mesh position={[0, -0.08, 0.24]} scale={[1.15, 0.78, 0.9]}>
          <sphereGeometry args={[0.13, 12, 10]} />
          <meshStandardMaterial color={STB.muzzle} roughness={0.9} transparent opacity={0.97} />
        </mesh>
        <mesh position={[0, -0.045, 0.355]} scale={[1.2, 0.8, 0.8]}>
          <sphereGeometry args={[0.034, 8, 8]} />
          <meshStandardMaterial color={STB.nose} roughness={0.6} />
        </mesh>
        {/* eye sockets, then the golden lamps */}
        {[-1, 1].map((sd) => (
          <group key={"eye" + sd}>
            <mesh position={[sd * 0.135, 0.05, 0.17]} scale={[1, 1.1, 0.6]}>
              <sphereGeometry args={[0.10, 10, 8]} />
              <meshStandardMaterial color={STB.coat} roughness={1} />
            </mesh>
            <mesh position={[sd * 0.13, 0.055, 0.272]} rotation={[0, sd * 0.26, 0]}>
              <circleGeometry args={[0.082, 20]} />
              <meshBasicMaterial map={eye} toneMapped={false} />
            </mesh>
          </group>
        ))}
        {/* ears — rounded, dark inner, ref'd for the idle twitch */}
        <group ref={earL} position={[-0.19, 0.25, -0.02]}>
          <mesh castShadow><coneGeometry args={[0.12, 0.26, 8]} /><meshStandardMaterial {...coat} /></mesh>
          <mesh position={[0, 0.02, 0.045]}><coneGeometry args={[0.065, 0.15, 7]} /><meshStandardMaterial color={STB.innerEar} roughness={1} /></mesh>
          <mesh position={[0, 0.13, 0]}><sphereGeometry args={[0.05, 8, 6]} /><meshStandardMaterial {...coat} /></mesh>
        </group>
        <group ref={earR} position={[0.19, 0.25, -0.02]}>
          <mesh castShadow><coneGeometry args={[0.12, 0.26, 8]} /><meshStandardMaterial {...coat} /></mesh>
          <mesh position={[0, 0.02, 0.045]}><coneGeometry args={[0.065, 0.15, 7]} /><meshStandardMaterial color={STB.innerEar} roughness={1} /></mesh>
          <mesh position={[0, 0.13, 0]}><sphereGeometry args={[0.05, 8, 6]} /><meshStandardMaterial {...coat} /></mesh>
        </group>
        {/* whiskers */}
        {[-1, 1].map((sd) =>
          [0, 1, 2].map((i) => (
            <mesh
              key={"wh" + sd + i}
              position={[sd * 0.20, -0.075 - i * 0.022, 0.26]}
              rotation={[0, sd * -0.45, sd * (0.10 - i * 0.11)]}
            >
              <boxGeometry args={[0.26, 0.006, 0.006]} />
              <meshBasicMaterial color={STB.whisker} transparent opacity={0.6} />
            </mesh>
          ))
        )}
      </group>
      </group>

      {/* ── legs: capsule limbs, mitten paws (pale front socks) ──
           RIG: index order is [foreL, foreR, hindL, hindR] → the
           arm slots then the leg slots. Positions untouched. */}
      {[[-0.16, 0.30, true], [0.16, 0.30, true], [-0.17, -0.36, false], [0.17, -0.36, false]].map(([ox, oz, front], i) => (
        <group key={"leg" + i} ref={legSlots[i]} position={[ox, 0, oz]}>
          <mesh position={[0, 0.18, 0]} castShadow>
            <capsuleGeometry args={[0.06, 0.16, 4, 8]} />
            <meshStandardMaterial {...coat} />
          </mesh>
          <mesh position={[0, 0.06, 0.03]} scale={[1, 0.7, 1.15]}>
            <sphereGeometry args={[0.08, 10, 8]} />
            <meshStandardMaterial color={front ? STB.sock : STB.coat} map={front ? null : fur} roughness={0.95} transparent opacity={0.97} />
          </mesh>
        </group>
      ))}

      {/* ── the tail — bead-chain question mark, glow-tipped ── */}
      <group ref={tail} position={[0, 0.46, -0.55]}>
        {STB_TAIL.map(([x, y, z, r], i) => {
          const tip = i === STB_TAIL.length - 1;
          return (
            <mesh key={"t" + i} position={[x, y, z]}>
              <sphereGeometry args={[r, 10, 8]} />
              <meshStandardMaterial
                {...coat}
                emissive={tip ? pal.glow : STB.stripe}
                emissiveIntensity={tip ? 0.9 : 0.06}
                toneMapped={!tip}
              />
            </mesh>
          );
        })}
      </group>

      {/* ── the spectral veil: rim aura + three drifting wisps ── */}
      <mesh position={[0, 0.52, 0.08]} scale={[0.92, 0.78, 1.28]}>
        <sphereGeometry args={[0.62, 16, 12]} />
        <meshBasicMaterial color={pal.glow} transparent opacity={0.07} side={THREE.BackSide} toneMapped={false} depthWrite={false} />
      </mesh>
      {[0, 1, 2].map((i) => (
        <mesh key={"wisp" + i} ref={(el) => (wisps.current[i] = el)}>
          <sphereGeometry args={[0.028, 6, 6]} />
          <meshBasicMaterial color={pal.glow} transparent opacity={0.4} toneMapped={false} depthWrite={false} />
        </mesh>
      ))}

      {/* under-glow (kept from the old body — light budget unchanged) */}
      <pointLight position={[0, 0.55, 0.45]} color={pal.glow} intensity={0.55} distance={2.6} />
    </group>
  );
}

// ────────────────────────────────────────────────────────────
// SPECTRAL TIGER CHAMPION — the Ascent worn as flesh (Batch 3D-8).
// Built to the card (Terror TCG STL-04): the kitten grown lean
// and pale — a ghost-white coat broken by midnight rosettes,
// burning amber lamps in shadowed sockets, three brow marks,
// and the card's signature: living smoke tendrils pouring off
// the head and neck like spectral locks. Adolescent, hungry —
// "an insatiable appetite for their own class."
//
// Contract: same component pattern as the Beb (pal in, group
// out), footprint one head taller (~0.92 head height) — Ascent
// ring, GroundBlob, StrikeFX rakes, and labels all still land.
// Colors are display-only dressing over the canon palette.
// ────────────────────────────────────────────────────────────
const STC = {
  coat: "#c8ded7",      // the card's pale spectral white-mint
  rosette: "#16333d",   // midnight rosette spots
  browMark: "#1d4a5a",  // the three brow dots
  muzzle: "#e4f2ec",
  innerEar: "#0b171b",
  nose: "#8a5560",
  whisker: "#eafff8",
  smoke: "#dff5ef",     // the neck ring
};

// Pale coat broken by midnight rosettes — one 512 canvas, cached,
// seeded, shared by every furred part.
function stcFurTex() {
  return canvasTex("stc_fur_v1", 512, 512, (g, w, h) => {
    const base = g.createLinearGradient(0, 0, 0, h);
    base.addColorStop(0, "#dcefe9");
    base.addColorStop(0.5, "#c4dcd5");
    base.addColorStop(1, "#a3c2bb");
    g.fillStyle = base; g.fillRect(0, 0, w, h);
    const R = mulberry32(1747);
    // pale fur mottle
    for (let i = 0; i < 900; i++) {
      g.fillStyle = R() > 0.5 ? "rgba(255,255,255,0.06)" : "rgba(47,163,147,0.05)";
      g.fillRect(R() * w, R() * h, 2 + R() * 4, 2 + R() * 4);
    }
    // rosettes — broken rings of dark blobs around a pale heart
    for (let i = 0; i < 26; i++) {
      const cx = R() * w, cy = R() * h, rr = 10 + R() * 14;
      const blobs = 3 + Math.floor(R() * 3);
      for (let b = 0; b < blobs; b++) {
        const a = R() * Math.PI * 2;
        g.fillStyle = "rgba(22,51,61,0.75)";
        g.beginPath();
        g.ellipse(cx + Math.cos(a) * rr, cy + Math.sin(a) * rr, 4 + R() * 5, 3 + R() * 4, a, 0, 7);
        g.fill();
      }
    }
    // a few faint teal ghost-streaks, the lineage showing through
    for (let i = 0; i < 6; i++) {
      const x = R() * w, y = R() * h;
      g.save(); g.translate(x, y); g.rotate((R() - 0.5) * 1.2);
      g.globalAlpha = 0.18; g.fillStyle = "#2fa393";
      g.beginPath();
      g.moveTo(0, -50); g.quadraticCurveTo(8, -10, 0, 50); g.quadraticCurveTo(-8, -10, 0, -50);
      g.closePath(); g.fill(); g.restore(); g.globalAlpha = 1;
    }
  });
}

// Amber furnace eyes — hotter and angrier than the kitten's, a
// narrower slit, a wider burnt rim. The card's whole menace.
function stcEyeTex() {
  return canvasTex("stc_eye_v1", 128, 128, (g) => {
    const r = g.createRadialGradient(64, 58, 3, 64, 64, 62);
    r.addColorStop(0, "#fff2c8");
    r.addColorStop(0.3, "#ffb547");
    r.addColorStop(0.62, "#e07018");
    r.addColorStop(0.88, "#571f04");
    r.addColorStop(1, "#120a04");
    g.fillStyle = r; g.beginPath(); g.arc(64, 64, 62, 0, 7); g.fill();
    g.fillStyle = "rgba(12,8,5,0.94)";
    g.beginPath(); g.ellipse(64, 66, 7, 32, 0, 0, 7); g.fill();
    g.fillStyle = "rgba(255,242,200,0.2)";
    g.beginPath(); g.ellipse(64, 46, 3.5, 8, 0, 0, 7); g.fill();
    g.fillStyle = "rgba(255,255,255,0.85)";
    g.beginPath(); g.arc(46, 42, 6, 0, 7); g.fill();
  });
}

// The twin tail — caP'n's call (Jul 21, third pass): all mane/robe
// fluff scrapped. The Champion's signature is the TAIL: two tails
// intertwined around a shared rising curve, fusing into ONE at the
// crest, then splitting back into two at the tip. Deterministic —
// two bead-strands offset from a spine by a separation that ramps
// to zero (the fuse) and reopens at the end (the split).
const STC_TWIN_TAIL = (() => {
  const N = 14;
  const strands = [[], []];
  for (let i = 0; i <= N; i++) {
    const f = i / N;
    // spine — the tail-up curve (relative to the tail root group)
    const sy = f * 0.64;
    const sz = -0.04 - Math.sin(f * Math.PI) * 0.22;
    // separation: apart & twisting → fused at the crest → split tips
    const sep =
      f < 0.55 ? 0.06 * Math.cos((f / 0.55) * (Math.PI / 2)) :
      f > 0.8 ? 0.05 * ((f - 0.8) / 0.2) : 0;
    const th = f * Math.PI * 3; // 1.5 turns of intertwine
    const fused = sep < 0.005;
    const r = 0.05 - f * 0.022 + (fused ? 0.012 : 0);
    strands[0].push([Math.cos(th) * sep, sy, sz + Math.sin(th) * sep, r]);
    strands[1].push([-Math.cos(th) * sep, sy, sz - Math.sin(th) * sep, r]);
  }
  return strands;
})();

// The smoke ring — the card's tendrils resolved (caP'n, Jul 21,
// final read): they aren't hair OR a mane, they form a constant
// RING around the neck. Ten billows on a circle, varied radii,
// riding a faint torus so the ring never breaks; the whole halo
// spins slowly and each billow breathes on its own fixed phase.
const STC_RING = (() => {
  const out = [];
  for (let i = 0; i < 10; i++) {
    const a = (i / 10) * Math.PI * 2;
    out.push({ a, r: 0.055 + Math.sin(i * 2.4) * 0.018 });
  }
  return out;
})();

export function SpectralTigerChampionBody({ pal }) {
  pal = pal || getBeingPalette("spectral_tiger");
  const fur = stcFurTex();
  const eye = stcEyeTex();
  const rig = useBeingRig();
  const tail = rig.tail;
  const legSlots = [rig.armL, rig.armR, rig.legL, rig.legR];
  const earL = useRef();
  const earR = useRef();
  const wisps = useRef([]);
  const ring = useRef();
  const ringBillows = useRef([]);

  useFrame((s) => {
    const t = s.clock.elapsedTime;
    if (tail.current) {
      tail.current.rotation.x = Math.sin(t * 1.3) * 0.09;
      tail.current.rotation.z = Math.sin(t * 0.9 + 1.7) * 0.12;
    }
    if (earL.current) earL.current.rotation.z = 0.20 + Math.sin(t * 1.1) * 0.04;
    if (earR.current) earR.current.rotation.z = -0.20 - Math.sin(t * 1.1 + 2.4) * 0.04;
    // the smoke ring — a constant slow circulation around the neck
    if (ring.current) ring.current.rotation.y = t * 0.45;
    ringBillows.current.forEach((m, i) => {
      if (!m) return;
      const p = 1 + Math.sin(t * 1.3 + i * 1.7) * 0.16;
      m.scale.setScalar(p);
      m.material.opacity = 0.38 + Math.sin(t * 1.1 + i * 0.9) * 0.1;
    });
    wisps.current.forEach((m, i) => {
      if (!m) return;
      const p = t * 0.55 + i * 1.6;
      m.position.set(Math.cos(p) * 0.62, 0.62 + Math.sin(t * 1.2 + i) * 0.2, Math.sin(p) * 0.62 + 0.05);
      m.material.opacity = 0.3 + Math.sin(t * 1.8 + i) * 0.16;
    });
  });

  const coat = { map: fur, roughness: 0.95, transparent: true, opacity: 0.97, emissive: "#2fa393", emissiveIntensity: 0.04 };

  return (
    <group>
      {/* ── torso: longer, leaner loaf + chest + haunches ──
           RIG: zero-transform torso slot; head + neck ring ride it. */}
      <group ref={rig.torso}>
      <mesh position={[0, 0.55, -0.04]} scale={[0.78, 0.7, 1.52]} castShadow>
        <sphereGeometry args={[0.34, 18, 14]} />
        <meshStandardMaterial {...coat} />
      </mesh>
      <mesh position={[0, 0.55, 0.44]} scale={[0.95, 0.92, 0.9]}>
        <sphereGeometry args={[0.22, 14, 12]} />
        <meshStandardMaterial {...coat} emissiveIntensity={0.07} />
      </mesh>
      {[-0.15, 0.15].map((ox, i) => (
        <mesh key={"haunch" + i} position={[ox, 0.46, -0.42]} scale={[0.8, 1, 1.15]}>
          <sphereGeometry args={[0.19, 12, 10]} />
          <meshStandardMaterial {...coat} />
        </mesh>
      ))}

      {/* ── the head — leaner skull, shadowed sockets, the marks ── */}
      <group ref={rig.head} position={[0, 0.92, 0.60]}>
        <mesh scale={[0.94, 0.96, 0.92]} castShadow>
          <sphereGeometry args={[0.27, 18, 14]} />
          <meshStandardMaterial {...coat} />
        </mesh>
        {/* the three brow marks */}
        {[[-0.07, 0.14, 0.20], [0, 0.165, 0.205], [0.07, 0.14, 0.20]].map(([x, y, z], i) => (
          <mesh key={"brow" + i} position={[x, y, z]}>
            <sphereGeometry args={[0.024, 8, 6]} />
            <meshStandardMaterial color={STC.browMark} roughness={1} emissive={pal.glow} emissiveIntensity={0.2} />
          </mesh>
        ))}
        {/* muzzle + nose */}
        <mesh position={[0, -0.08, 0.21]} scale={[1.1, 0.72, 0.9]}>
          <sphereGeometry args={[0.12, 12, 10]} />
          <meshStandardMaterial color={STC.muzzle} roughness={0.9} transparent opacity={0.97} />
        </mesh>
        <mesh position={[0, -0.05, 0.315]} scale={[1.2, 0.8, 0.8]}>
          <sphereGeometry args={[0.032, 8, 8]} />
          <meshStandardMaterial color={STC.nose} roughness={0.6} />
        </mesh>
        {/* shadowed sockets, then the amber lamps */}
        {[-1, 1].map((sd) => (
          <group key={"eye" + sd}>
            <mesh position={[sd * 0.125, 0.05, 0.155]} scale={[1.05, 1.15, 0.6]}>
              <sphereGeometry args={[0.095, 10, 8]} />
              <meshStandardMaterial color={STC.rosette} roughness={1} />
            </mesh>
            <mesh position={[sd * 0.12, 0.055, 0.252]} rotation={[0, sd * 0.26, 0]}>
              <circleGeometry args={[0.075, 20]} />
              <meshBasicMaterial map={eye} toneMapped={false} />
            </mesh>
          </group>
        ))}
        {/* ears — taller, alert */}
        <group ref={earL} position={[-0.17, 0.25, -0.03]}>
          <mesh castShadow><coneGeometry args={[0.10, 0.28, 8]} /><meshStandardMaterial {...coat} /></mesh>
          <mesh position={[0, 0.02, 0.04]}><coneGeometry args={[0.055, 0.17, 7]} /><meshStandardMaterial color={STC.innerEar} roughness={1} /></mesh>
          <mesh position={[0, 0.14, 0]}><sphereGeometry args={[0.042, 8, 6]} /><meshStandardMaterial {...coat} /></mesh>
        </group>
        <group ref={earR} position={[0.17, 0.25, -0.03]}>
          <mesh castShadow><coneGeometry args={[0.10, 0.28, 8]} /><meshStandardMaterial {...coat} /></mesh>
          <mesh position={[0, 0.02, 0.04]}><coneGeometry args={[0.055, 0.17, 7]} /><meshStandardMaterial color={STC.innerEar} roughness={1} /></mesh>
          <mesh position={[0, 0.14, 0]}><sphereGeometry args={[0.042, 8, 6]} /><meshStandardMaterial {...coat} /></mesh>
        </group>
        {/* whiskers */}
        {[-1, 1].map((sd) =>
          [0, 1].map((i) => (
            <mesh
              key={"wh" + sd + i}
              position={[sd * 0.19, -0.07 - i * 0.025, 0.23]}
              rotation={[0, sd * -0.45, sd * (0.08 - i * 0.13)]}
            >
              <boxGeometry args={[0.26, 0.006, 0.006]} />
              <meshBasicMaterial color={STC.whisker} transparent opacity={0.55} />
            </mesh>
          ))
        )}
      </group>

      {/* ── the smoke ring — the constant halo circling the neck ── */}
      <group position={[0, 0.78, 0.48]} rotation={[-0.45, 0, 0]}>
        {/* faint continuous band so the ring never breaks */}
        <mesh rotation={[Math.PI / 2, 0, 0]}>
          <torusGeometry args={[0.24, 0.045, 8, 24]} />
          <meshBasicMaterial color={STC.smoke} transparent opacity={0.16} depthWrite={false} toneMapped={false} />
        </mesh>
        {/* the circulating billows */}
        <group ref={ring}>
          {STC_RING.map((b, i) => (
            <mesh
              key={"ring" + i}
              ref={(el) => (ringBillows.current[i] = el)}
              position={[Math.cos(b.a) * 0.24, 0, Math.sin(b.a) * 0.24]}
            >
              <sphereGeometry args={[b.r, 8, 6]} />
              <meshBasicMaterial color={STC.smoke} transparent opacity={0.4} depthWrite={false} toneMapped={false} />
            </mesh>
          ))}
        </group>
      </group>

      </group>

      {/* ── legs: longer capsules, rosetted fur down to the paws ──
           RIG: [foreL, foreR, hindL, hindR] → arm slots, leg slots. */}
      {[[-0.15, 0.34], [0.15, 0.34], [-0.16, -0.42], [0.16, -0.42]].map(([ox, oz], i) => (
        <group key={"leg" + i} ref={legSlots[i]} position={[ox, 0, oz]}>
          <mesh position={[0, 0.22, 0]} castShadow>
            <capsuleGeometry args={[0.055, 0.24, 4, 8]} />
            <meshStandardMaterial {...coat} />
          </mesh>
          <mesh position={[0, 0.06, 0.03]} scale={[1, 0.7, 1.15]}>
            <sphereGeometry args={[0.075, 10, 8]} />
            <meshStandardMaterial {...coat} />
          </mesh>
        </group>
      ))}

      {/* ── the twin tail — intertwined, fused at the crest, split tips ── */}
      <group ref={tail} position={[0, 0.56, -0.66]}>
        {STC_TWIN_TAIL.map((strand, si) => (
          <group key={"strand" + si}>
            {strand.map(([x, y, z, r], i, arr) => {
              const tip = i === arr.length - 1;
              return (
                <mesh key={"t" + i} position={[x, y, z]}>
                  <sphereGeometry args={[r, 10, 8]} />
                  <meshStandardMaterial
                    {...coat}
                    emissive={tip ? pal.glow : "#2fa393"}
                    emissiveIntensity={tip ? 0.9 : 0.05}
                    toneMapped={!tip}
                  />
                </mesh>
              );
            })}
          </group>
        ))}
      </group>

      {/* ── the spectral veil: pale rim aura + four wisps ── */}
      <mesh position={[0, 0.62, 0.06]} scale={[0.92, 0.82, 1.32]}>
        <sphereGeometry args={[0.66, 16, 12]} />
        <meshBasicMaterial color={pal.glow} transparent opacity={0.08} side={THREE.BackSide} toneMapped={false} depthWrite={false} />
      </mesh>
      {[0, 1, 2, 3].map((i) => (
        <mesh key={"wisp" + i} ref={(el) => (wisps.current[i] = el)}>
          <sphereGeometry args={[0.026, 6, 6]} />
          <meshBasicMaterial color={pal.glow} transparent opacity={0.36} toneMapped={false} depthWrite={false} />
        </mesh>
      ))}

      {/* under-glow (light budget: one, same as the Beb) */}
      <pointLight position={[0, 0.65, 0.45]} color={pal.glow} intensity={0.55} distance={2.8} />
    </group>
  );
}

// ────────────────────────────────────────────────────────────
// SPECTRAL TIGER LORD — the Ascent completed (Batch 3D-8).
// Built to the card (Terror TCG STL-07): King of the Spirit
// Worlds. A seated regal mass in a deep flowing coat, crowned
// by a radiating fan of spectral plumes; SIX eyes — the golden
// pair, two vast outer orbs set in dark sockets, and the third
// eye burning on the brow; white cheek ruffs and long whiskers;
// forelegs that dissolve into pooling smoke where paws should
// be. It does not prowl. It presides.
//
// Contract: seated footprint inside the Beb/Champion disc; head
// at ~1.08 so the worn Lord crown (PlayerBody, y=1.5) lands at
// the mane's crest, framed by the fan. One pointLight.
// ────────────────────────────────────────────────────────────
const STL = {
  coat: "#16323e",      // the card's deep spirit-blue
  ruff: "#e8f4ee",      // white cheek fur
  muzzle: "#dcece6",
  nose: "#8a5560",
  whisker: "#eafff8",
  smoke: "#dff5ef",
  socket: "#0a1418",    // the dark orb sockets
};

// Deep flowing coat — long vertical spirit-streaks over midnight
// blue, pale cores; the card's living-current fur.
function stlFurTex() {
  return canvasTex("stl_fur_v1", 512, 512, (g, w, h) => {
    const base = g.createLinearGradient(0, 0, 0, h);
    base.addColorStop(0, "#1b3d4a");
    base.addColorStop(0.5, "#122a34");
    base.addColorStop(1, "#0b1b22");
    g.fillStyle = base; g.fillRect(0, 0, w, h);
    const R = mulberry32(3121);
    for (let i = 0; i < 900; i++) {
      g.fillStyle = R() > 0.5 ? "rgba(47,163,147,0.06)" : "rgba(3,9,11,0.09)";
      g.fillRect(R() * w, R() * h, 2 + R() * 4, 2 + R() * 4);
    }
    const streak = (x, y, len, wd, ang, fill, alpha) => {
      g.save(); g.translate(x, y); g.rotate(ang);
      g.beginPath();
      g.moveTo(0, -len / 2);
      g.quadraticCurveTo(wd, -len * 0.1, 0, len / 2);
      g.quadraticCurveTo(-wd, -len * 0.1, 0, -len / 2);
      g.closePath();
      g.globalAlpha = alpha; g.fillStyle = fill; g.fill();
      g.restore(); g.globalAlpha = 1;
    };
    for (let i = 0; i < 22; i++) {
      const x = R() * w, y = R() * h;
      const len = 120 + R() * 90, wd = 10 + R() * 8, ang = (R() - 0.5) * 0.5;
      streak(x, y, len, wd, ang, "#2c7f8f", 0.5);
      streak(x, y, len * 0.55, wd * 0.4, ang, "#9fdcea", 0.3);
    }
    for (let i = 0; i < 5; i++) {
      const x = R() * w, y = R() * h;
      streak(x, y, 90 + R() * 60, 6 + R() * 4, (R() - 0.5) * 0.5, "#eafff8", 0.22);
    }
  });
}

// The golden regard — round pupil, serene. Shared by the main
// pair and the vast outer orbs.
function stlEyeTex() {
  return canvasTex("stl_eye_v1", 128, 128, (g) => {
    const r = g.createRadialGradient(64, 60, 4, 64, 64, 62);
    r.addColorStop(0, "#fff8d8");
    r.addColorStop(0.35, "#ffd257");
    r.addColorStop(0.7, "#d98d1e");
    r.addColorStop(0.92, "#5a350a");
    r.addColorStop(1, "#120c04");
    g.fillStyle = r; g.beginPath(); g.arc(64, 64, 62, 0, 7); g.fill();
    g.fillStyle = "rgba(14,10,6,0.9)";
    g.beginPath(); g.arc(64, 66, 13, 0, 7); g.fill();
    g.fillStyle = "rgba(255,255,255,0.85)";
    g.beginPath(); g.arc(48, 44, 6, 0, 7); g.fill();
  });
}

// The third eye — the golden regard ringed in spirit-teal.
function stlThirdEyeTex() {
  return canvasTex("stl_eye3_v1", 128, 128, (g) => {
    const r = g.createRadialGradient(64, 62, 3, 64, 64, 62);
    r.addColorStop(0, "#fff8d8");
    r.addColorStop(0.3, "#ffd257");
    r.addColorStop(0.58, "#d98d1e");
    r.addColorStop(0.78, "#1c5a66");
    r.addColorStop(0.92, "#7fe8d8");
    r.addColorStop(1, "#0a1418");
    g.fillStyle = r; g.beginPath(); g.arc(64, 64, 62, 0, 7); g.fill();
    g.fillStyle = "rgba(14,10,6,0.9)";
    g.beginPath(); g.ellipse(64, 65, 8, 16, 0, 0, 7); g.fill();
    g.fillStyle = "rgba(255,255,255,0.8)";
    g.beginPath(); g.arc(52, 48, 4.5, 0, 7); g.fill();
  });
}

// The mane fan — two arcs of plumes, computed deterministically.
// Outer ring of nine long plumes, inner ring of seven bright ones.
const STL_PLUMES = (() => {
  const out = [];
  for (let i = 0; i < 9; i++) {
    const a = -1.75 + (3.5 * i) / 8;
    out.push({ a, len: 0.62 - Math.abs(a) * 0.10, r: 0.10, ring: 0 });
  }
  for (let i = 0; i < 7; i++) {
    const a = -1.35 + (2.7 * i) / 6;
    out.push({ a, len: 0.42 - Math.abs(a) * 0.06, r: 0.075, ring: 1 });
  }
  return out;
})();

// Base smoke curls — two bead-chains coiling off the seated mass.
const STL_CURLS = [
  [[-0.30, 0.16, -0.34], [-0.40, 0.10, -0.44], [-0.49, 0.06, -0.52], [-0.55, 0.04, -0.58], [-0.58, 0.03, -0.62]],
  [[0.30, 0.16, -0.34], [0.40, 0.10, -0.44], [0.49, 0.06, -0.52], [0.55, 0.04, -0.58], [0.58, 0.03, -0.62]],
];

export function SpectralTigerLordBody({ pal }) {
  pal = pal || getBeingPalette("spectral_tiger");
  const fur = stlFurTex();
  const eye = stlEyeTex();
  const eye3 = stlThirdEyeTex();
  const plumes = useRef([]);
  const wisps = useRef([]);
  const paws = useRef([]);
  const third = useRef();
  const rig = useBeingRig();
  // ALREADY SEATED. Haunches are part of the seated mass, so there
  // are no leg slots — nothing to fold. The forelegs dissolve into
  // pooled smoke; those are the arm slots.
  const armSlots = [rig.armL, rig.armR];

  useFrame((s) => {
    const t = s.clock.elapsedTime;
    // the mane breathes — each plume sways on its own fixed phase
    plumes.current.forEach((grp, i) => {
      if (!grp) return;
      grp.rotation.z = -STL_PLUMES[i].a + Math.sin(t * 0.7 + i * 0.9) * 0.04;
    });
    // the smoke paws pool and breathe
    paws.current.forEach((m, i) => {
      if (!m) return;
      m.material.opacity = 0.55 + Math.sin(t * 1.4 + i * 1.1) * 0.15;
    });
    // the third eye's slow pulse
    if (third.current) {
      const p = 1 + Math.sin(t * 1.1) * 0.05;
      third.current.scale.set(p, p, 1);
    }
    wisps.current.forEach((m, i) => {
      if (!m) return;
      const p = t * 0.45 + i * 1.3;
      m.position.set(Math.cos(p) * 0.8, 0.75 + Math.sin(t * 1.1 + i) * 0.24, Math.sin(p) * 0.8);
      m.material.opacity = 0.28 + Math.sin(t * 1.6 + i) * 0.14;
    });
  });

  const coat = { map: fur, roughness: 0.95, transparent: true, opacity: 0.97, emissive: "#2c7f8f", emissiveIntensity: 0.06 };
  const smokeMat = { color: STL.smoke, roughness: 1, transparent: true, opacity: 0.65, emissive: pal.glow, emissiveIntensity: 0.22 };

  return (
    <group>
      {/* ── the seated mass: haunches + upright chest ──
           RIG: zero-transform torso slot; the head rides it. */}
      <group ref={rig.torso}>
      <mesh position={[0, 0.30, -0.06]} scale={[1.25, 0.72, 1.2]} castShadow>
        <sphereGeometry args={[0.40, 18, 14]} />
        <meshStandardMaterial {...coat} />
      </mesh>
      <mesh position={[0, 0.60, 0.06]} scale={[1.0, 0.95, 1.05]} castShadow>
        <sphereGeometry args={[0.42, 18, 14]} />
        <meshStandardMaterial {...coat} />
      </mesh>
      {/* chest ruff */}
      <mesh position={[0, 0.66, 0.34]} scale={[0.95, 1.05, 0.85]}>
        <sphereGeometry args={[0.24, 14, 12]} />
        <meshStandardMaterial color={STL.ruff} roughness={1} transparent opacity={0.92} emissive={pal.glow} emissiveIntensity={0.05} />
      </mesh>

      {/* ── the head — broad regal skull, six eyes, white ruffs ── */}
      <group ref={rig.head} position={[0, 1.08, 0.28]}>
        <mesh scale={[1.05, 0.95, 0.92]} castShadow>
          <sphereGeometry args={[0.30, 18, 14]} />
          <meshStandardMaterial {...coat} />
        </mesh>
        {/* white cheek ruffs — three flares per side */}
        {[-1, 1].map((sd) =>
          [0, 1, 2].map((k) => (
            <mesh
              key={"ruff" + sd + k}
              position={[sd * (0.26 + k * 0.015), -0.06 + k * 0.06, 0.09]}
              rotation={[0, 0, sd * (-1.55 - k * 0.28)]}
            >
              <coneGeometry args={[0.085, 0.26, 7]} />
              <meshStandardMaterial color={STL.ruff} roughness={1} transparent opacity={0.9} />
            </mesh>
          ))
        )}
        {/* muzzle + nose */}
        <mesh position={[0, -0.09, 0.24]} scale={[1.3, 0.75, 0.9]}>
          <sphereGeometry args={[0.14, 12, 10]} />
          <meshStandardMaterial color={STL.muzzle} roughness={0.9} transparent opacity={0.96} />
        </mesh>
        <mesh position={[0, -0.055, 0.36]} scale={[1.25, 0.85, 0.8]}>
          <sphereGeometry args={[0.04, 8, 8]} />
          <meshStandardMaterial color={STL.nose} roughness={0.6} />
        </mesh>
        {/* the golden pair */}
        {[-1, 1].map((sd) => (
          <group key={"eye" + sd}>
            <mesh position={[sd * 0.135, 0.05, 0.17]} scale={[1, 1.1, 0.6]}>
              <sphereGeometry args={[0.10, 10, 8]} />
              <meshStandardMaterial color={STL.socket} roughness={1} />
            </mesh>
            <mesh position={[sd * 0.13, 0.055, 0.272]} rotation={[0, sd * 0.22, 0]}>
              <circleGeometry args={[0.082, 20]} />
              <meshBasicMaterial map={eye} toneMapped={false} />
            </mesh>
          </group>
        ))}
        {/* the third eye, on the brow */}
        <group position={[0, 0.185, 0.20]}>
          <mesh scale={[1, 1.1, 0.6]}>
            <sphereGeometry args={[0.065, 10, 8]} />
            <meshStandardMaterial color={STL.socket} roughness={1} />
          </mesh>
          <mesh ref={third} position={[0, 0.004, 0.045]} rotation={[-0.22, 0, 0]}>
            <circleGeometry args={[0.05, 18]} />
            <meshBasicMaterial map={eye3} toneMapped={false} />
          </mesh>
        </group>
        {/* the vast outer orbs, set wide in dark sockets */}
        {[-1, 1].map((sd) => (
          <group key={"orb" + sd} position={[sd * 0.33, 0.10, 0.02]}>
            <mesh scale={[1, 1.05, 0.85]}>
              <sphereGeometry args={[0.105, 12, 10]} />
              <meshStandardMaterial color={STL.socket} roughness={1} />
            </mesh>
            <mesh position={[sd * 0.055, 0.005, 0.055]} rotation={[0, sd * 0.85, 0]}>
              <circleGeometry args={[0.072, 20]} />
              <meshBasicMaterial map={eye} toneMapped={false} />
            </mesh>
          </group>
        ))}
        {/* ears — half-lost in the mane */}
        {[-1, 1].map((sd) => (
          <mesh key={"ear" + sd} position={[sd * 0.19, 0.26, -0.06]} rotation={[0, 0, sd * -0.2]}>
            <coneGeometry args={[0.09, 0.18, 7]} />
            <meshStandardMaterial {...coat} />
          </mesh>
        ))}
        {/* long whiskers */}
        {[-1, 1].map((sd) =>
          [0, 1, 2].map((i) => (
            <mesh
              key={"wh" + sd + i}
              position={[sd * 0.24, -0.08 - i * 0.024, 0.26]}
              rotation={[0, sd * -0.4, sd * (0.12 - i * 0.12)]}
            >
              <boxGeometry args={[0.36, 0.006, 0.006]} />
              <meshBasicMaterial color={STL.whisker} transparent opacity={0.6} />
            </mesh>
          ))
        )}
        {/* ── the mane fan — the crown of the Spirit Worlds ── */}
        {STL_PLUMES.map((p, i) => (
          <group
            key={"plume" + i}
            ref={(el) => (plumes.current[i] = el)}
            position={[
              Math.sin(p.a) * (p.ring ? 0.24 : 0.30),
              Math.cos(p.a) * (p.ring ? 0.20 : 0.24) + 0.02,
              -0.16 - p.ring * 0.07,
            ]}
            rotation={[0.12, 0, -p.a]}
          >
            <mesh position={[0, p.len / 2 - 0.04, 0]}>
              <coneGeometry args={[p.r, p.len, 7]} />
              <meshStandardMaterial {...coat} emissiveIntensity={p.ring ? 0.2 : 0.1} />
            </mesh>
          </group>
        ))}
      </group>

      </group>

      {/* ── forelegs dissolving into pooled smoke paws ──
           RIG: the arm slots. There is no ankle here — the limb ends
           in smoke — so a grip pose rotates from the shoulder. */}
      {[-1, 1].map((sd, sideIdx) => (
        <group key={"paw" + sd} ref={armSlots[sideIdx]}>
          <mesh position={[sd * 0.21, 0.36, 0.40]} rotation={[0.32, 0, sd * 0.06]} castShadow>
            <capsuleGeometry args={[0.075, 0.2, 4, 8]} />
            <meshStandardMaterial {...coat} opacity={0.85} />
          </mesh>
          {[[0.14, 0.09], [0.07, 0.07], [0.04, 0.055]].map(([py, pr], bi) => (
            <mesh
              key={"pw" + bi}
              ref={(el) => (paws.current[sideIdx * 3 + bi] = el)}
              position={[sd * (0.22 + bi * 0.03), py, 0.47 + bi * 0.04]}
            >
              <sphereGeometry args={[pr, 10, 8]} />
              <meshStandardMaterial {...smokeMat} />
            </mesh>
          ))}
        </group>
      ))}

      {/* ── base smoke curls coiling off the seated mass ── */}
      {STL_CURLS.map((path, ci) => (
        <group key={"curl" + ci}>
          {path.map(([x, y, z], bi) => (
            <mesh key={"cb" + bi} position={[x, y, z]}>
              <sphereGeometry args={[Math.max(0.02, 0.05 - bi * 0.007), 8, 6]} />
              <meshStandardMaterial {...smokeMat} opacity={0.5} emissiveIntensity={0.12} />
            </mesh>
          ))}
        </group>
      ))}

      {/* ── the veil, widened to a king's measure ── */}
      <mesh position={[0, 0.72, 0.02]} scale={[1.05, 0.95, 1.15]}>
        <sphereGeometry args={[0.85, 16, 12]} />
        <meshBasicMaterial color={pal.glow} transparent opacity={0.09} side={THREE.BackSide} toneMapped={false} depthWrite={false} />
      </mesh>
      {[0, 1, 2, 3, 4].map((i) => (
        <mesh key={"wisp" + i} ref={(el) => (wisps.current[i] = el)}>
          <sphereGeometry args={[0.03, 6, 6]} />
          <meshBasicMaterial color={pal.glow} transparent opacity={0.34} toneMapped={false} depthWrite={false} />
        </mesh>
      ))}

      {/* under-glow (light budget: one, same as every tiger) */}
      <pointLight position={[0, 0.8, 0.4]} color={pal.glow} intensity={0.7} distance={3.2} />
    </group>
  );
}

// ────────────────────────────────────────────────────────────
// THE DRAGON WING — the house wing primitive (Batch 3D-9 r2).
// Built flat in local XY (leading-edge bead-chain up-and-out,
// three finger capsules fanning from the wrist, two flattened
// membrane sails between), then mirrored per side by group
// scale and posed per tier by rotation. caP'n's call: every
// Chaos Dragon tier flies.
// ────────────────────────────────────────────────────────────
const CD_WING_EDGE = [
  [0, 0, 0.052],
  [0.11, 0.13, 0.046],
  [0.22, 0.27, 0.040],
  [0.32, 0.42, 0.033],
];
const CD_WING_FINGERS = [
  { ang: -0.25, len: 0.44 },
  { ang: -0.72, len: 0.40 },
  { ang: -1.15, len: 0.33 },
];

function CDWing({ boneMat, membrane, mOpacity = 0.5, glow }) {
  return (
    <group>
      {/* leading edge — bead-chain up and out */}
      {CD_WING_EDGE.map(([x, y, r], i) => (
        <mesh key={"e" + i} position={[x, y, 0]} castShadow={i === 0}>
          <sphereGeometry args={[r, 10, 8]} />
          <meshStandardMaterial {...boneMat} />
        </mesh>
      ))}
      {/* fingers fanning from the wrist */}
      {CD_WING_FINGERS.map((f, i) => (
        <group key={"f" + i} position={[0.32, 0.42, 0]} rotation={[0, 0, f.ang - Math.PI / 2]}>
          <mesh position={[0, f.len / 2, 0]}>
            <capsuleGeometry args={[0.016, f.len, 4, 6]} />
            <meshStandardMaterial {...boneMat} />
          </mesh>
        </group>
      ))}
      {/* membrane sails — thin flattened spheres, double-sided */}
      <mesh position={[0.42, 0.20, 0]} rotation={[0, 0, 0.5]} scale={[0.40, 0.30, 0.05]}>
        <sphereGeometry args={[1, 14, 12]} />
        <meshStandardMaterial color={membrane} roughness={0.7} transparent opacity={mOpacity} side={THREE.DoubleSide} emissive={glow} emissiveIntensity={0.1} />
      </mesh>
      <mesh position={[0.56, 0.02, 0]} rotation={[0, 0, 0.35]} scale={[0.30, 0.22, 0.045]}>
        <sphereGeometry args={[1, 14, 12]} />
        <meshStandardMaterial color={membrane} roughness={0.7} transparent opacity={mOpacity * 0.85} side={THREE.DoubleSide} emissive={glow} emissiveIntensity={0.08} />
      </mesh>
    </group>
  );
}

// ────────────────────────────────────────────────────────────
// CHAOS DRAGON BEB — the gorgeous pass (Batch 3D-9, round 2).
// Built to the card (Terror TCG CDL-01): "The One & Only Leader
// of the Unstable World" — a pale porcelain hatchling with vast
// obsidian doll-eyes rimmed in weeping red, a radiating crown of
// bone spikes tipped in flame, a teal leaf-scale beard pouring
// down the chest, forelimbs of living golden fire trailing a
// flame spiral, and — per caP'n — new-hatched wings, half-open.
// Round 2: cheek balls cut, muzzle wedged reptilian, nostrils
// added (more Dragon, less fox), wings on.
//
// Contract unchanged: head at the old crimson box-dragon's
// strike height (~y 0.95, muzzle forward) so the jaw-snap flare
// ([0, 0.95, 0.9]), labels, Ascent ring, and GroundBlob all
// still land. One pointLight, same as the old body. Colors are
// display-only dressing over the canon palette (Vyren pattern).
// ────────────────────────────────────────────────────────────
const CDB = {
  skin: "#ded6c6",      // the card's pale porcelain hide
  skinDeep: "#b9ad9a",
  crown: "#e8dfc9",     // bone spike-crown
  flameTip: "#e84a3a",  // the crown's red flame tips (canon main)
  beard: "#2e7d8c",     // teal leaf-scale beard
  beardHi: "#5fc4c0",
  eyeRim: "#8c1f1a",    // the weeping red rims
  nostril: "#5a4638",
  membrane: "#f0c9a0",  // hatchling wing sail, fire-warmed
  flame: "#ffd08a",     // living golden fire (canon accent)
};

// The hide — porcelain base, seeded mottle in rose and faint
// teal, soft scale arcs. One 512 canvas, cached, shared by every
// hided part; sphere/capsule UVs wrap it organically.
function cdbSkinTex() {
  return canvasTex("cdb_skin_v1", 512, 512, (g, w, h) => {
    const base = g.createLinearGradient(0, 0, 0, h);
    base.addColorStop(0, "#e9e2d3");
    base.addColorStop(0.55, "#dbd2c0");
    base.addColorStop(1, "#c4b8a2");
    g.fillStyle = base; g.fillRect(0, 0, w, h);
    const R = mulberry32(4451);
    // porcelain mottle — rose flush and faint teal, the card's skin
    for (let i = 0; i < 1000; i++) {
      g.fillStyle = R() > 0.6 ? "rgba(200,120,110,0.05)" : R() > 0.3 ? "rgba(46,125,140,0.04)" : "rgba(90,78,60,0.06)";
      g.fillRect(R() * w, R() * h, 2 + R() * 4, 2 + R() * 4);
    }
    // soft scale arcs — a hatchling's first scales, barely there
    g.strokeStyle = "rgba(120,105,84,0.14)"; g.lineWidth = 1.5;
    for (let i = 0; i < 90; i++) {
      const x = R() * w, y = R() * h, r = 6 + R() * 9;
      g.beginPath(); g.arc(x, y, r, Math.PI * 0.15, Math.PI * 0.85); g.stroke();
    }
  });
}

// The obsidian doll-eyes — the card's whole face. Near-black
// depths ringed in weeping red, a vast soft specular star.
function cdbEyeTex() {
  return canvasTex("cdb_eye_v1", 128, 128, (g) => {
    const r = g.createRadialGradient(64, 60, 3, 64, 64, 62);
    r.addColorStop(0, "#2a2030");
    r.addColorStop(0.45, "#171019");
    r.addColorStop(0.78, "#0c080d");
    r.addColorStop(0.9, "#8c1f1a");
    r.addColorStop(1, "#3a0c08");
    g.fillStyle = r; g.beginPath(); g.arc(64, 64, 62, 0, 7); g.fill();
    // the vast doll-glass speculars
    g.fillStyle = "rgba(255,250,240,0.9)";
    g.beginPath(); g.arc(45, 42, 10, 0, 7); g.fill();
    g.fillStyle = "rgba(255,250,240,0.4)";
    g.beginPath(); g.arc(82, 80, 5, 0, 7); g.fill();
    g.fillStyle = "rgba(232,74,58,0.35)";
    g.beginPath(); g.arc(64, 108, 12, 0, 7); g.fill();
  });
}

// The spike-crown — a radiating halo of bone plumes around the
// skull, flame-tipped, computed deterministically (fan pattern).
const CDB_CROWN = (() => {
  const out = [];
  for (let i = 0; i < 9; i++) {
    const a = -1.85 + (3.7 * i) / 8;
    out.push({ a, len: 0.40 - Math.abs(a) * 0.055, r: 0.045, ring: 0 });
  }
  for (let i = 0; i < 6; i++) {
    const a = -1.3 + (2.6 * i) / 5;
    out.push({ a, len: 0.24 - Math.abs(a) * 0.03, r: 0.035, ring: 1 });
  }
  return out;
})();

// The flame spiral at its side — the card's golden swirl as a
// bead-chain rising around a coil (seedless, hand-tuned curve).
const CDB_SWIRL = (() => {
  const out = [];
  for (let i = 0; i < 10; i++) {
    const f = i / 9;
    const a = f * Math.PI * 2.4;
    out.push([
      0.42 + Math.cos(a) * (0.13 - f * 0.05),
      0.22 + f * 0.62,
      0.18 + Math.sin(a) * (0.13 - f * 0.05),
      0.062 - f * 0.038,
    ]);
  }
  return out;
})();

export function ChaosDragonBody({ pal }) {
  pal = pal || getBeingPalette("chaos_dragon");
  const skin = cdbSkinTex();
  const eye = cdbEyeTex();
  const crown = useRef([]);
  const swirl = useRef([]);
  const arms = useRef([]);
  const rig = useBeingRig();
  const tail = rig.tail;
  const armSlots = [rig.armL, rig.armR];
  const legSlots = [rig.legL, rig.legR];
  const wingL = useRef();
  const wingR = useRef();
  const embers = useRef([]);

  useFrame((s) => {
    const t = s.clock.elapsedTime;
    // the crown breathes — each spike sways on its own fixed phase
    crown.current.forEach((grp, i) => {
      if (!grp) return;
      grp.rotation.z = -CDB_CROWN[i].a + Math.sin(t * 0.8 + i * 1.1) * 0.035;
    });
    // the hatchling wings test the air — small, eager beats
    if (wingL.current) wingL.current.rotation.z = 0.35 + Math.sin(t * 1.6) * 0.09;
    if (wingR.current) wingR.current.rotation.z = 0.35 + Math.sin(t * 1.6 + 0.4) * 0.09;
    // the flame spiral licks upward, each bead breathing
    swirl.current.forEach((m, i) => {
      if (!m) return;
      const p = 1 + Math.sin(t * 2.2 + i * 0.8) * 0.2;
      m.scale.setScalar(p);
      m.material.opacity = 0.55 + Math.sin(t * 1.7 + i * 1.2) * 0.2;
    });
    // the fire-arms flicker
    arms.current.forEach((m, i) => {
      if (!m) return;
      m.material.emissiveIntensity = 0.7 + Math.sin(t * 3.1 + i * 2.2) * 0.25;
    });
    if (tail.current) {
      tail.current.rotation.x = Math.sin(t * 1.3) * 0.08;
      tail.current.rotation.z = Math.sin(t * 0.95 + 1.1) * 0.1;
    }
    embers.current.forEach((m, i) => {
      if (!m) return;
      const p = t * 0.55 + i * 2.0;
      m.position.set(Math.cos(p) * 0.58, 0.6 + Math.sin(t * 1.4 + i) * 0.2, Math.sin(p) * 0.58 + 0.1);
      m.material.opacity = 0.34 + Math.sin(t * 2.1 + i) * 0.18;
    });
  });

  const hide = { map: skin, roughness: 0.92 };

  return (
    <group scale={0.92}>{/* lowered a notch so the Codex frames the head (caP'n, r3) */}
      {/* ── the seated hatchling: round haunch + upright chest ──
           RIG: torso slot. ALREADY SEATED — the haunch is the pose.
           Beard and head ride inside so a pitch carries them. */}
      <group ref={rig.torso}>
      <mesh position={[0, 0.34, -0.08]} scale={[1.1, 0.78, 1.05]} castShadow>
        <sphereGeometry args={[0.34, 18, 14]} />
        <meshStandardMaterial {...hide} />
      </mesh>
      <mesh position={[0, 0.62, 0.08]} scale={[0.92, 0.98, 0.9]} castShadow>
        <sphereGeometry args={[0.32, 18, 14]} />
        <meshStandardMaterial {...hide} />
      </mesh>

      {/* ── the teal leaf-scale beard, pouring down the chest ── */}
      {[0, 1, 2].map((row) =>
        Array.from({ length: 3 + row }, (_, k) => {
          const n = 3 + row;
          const x = (k - (n - 1) / 2) * (0.11 + row * 0.015);
          return (
            <mesh
              key={"leaf" + row + k}
              position={[x, 0.86 - row * 0.14, 0.30 + row * 0.035]}
              rotation={[0.5 + row * 0.12, 0, 0]}
            >
              <coneGeometry args={[0.055 - row * 0.006, 0.16, 6]} />
              <meshStandardMaterial
                color={row === 0 ? CDB.beardHi : CDB.beard}
                roughness={0.85}
                emissive={CDB.beardHi}
                emissiveIntensity={0.08}
              />
            </mesh>
          );
        })
      )}

      {/* ── the head — vast, round, the card's porcelain face ── */}
      <group ref={rig.head} position={[0, 0.95, 0.30]}>
        <mesh scale={[1.02, 0.96, 0.9]} castShadow>
          <sphereGeometry args={[0.28, 18, 14]} />
          <meshStandardMaterial {...hide} />
        </mesh>
        {/* reptilian wedge muzzle + nostrils (round 2: the fox is out) */}
        <mesh position={[0, -0.075, 0.22]} scale={[0.85, 0.58, 1.05]}>
          <sphereGeometry args={[0.10, 12, 10]} />
          <meshStandardMaterial {...hide} />
        </mesh>
        {[-1, 1].map((sd) => (
          <mesh key={"nos" + sd} position={[sd * 0.035, -0.05, 0.315]}>
            <sphereGeometry args={[0.014, 6, 6]} />
            <meshStandardMaterial color={CDB.nostril} roughness={1} />
          </mesh>
        ))}
        {/* the obsidian doll-eyes — huge, red-rimmed sockets */}
        {[-1, 1].map((sd) => (
          <group key={"eye" + sd}>
            <mesh position={[sd * 0.125, 0.045, 0.15]} scale={[1.1, 1.2, 0.6]}>
              <sphereGeometry args={[0.105, 10, 8]} />
              <meshStandardMaterial color={CDB.eyeRim} roughness={1} />
            </mesh>
            <mesh position={[sd * 0.12, 0.05, 0.252]} rotation={[0, sd * 0.24, 0]}>
              <circleGeometry args={[0.095, 20]} />
              <meshBasicMaterial map={eye} toneMapped={false} />
            </mesh>
          </group>
        ))}
        {/* the twin red brow-flames above the eyes */}
        {[-1, 1].map((sd) => (
          <mesh key={"brow" + sd} position={[sd * 0.13, 0.20, 0.14]} rotation={[-0.3, 0, sd * -0.5]}>
            <coneGeometry args={[0.035, 0.14, 6]} />
            <meshStandardMaterial color={CDB.flameTip} emissive={CDB.flameTip} emissiveIntensity={0.5} roughness={0.7} />
          </mesh>
        ))}
        {/* ── the spike-crown — bone halo, flame-tipped ── */}
        {CDB_CROWN.map((p, i) => (
          <group
            key={"spike" + i}
            ref={(el) => (crown.current[i] = el)}
            position={[
              Math.sin(p.a) * (p.ring ? 0.20 : 0.25),
              Math.cos(p.a) * (p.ring ? 0.17 : 0.21) + 0.03,
              -0.10 - p.ring * 0.06,
            ]}
            rotation={[0.1, 0, -p.a]}
          >
            <mesh position={[0, p.len / 2 - 0.03, 0]}>
              <coneGeometry args={[p.r, p.len, 6]} />
              <meshStandardMaterial {...hide} />
            </mesh>
            <mesh position={[0, p.len - 0.02, 0]}>
              <coneGeometry args={[p.r * 0.55, p.len * 0.28, 6]} />
              <meshStandardMaterial color={CDB.flameTip} emissive={pal.glow} emissiveIntensity={0.6} toneMapped={false} />
            </mesh>
          </group>
        ))}
      </group>

      </group>

      {/* ── the new-hatched wings, half-open, testing the air ── */}
      {[-1, 1].map((sd) => (
        <group key={"wing" + sd} position={[sd * 0.20, 0.66, -0.16]} scale={[sd * 0.6, 0.6, 0.6]} rotation={[0.25, sd * -0.35, 0]}>
          <group ref={sd < 0 ? wingL : wingR}>
            <CDWing boneMat={hide} membrane={CDB.membrane} mOpacity={0.55} glow={pal.glow} />
          </group>
        </group>
      ))}

      {/* ── forelimbs of living golden fire ──
           RIG: the arm slots. Fire, not flesh — it reaches from the
           shoulder and the flame follows. */}
      {[-1, 1].map((sd, si) => (
        <group key={"arm" + sd} ref={armSlots[si]}>
          <mesh
            ref={(el) => (arms.current[si] = el)}
            position={[sd * 0.26, 0.44, 0.26]}
            rotation={[0.5, 0, sd * 0.35]}
            castShadow
          >
            <capsuleGeometry args={[0.055, 0.2, 4, 8]} />
            <meshStandardMaterial color={CDB.flame} emissive={pal.glow} emissiveIntensity={0.8} roughness={0.5} toneMapped={false} />
          </mesh>
          <mesh position={[sd * 0.30, 0.26, 0.36]} rotation={[0.9, 0, sd * 0.3]}>
            <coneGeometry args={[0.05, 0.14, 6]} />
            <meshBasicMaterial color={CDB.flame} transparent opacity={0.85} toneMapped={false} depthWrite={false} />
          </mesh>
        </group>
      ))}

      {/* ── stubby hind feet ──
           RIG: leg slots. Nothing to fold on a seated hatchling, but
           a jump celebration will want them. */}
      {[-0.16, 0.16].map((ox, i) => (
        <group key={"foot" + i} ref={legSlots[i]}>
          <mesh position={[ox, 0.08, 0.18]} scale={[1, 0.65, 1.2]}>
            <sphereGeometry args={[0.09, 10, 8]} />
            <meshStandardMaterial {...hide} />
          </mesh>
        </group>
      ))}

      {/* ── the tail — a small curled bead-chain, ember-tipped ── */}
      <group ref={tail} position={[0, 0.30, -0.42]}>
        {[[0, 0, -0.02, 0.07], [0, 0.05, -0.13, 0.06], [0.04, 0.13, -0.2, 0.05], [0.1, 0.22, -0.22, 0.042], [0.16, 0.3, -0.2, 0.034]].map(([x, y, z, r], i, arr) => {
          const tip = i === arr.length - 1;
          return (
            <mesh key={"t" + i} position={[x, y, z]}>
              <sphereGeometry args={[r, 10, 8]} />
              <meshStandardMaterial
                {...hide}
                emissive={tip ? pal.glow : "#000000"}
                emissiveIntensity={tip ? 0.9 : 0}
                toneMapped={!tip}
              />
            </mesh>
          );
        })}
      </group>

      {/* ── the flame spiral at its side — the card's golden swirl ── */}
      {CDB_SWIRL.map(([x, y, z, r], i) => (
        <mesh key={"sw" + i} ref={(el) => (swirl.current[i] = el)} position={[x, y, z]}>
          <sphereGeometry args={[r, 8, 6]} />
          <meshBasicMaterial color={CDB.flame} transparent opacity={0.6} toneMapped={false} depthWrite={false} />
        </mesh>
      ))}

      {/* ── the warm veil: rim aura + three ember motes ── */}
      <mesh position={[0, 0.56, 0.06]} scale={[0.95, 0.85, 1.15]}>
        <sphereGeometry args={[0.62, 16, 12]} />
        <meshBasicMaterial color={pal.glow} transparent opacity={0.07} side={THREE.BackSide} toneMapped={false} depthWrite={false} />
      </mesh>
      {[0, 1, 2].map((i) => (
        <mesh key={"ember" + i} ref={(el) => (embers.current[i] = el)}>
          <sphereGeometry args={[0.026, 6, 6]} />
          <meshBasicMaterial color={pal.glow} transparent opacity={0.4} toneMapped={false} depthWrite={false} />
        </mesh>
      ))}

      {/* under-glow (kept from the old body — light budget unchanged) */}
      <pointLight position={[0, 1.0, 0.6]} color={pal.glow} intensity={0.8} distance={3} />
    </group>
  );
}

// ────────────────────────────────────────────────────────────
// CHAOS DRAGON CHAMPION — the Ascent worn as flesh (Batch 3D-9,
// round 2 — REBUILT on caP'n's read: too dog, too quadruped).
// Built to the card (Terror TCG CDL-04): the innocent typhoon,
// grown — and the card never showed a beast on all fours. It
// shows a PRESENCE: the burning candle-crest, the vast gilded
// blade-ears, the radiating whisker-spines, and beneath the
// head a dark feathered mass. Round 2 reads that mass as what
// it is — a ROBE. The Champion stands on hind legs, robed in
// dark feathers to the ground, clawed feet at the hem, slim
// scaled arms, tall folded wings rising behind, snout humbled
// to a reptilian wedge. The twin golden suns stay.
//
// Contract: head at the strike height (~y 1.02) so the jaw-snap
// flare, labels, Ascent ring, and GroundBlob all still land.
// One pointLight.
// ────────────────────────────────────────────────────────────
const CDC = {
  scale: "#4a7d64",     // verdigris hide
  scaleDeep: "#22392e",
  blade: "#8aa06a",     // the great blade-ears
  bladeEdge: "#ffd08a", // gilded edges (canon accent)
  robe: "#141d18",      // the dark feather robe
  robeHi: "#26362c",
  whisker: "#e8d8a8",   // pale gold spines
  flameCore: "#fff2c8", // the candle's heart
  disc: "#ffd08a",      // the twin suns
  membrane: "#b8a668",  // wing sail, old gold-green
  nostril: "#16241d",
};

// Verdigris hide — scale-row strokes over deep green, gold
// flecks catching light. Cached, seeded, shared.
function cdcScaleTex() {
  return canvasTex("cdc_scale_v1", 512, 512, (g, w, h) => {
    const base = g.createLinearGradient(0, 0, 0, h);
    base.addColorStop(0, "#5a8f74");
    base.addColorStop(0.5, "#44735c");
    base.addColorStop(1, "#2c4a3e");
    g.fillStyle = base; g.fillRect(0, 0, w, h);
    const R = mulberry32(7717);
    for (let i = 0; i < 900; i++) {
      g.fillStyle = R() > 0.5 ? "rgba(20,32,26,0.08)" : "rgba(160,200,150,0.05)";
      g.fillRect(R() * w, R() * h, 2 + R() * 4, 2 + R() * 4);
    }
    // scale rows — staggered arcs
    g.lineWidth = 1.6;
    for (let row = 0; row < 20; row++) {
      const y = (row / 20) * h;
      for (let cx = ((row % 2) * 13); cx < w; cx += 26) {
        g.strokeStyle = "rgba(16,28,22,0.35)";
        g.beginPath(); g.arc(cx, y, 11, Math.PI * 0.1, Math.PI * 0.9); g.stroke();
      }
    }
    // gold flecks — the gilding
    for (let i = 0; i < 60; i++) {
      g.fillStyle = "rgba(255,208,138,0.28)";
      const x = R() * w, y = R() * h;
      g.beginPath(); g.arc(x, y, 1.2 + R() * 2, 0, 7); g.fill();
    }
  });
}

// The robe — dark feathers falling in overlapping rows, faint
// gold catch-light at the barbs. Cached, seeded.
function cdcRobeTex() {
  return canvasTex("cdc_robe_v1", 512, 512, (g, w, h) => {
    const base = g.createLinearGradient(0, 0, 0, h);
    base.addColorStop(0, "#1d2a22");
    base.addColorStop(0.5, "#141d18");
    base.addColorStop(1, "#0b110d");
    g.fillStyle = base; g.fillRect(0, 0, w, h);
    const R = mulberry32(5527);
    // feather rows — downward tapered strokes, staggered
    const feather = (x, y, len, wd, fill, alpha) => {
      g.save(); g.translate(x, y);
      g.beginPath();
      g.moveTo(0, 0);
      g.quadraticCurveTo(wd, len * 0.4, 0, len);
      g.quadraticCurveTo(-wd, len * 0.4, 0, 0);
      g.closePath();
      g.globalAlpha = alpha; g.fillStyle = fill; g.fill();
      g.restore(); g.globalAlpha = 1;
    };
    for (let row = 0; row < 12; row++) {
      const y = (row / 12) * h - 10;
      for (let k = 0; k < 14; k++) {
        const x = ((k + (row % 2) * 0.5) / 14) * w;
        feather(x + (R() - 0.5) * 8, y, 52 + R() * 18, 9 + R() * 5, "#26362c", 0.55);
        feather(x + (R() - 0.5) * 8, y + 4, 30 + R() * 12, 4 + R() * 3, "#3c5244", 0.3);
      }
    }
    // gold barb catch-lights
    for (let i = 0; i < 46; i++) {
      g.fillStyle = "rgba(255,208,138,0.16)";
      g.beginPath(); g.arc(R() * w, R() * h, 1 + R() * 1.6, 0, 7); g.fill();
    }
  });
}

// The amber regard — vast round golden eyes, dark round pupils,
// unblinking. The card's serene menace.
function cdcEyeTex() {
  return canvasTex("cdc_eye_v1", 128, 128, (g) => {
    const r = g.createRadialGradient(64, 60, 4, 64, 64, 62);
    r.addColorStop(0, "#fff2c8");
    r.addColorStop(0.35, "#ffb547");
    r.addColorStop(0.68, "#d98d1e");
    r.addColorStop(0.9, "#4a2c08");
    r.addColorStop(1, "#120c04");
    g.fillStyle = r; g.beginPath(); g.arc(64, 64, 62, 0, 7); g.fill();
    g.fillStyle = "rgba(12,9,5,0.94)";
    g.beginPath(); g.arc(64, 66, 17, 0, 7); g.fill();
    g.fillStyle = "rgba(255,255,255,0.85)";
    g.beginPath(); g.arc(48, 44, 6.5, 0, 7); g.fill();
    g.fillStyle = "rgba(255,255,255,0.4)";
    g.beginPath(); g.arc(78, 82, 3, 0, 7); g.fill();
  });
}

// The blade-ears — two vast scythes sweeping up and out, built
// as tapering bead-chains along the card's curve (deterministic).
const CDC_BLADE = (() => {
  const N = 6;
  const out = [];
  for (let i = 0; i <= N; i++) {
    const f = i / N;
    out.push([
      0.06 + Math.sin(f * 1.35) * 0.34,
      0.10 + f * 0.52,
      -0.04 - f * 0.10,
      0.085 * (1 - f * 0.72),
    ]);
  }
  return out;
})();

// Whisker-spines radiating from the jaw — angles fixed, per side.
const CDC_SPINES = [
  { y: 0.08, z: 0.10, ang: 0.55, len: 0.34 },
  { y: 0.02, z: 0.14, ang: 0.25, len: 0.40 },
  { y: -0.04, z: 0.14, ang: -0.05, len: 0.38 },
  { y: -0.09, z: 0.10, ang: -0.4, len: 0.30 },
];

export function ChaosDragonChampionBody({ pal }) {
  // RIG NOTE: this body is ROBED — dark feathers from the shoulders
  // to the ground. There is no waist to pitch and no thigh to fold;
  // only clawed feet peek from the hem. So the torso slot wraps the
  // robe alone and the head carries its own slot rather than riding
  // inside it (source order puts arms and feet between them, and
  // reparenting across that gap would reorder transparent draws).
  // A seated pose on this body settles the hem — it does not fold.
  pal = pal || getBeingPalette("chaos_dragon");
  const scaleT = cdcScaleTex();
  const robeT = cdcRobeTex();
  const eye = cdcEyeTex();
  const rig = useBeingRig();
  const armSlots = [rig.armL, rig.armR];
  const legSlots = [rig.legL, rig.legR];
  const flame = useRef();
  const flameCore = useRef();
  const discs = useRef([]);
  const wingL = useRef();
  const wingR = useRef();
  const embers = useRef([]);

  useFrame((s) => {
    const t = s.clock.elapsedTime;
    // the candle-flame gutters — never still, never out
    if (flame.current) {
      flame.current.scale.set(
        1 + Math.sin(t * 5.1) * 0.08,
        1 + Math.sin(t * 3.7 + 0.6) * 0.14,
        1 + Math.sin(t * 4.4 + 1.9) * 0.08
      );
      flame.current.rotation.z = Math.sin(t * 2.6) * 0.06;
    }
    if (flameCore.current) {
      flameCore.current.material.opacity = 0.85 + Math.sin(t * 6.2) * 0.12;
    }
    // the tall wings hold their fold, breathing slow
    if (wingL.current) wingL.current.rotation.z = 0.15 + Math.sin(t * 0.9) * 0.05;
    if (wingR.current) wingR.current.rotation.z = 0.15 + Math.sin(t * 0.9 + 0.5) * 0.05;
    // the twin suns bob slow, half a phase apart
    discs.current.forEach((m, i) => {
      if (!m) return;
      m.position.y = 0.62 + Math.sin(t * 0.8 + i * Math.PI) * 0.04;
      m.material.opacity = 0.3 + Math.sin(t * 1.1 + i * Math.PI) * 0.08;
    });
    embers.current.forEach((m, i) => {
      if (!m) return;
      const p = t * 0.5 + i * 1.7;
      m.position.set(Math.cos(p) * 0.64, 0.66 + Math.sin(t * 1.3 + i) * 0.2, Math.sin(p) * 0.64 + 0.05);
      m.material.opacity = 0.3 + Math.sin(t * 1.9 + i) * 0.16;
    });
  });

  const hide = { map: scaleT, roughness: 0.88 };
  const robe = { map: robeT, roughness: 0.98 };

  return (
    <group scale={0.92}>{/* lowered a notch so the Codex frames the head (caP'n, r3) */}
      {/* ── the robe — dark feathers, shoulders to the ground ──
           RIG: the torso slot. The robe is the whole lower body. */}
      <group ref={rig.torso}>
      <mesh position={[0, 0.46, 0]} scale={[1, 1, 0.88]} castShadow>
        <coneGeometry args={[0.36, 0.92, 14]} />
        <meshStandardMaterial {...robe} />
      </mesh>
      {/* shoulder mantle */}
      <mesh position={[0, 0.84, 0]} scale={[1.15, 0.5, 1.0]}>
        <sphereGeometry args={[0.21, 14, 10]} />
        <meshStandardMaterial {...robe} />
      </mesh>
      {/* feather fringe at the hem */}
      {Array.from({ length: 10 }, (_, i) => {
        const a = (i / 10) * Math.PI * 2;
        return (
          <mesh
            key={"hem" + i}
            position={[Math.cos(a) * 0.30, 0.09, Math.sin(a) * 0.27]}
            rotation={[Math.sin(a) * 0.35, 0, Math.cos(a) * -0.35]}
          >
            <coneGeometry args={[0.05, 0.2, 5]} />
            <meshStandardMaterial {...robe} />
          </mesh>
        );
      })}
      {/* collar ruff ringing the throat */}
      {Array.from({ length: 8 }, (_, i) => {
        const a = (i / 8) * Math.PI * 2;
        return (
          <mesh
            key={"ruff" + i}
            position={[Math.cos(a) * 0.15, 0.92, Math.sin(a) * 0.13]}
            rotation={[0.4 + Math.sin(a) * 0.2, 0, Math.cos(a) * -0.8]}
          >
            <coneGeometry args={[0.045, 0.17, 5]} />
            <meshStandardMaterial color={CDC.robeHi} roughness={1} />
          </mesh>
        );
      })}

      </group>

      {/* ── slim scaled arms from the robe, claws at rest ──
           RIG: the arm slots. Claws at rest close on a rim. */}
      {[-1, 1].map((sd, si) => (
        <group key={"arm" + sd} ref={armSlots[si]}>
          <mesh position={[sd * 0.22, 0.62, 0.14]} rotation={[0.35, 0, sd * 0.3]} castShadow>
            <capsuleGeometry args={[0.042, 0.24, 4, 8]} />
            <meshStandardMaterial {...hide} />
          </mesh>
          <mesh position={[sd * 0.25, 0.46, 0.24]} scale={[0.9, 0.7, 1.2]}>
            <sphereGeometry args={[0.055, 10, 8]} />
            <meshStandardMaterial {...hide} />
          </mesh>
        </group>
      ))}

      {/* ── clawed feet at the hem ──
           RIG: leg slots. All the leg this body has. */}
      {[-0.13, 0.13].map((ox, i) => (
        <group key={"foot" + i} ref={legSlots[i]} position={[ox, 0.055, 0.22]}>
          <mesh scale={[1, 0.6, 1.3]}>
            <sphereGeometry args={[0.08, 10, 8]} />
            <meshStandardMaterial {...hide} />
          </mesh>
          {[-0.035, 0, 0.035].map((tx, k) => (
            <mesh key={"claw" + k} position={[tx, -0.01, 0.1]} rotation={[1.35, 0, 0]}>
              <coneGeometry args={[0.016, 0.06, 5]} />
              <meshStandardMaterial color={CDC.whisker} roughness={0.6} />
            </mesh>
          ))}
        </group>
      ))}

      {/* ── the neck, short and ruffed ── */}
      <mesh position={[0, 0.96, 0.05]}>
        <sphereGeometry args={[0.11, 12, 10]} />
        <meshStandardMaterial {...hide} />
      </mesh>

      {/* ── the head — verdigris skull, humbled wedge snout ── */}
      <group ref={rig.head} position={[0, 1.06, 0.10]}>
        <mesh scale={[0.88, 0.85, 1.0]} castShadow>
          <sphereGeometry args={[0.21, 18, 14]} />
          <meshStandardMaterial {...hide} />
        </mesh>
        {/* small reptilian wedge + nostrils (round 2: snout humbled) */}
        <mesh position={[0, -0.04, 0.17]} scale={[0.62, 0.48, 0.95]}>
          <sphereGeometry args={[0.11, 12, 10]} />
          <meshStandardMaterial {...hide} />
        </mesh>
        {[-1, 1].map((sd) => (
          <mesh key={"nos" + sd} position={[sd * 0.028, -0.02, 0.265]}>
            <sphereGeometry args={[0.011, 6, 6]} />
            <meshStandardMaterial color={CDC.nostril} roughness={1} />
          </mesh>
        ))}
        {/* the amber lamps in shadowed sockets */}
        {[-1, 1].map((sd) => (
          <group key={"eye" + sd}>
            <mesh position={[sd * 0.11, 0.045, 0.12]} scale={[1.05, 1.15, 0.6]}>
              <sphereGeometry args={[0.088, 10, 8]} />
              <meshStandardMaterial color={CDC.scaleDeep} roughness={1} />
            </mesh>
            <mesh position={[sd * 0.108, 0.05, 0.202]} rotation={[0, sd * 0.3, 0]}>
              <circleGeometry args={[0.076, 20]} />
              <meshBasicMaterial map={eye} toneMapped={false} />
            </mesh>
          </group>
        ))}
        {/* ── the candle-flame crest — the card's burning heart ── */}
        <group position={[0, 0.24, 0.0]}>
          <mesh>
            <coneGeometry args={[0.05, 0.09, 8]} />
            <meshStandardMaterial {...hide} />
          </mesh>
          <group ref={flame} position={[0, 0.13, 0]}>
            <mesh>
              <coneGeometry args={[0.055, 0.26, 8]} />
              <meshBasicMaterial color={pal.glow} transparent opacity={0.55} toneMapped={false} depthWrite={false} />
            </mesh>
            <mesh ref={flameCore} position={[0, -0.03, 0]}>
              <coneGeometry args={[0.028, 0.15, 8]} />
              <meshBasicMaterial color={CDC.flameCore} transparent opacity={0.9} toneMapped={false} depthWrite={false} />
            </mesh>
          </group>
        </group>
        {/* ── the blade-ears — two vast gilded scythes ── */}
        {[-1, 1].map((sd) => (
          <group key={"blade" + sd}>
            {CDC_BLADE.map(([bx, by, bz, br], i, arr) => {
              const edge = i >= arr.length - 2;
              return (
                <mesh
                  key={"b" + i}
                  position={[sd * bx, by, bz]}
                  rotation={[0, 0, sd * -0.35]}
                  scale={[0.42, 1.25, 0.8]}
                  castShadow={i === 0}
                >
                  <sphereGeometry args={[br, 10, 8]} />
                  <meshStandardMaterial
                    color={edge ? CDC.bladeEdge : CDC.blade}
                    map={edge ? null : scaleT}
                    roughness={0.8}
                    emissive={edge ? pal.glow : "#000000"}
                    emissiveIntensity={edge ? 0.45 : 0}
                    toneMapped={!edge}
                  />
                </mesh>
              );
            })}
          </group>
        ))}
        {/* ── whisker-spines radiating from the jaw ── */}
        {[-1, 1].map((sd) =>
          CDC_SPINES.map((sp, i) => (
            <mesh
              key={"spine" + sd + i}
              position={[sd * 0.15, sp.y, sp.z]}
              rotation={[0, sd * -0.5, sd * sp.ang]}
            >
              <boxGeometry args={[sp.len, 0.007, 0.007]} />
              <meshBasicMaterial color={CDC.whisker} transparent opacity={0.6} />
            </mesh>
          ))
        )}
      </group>

      {/* ── the tall folded wings, rising behind the robe ── */}
      {[-1, 1].map((sd) => (
        <group key={"wing" + sd} position={[sd * 0.16, 0.80, -0.16]} scale={[sd * 0.95, 0.95, 0.95]} rotation={[0.1, sd * -0.25, 0.35]}>
          <group ref={sd < 0 ? wingL : wingR}>
            <CDWing boneMat={hide} membrane={CDC.membrane} mOpacity={0.6} glow={pal.glow} />
          </group>
        </group>
      ))}

      {/* ── the twin golden suns, riding low behind the shoulders ── */}
      {[-1, 1].map((sd, i) => (
        <mesh
          key={"disc" + sd}
          ref={(el) => (discs.current[i] = el)}
          position={[sd * 0.36, 0.62, -0.24]}
          rotation={[0, sd * 0.3, 0]}
        >
          <circleGeometry args={[0.13, 24]} />
          <meshBasicMaterial color={CDC.disc} transparent opacity={0.32} side={THREE.DoubleSide} toneMapped={false} depthWrite={false} />
        </mesh>
      ))}

      {/* ── the tail, peeking from the hem behind ── */}
      {[[0, 0.1, -0.34, 0.055], [0.05, 0.09, -0.44, 0.045], [0.12, 0.1, -0.52, 0.036], [0.19, 0.13, -0.57, 0.028]].map(([x, y, z, r], i, arr) => {
        const tip = i === arr.length - 1;
        return (
          <mesh key={"t" + i} position={[x, y, z]}>
            <sphereGeometry args={[r, 10, 8]} />
            <meshStandardMaterial
              {...hide}
              emissive={tip ? pal.glow : "#000000"}
              emissiveIntensity={tip ? 0.9 : 0}
              toneMapped={!tip}
            />
          </mesh>
        );
      })}

      {/* ── the warm veil: rim aura + four ember motes ── */}
      <mesh position={[0, 0.62, 0.0]} scale={[0.9, 0.95, 1.05]}>
        <sphereGeometry args={[0.62, 16, 12]} />
        <meshBasicMaterial color={pal.glow} transparent opacity={0.08} side={THREE.BackSide} toneMapped={false} depthWrite={false} />
      </mesh>
      {[0, 1, 2, 3].map((i) => (
        <mesh key={"ember" + i} ref={(el) => (embers.current[i] = el)}>
          <sphereGeometry args={[0.025, 6, 6]} />
          <meshBasicMaterial color={pal.glow} transparent opacity={0.36} toneMapped={false} depthWrite={false} />
        </mesh>
      ))}

      {/* under-glow (light budget: one, same as the Beb) */}
      <pointLight position={[0, 1.0, 0.6]} color={pal.glow} intensity={0.8} distance={3} />
    </group>
  );
}

// ────────────────────────────────────────────────────────────
// CHAOS DRAGON LORD — the Ascent completed (Batch 3D-9, round 2).
// Built to the card (Terror TCG CDL-07): the being Xavizu the
// D'aeternuz named a Threat and allied with rather than fight.
// A bone-white colossus presiding on a long serpentine neck —
// two vast ivory horns sweeping up and out, the burning brow-eye
// ringed in crimson, small golden watch-eyes scattered across
// the skull (the card shows MANY — they stay), a radiating fan
// of pale tendrils, crimson drapes falling from the chest, and
// — round 2 — vast ivory wings, spread. The muzzle narrowed to
// a reptilian wedge, nostrils set. It does not stalk. It regards.
//
// Contract: presiding footprint inside the Beb/Champion disc;
// head at ~1.12 so the worn Lord crown (PlayerBody, y=1.5) lands
// framed between the horns. One pointLight.
// ────────────────────────────────────────────────────────────
const CDL = {
  bone: "#e6dfd0",      // ivory hide
  boneDeep: "#b3a88f",
  socket: "#171009",    // dark eye sockets
  drape: "#a81f14",     // the crimson falls
  tendril: "#ece6d8",   // the pale fan
  membrane: "#f2e8d0",  // wing sail, pale gold-white
  nostril: "#4a3a2c",
};

// Ivory hide — bone streaks and hairline cracks over warm white,
// a faint crimson bleed at the low stops. Cached, seeded, shared.
function cdlBoneTex() {
  return canvasTex("cdl_bone_v1", 512, 512, (g, w, h) => {
    const base = g.createLinearGradient(0, 0, 0, h);
    base.addColorStop(0, "#efe9db");
    base.addColorStop(0.55, "#ddd4c1");
    base.addColorStop(1, "#bfb298");
    g.fillStyle = base; g.fillRect(0, 0, w, h);
    const R = mulberry32(9973);
    for (let i = 0; i < 900; i++) {
      g.fillStyle = R() > 0.6 ? "rgba(120,104,80,0.06)" : R() > 0.3 ? "rgba(255,255,250,0.06)" : "rgba(168,31,20,0.03)";
      g.fillRect(R() * w, R() * h, 2 + R() * 4, 2 + R() * 4);
    }
    // long bone streaks — the sinew of the card's face
    const streak = (x, y, len, wd, ang, fill, alpha) => {
      g.save(); g.translate(x, y); g.rotate(ang);
      g.beginPath();
      g.moveTo(0, -len / 2);
      g.quadraticCurveTo(wd, -len * 0.1, 0, len / 2);
      g.quadraticCurveTo(-wd, -len * 0.1, 0, -len / 2);
      g.closePath();
      g.globalAlpha = alpha; g.fillStyle = fill; g.fill();
      g.restore(); g.globalAlpha = 1;
    };
    for (let i = 0; i < 18; i++) {
      const x = R() * w, y = R() * h;
      streak(x, y, 110 + R() * 90, 8 + R() * 6, (R() - 0.5) * 0.4, "#c9bda2", 0.4);
      streak(x, y, 60 + R() * 50, 4 + R() * 3, (R() - 0.5) * 0.4, "#f5f0e4", 0.3);
    }
    // hairline cracks
    g.strokeStyle = "rgba(90,74,52,0.25)"; g.lineWidth = 1;
    for (let i = 0; i < 26; i++) {
      let x = R() * w, y = R() * h;
      g.beginPath(); g.moveTo(x, y);
      for (let k = 0; k < 4; k++) { x += (R() - 0.5) * 30; y += R() * 22; g.lineTo(x, y); }
      g.stroke();
    }
  });
}

// The golden watch — round pupil, patient. Shared by the main
// pair and the small scattered eyes.
function cdlEyeTex() {
  return canvasTex("cdl_eye_v1", 128, 128, (g) => {
    const r = g.createRadialGradient(64, 60, 4, 64, 64, 62);
    r.addColorStop(0, "#fff8d8");
    r.addColorStop(0.35, "#ffd257");
    r.addColorStop(0.7, "#d98d1e");
    r.addColorStop(0.92, "#5a350a");
    r.addColorStop(1, "#120c04");
    g.fillStyle = r; g.beginPath(); g.arc(64, 64, 62, 0, 7); g.fill();
    g.fillStyle = "rgba(14,10,6,0.9)";
    g.beginPath(); g.arc(64, 66, 12, 0, 7); g.fill();
    g.fillStyle = "rgba(255,255,255,0.85)";
    g.beginPath(); g.arc(48, 44, 6, 0, 7); g.fill();
  });
}

// The brow-eye — the crimson-ringed regard on the Lord's brow.
function cdlBrowEyeTex() {
  return canvasTex("cdl_brow_v1", 128, 128, (g) => {
    const r = g.createRadialGradient(64, 62, 3, 64, 64, 62);
    r.addColorStop(0, "#fff2c8");
    r.addColorStop(0.28, "#ff9b2e");
    r.addColorStop(0.55, "#e84a3a");
    r.addColorStop(0.78, "#5c120a");
    r.addColorStop(0.9, "#e84a3a");
    r.addColorStop(1, "#1a0604");
    g.fillStyle = r; g.beginPath(); g.arc(64, 64, 62, 0, 7); g.fill();
    g.fillStyle = "rgba(14,8,5,0.92)";
    g.beginPath(); g.ellipse(64, 65, 9, 18, 0, 0, 7); g.fill();
    g.fillStyle = "rgba(255,255,255,0.8)";
    g.beginPath(); g.arc(52, 46, 4.5, 0, 7); g.fill();
  });
}

// The vast ivory horns — bead-chains along the card's up-and-out
// sweep, thick at the temple, tapering past the crown line.
const CDL_HORN = (() => {
  const N = 7;
  const out = [];
  for (let i = 0; i <= N; i++) {
    const f = i / N;
    out.push([
      0.14 + Math.sin(f * 1.5) * 0.42,
      0.12 + f * 0.5 + f * f * 0.1,
      -0.06 - f * 0.06,
      0.10 * (1 - f * 0.68),
    ]);
  }
  return out;
})();

// The tendril fan — the radiating pale crown behind the skull.
const CDL_FAN = (() => {
  const out = [];
  for (let i = 0; i < 11; i++) {
    const a = -1.9 + (3.8 * i) / 10;
    out.push({ a, len: 0.5 - Math.abs(a) * 0.08, r: 0.05, ring: 0 });
  }
  for (let i = 0; i < 7; i++) {
    const a = -1.4 + (2.8 * i) / 6;
    out.push({ a, len: 0.32 - Math.abs(a) * 0.05, r: 0.04, ring: 1 });
  }
  return out;
})();

// The small watch-eyes — scattered across the skull per the card.
// [x, y, z, radius] in head-space; the card shows many, so many stay.
const CDL_SMALL_EYES = [
  [-0.20, 0.14, 0.16, 0.036],
  [0.20, 0.14, 0.16, 0.036],
  [-0.10, -0.05, 0.235, 0.03],
  [0.10, -0.05, 0.235, 0.03],
];

export function ChaosDragonLordBody({ pal }) {
  pal = pal || getBeingPalette("chaos_dragon");
  const bone = cdlBoneTex();
  const eye = cdlEyeTex();
  const brow = cdlBrowEyeTex();
  const fan = useRef([]);
  const browRef = useRef();
  const drapes = useRef([]);
  const rig = useBeingRig();
  // ALREADY SEATED. The presiding mass IS the pose — no leg slots.
  const armSlots = [rig.armL, rig.armR];
  const wingL = useRef();
  const wingR = useRef();
  const wisps = useRef([]);

  useFrame((s) => {
    const t = s.clock.elapsedTime;
    // the tendril fan breathes, each plume on its own fixed phase
    fan.current.forEach((grp, i) => {
      if (!grp) return;
      grp.rotation.z = -CDL_FAN[i].a + Math.sin(t * 0.65 + i * 0.85) * 0.035;
    });
    // the brow-eye's slow pulse
    if (browRef.current) {
      const p = 1 + Math.sin(t * 1.0) * 0.05;
      browRef.current.scale.set(p, p, 1);
    }
    // the great wings hold their spread, breathing like tide
    if (wingL.current) wingL.current.rotation.z = Math.sin(t * 0.55) * 0.05;
    if (wingR.current) wingR.current.rotation.z = Math.sin(t * 0.55 + 0.7) * 0.05;
    // the crimson drapes sway barely
    drapes.current.forEach((m, i) => {
      if (!m) return;
      m.rotation.x = 0.12 + Math.sin(t * 0.7 + i * 2.4) * 0.04;
    });
    wisps.current.forEach((m, i) => {
      if (!m) return;
      const p = t * 0.42 + i * 1.35;
      m.position.set(Math.cos(p) * 0.82, 0.78 + Math.sin(t * 1.05 + i) * 0.24, Math.sin(p) * 0.82);
      m.material.opacity = 0.28 + Math.sin(t * 1.5 + i) * 0.14;
    });
  });

  const hide = { map: bone, roughness: 0.9 };

  return (
    <group scale={0.92}>{/* lowered a notch so the Codex frames the head (caP'n, r3) */}
      {/* ── the presiding mass: haunches + upright chest ──
           RIG: torso slot. Drapes, neck and head all ride inside. */}
      <group ref={rig.torso}>
      <mesh position={[0, 0.30, -0.08]} scale={[1.28, 0.72, 1.2]} castShadow>
        <sphereGeometry args={[0.40, 18, 14]} />
        <meshStandardMaterial {...hide} />
      </mesh>
      <mesh position={[0, 0.58, 0.04]} scale={[1.0, 0.95, 1.02]} castShadow>
        <sphereGeometry args={[0.40, 18, 14]} />
        <meshStandardMaterial {...hide} />
      </mesh>

      {/* ── the crimson drapes falling from the chest ── */}
      {[-1, 1].map((sd, i) => (
        <mesh
          key={"drape" + sd}
          ref={(el) => (drapes.current[i] = el)}
          position={[sd * 0.26, 0.52, 0.32]}
          rotation={[0.12, 0, sd * 0.1]}
        >
          <coneGeometry args={[0.09, 0.5, 6]} />
          <meshStandardMaterial color={CDL.drape} roughness={0.85} transparent opacity={0.9} emissive={CDL.drape} emissiveIntensity={0.12} />
        </mesh>
      ))}

      {/* ── the serpentine neck, rising ── */}
      {[[0, 0.74, 0.14, 0.17], [0, 0.88, 0.2, 0.155], [0, 1.0, 0.23, 0.145]].map(([x, y, z, r], i) => (
        <mesh key={"neck" + i} position={[x, y, z]}>
          <sphereGeometry args={[r, 12, 10]} />
          <meshStandardMaterial {...hide} />
        </mesh>
      ))}

      {/* ── the head — broad ivory skull, the many-eyed regard ── */}
      <group ref={rig.head} position={[0, 1.12, 0.26]}>
        <mesh scale={[1.05, 0.92, 0.95]} castShadow>
          <sphereGeometry args={[0.29, 18, 14]} />
          <meshStandardMaterial {...hide} />
        </mesh>
        {/* the heavy brow ridge */}
        <mesh position={[0, 0.12, 0.13]} scale={[1.15, 0.5, 0.75]}>
          <sphereGeometry args={[0.2, 14, 10]} />
          <meshStandardMaterial {...hide} />
        </mesh>
        {/* reptilian wedge muzzle + nostrils (round 2: narrowed) */}
        <mesh position={[0, -0.08, 0.22]} scale={[0.78, 0.55, 1.1]}>
          <sphereGeometry args={[0.14, 12, 10]} />
          <meshStandardMaterial {...hide} />
        </mesh>
        {[-1, 1].map((sd) => (
          <mesh key={"nos" + sd} position={[sd * 0.045, -0.055, 0.365]}>
            <sphereGeometry args={[0.016, 6, 6]} />
            <meshStandardMaterial color={CDL.nostril} roughness={1} />
          </mesh>
        ))}
        {/* the golden pair, deep in shadowed sockets */}
        {[-1, 1].map((sd) => (
          <group key={"eye" + sd}>
            <mesh position={[sd * 0.14, 0.035, 0.17]} scale={[1, 1.05, 0.6]}>
              <sphereGeometry args={[0.095, 10, 8]} />
              <meshStandardMaterial color={CDL.socket} roughness={1} />
            </mesh>
            <mesh position={[sd * 0.135, 0.04, 0.262]} rotation={[0, sd * 0.24, 0]}>
              <circleGeometry args={[0.072, 20]} />
              <meshBasicMaterial map={eye} toneMapped={false} />
            </mesh>
          </group>
        ))}
        {/* the burning brow-eye, crimson-ringed */}
        <group position={[0, 0.17, 0.245]}>
          <mesh scale={[1, 1.1, 0.6]}>
            <sphereGeometry args={[0.062, 10, 8]} />
            <meshStandardMaterial color={CDL.socket} roughness={1} />
          </mesh>
          <mesh ref={browRef} position={[0, 0.004, 0.042]} rotation={[-0.2, 0, 0]}>
            <circleGeometry args={[0.05, 18]} />
            <meshBasicMaterial map={brow} toneMapped={false} />
          </mesh>
        </group>
        {/* the small watch-eyes, scattered — the card shows many */}
        {CDL_SMALL_EYES.map(([x, y, z, r], i) => (
          <group key={"small" + i} position={[x, y, z]}>
            <mesh scale={[1, 1.1, 0.55]}>
              <sphereGeometry args={[r * 1.35, 8, 6]} />
              <meshStandardMaterial color={CDL.socket} roughness={1} />
            </mesh>
            <mesh position={[0, 0, r * 0.85]} rotation={[0, x > 0 ? 0.5 : x < 0 ? -0.5 : 0, 0]}>
              <circleGeometry args={[r, 14]} />
              <meshBasicMaterial map={eye} toneMapped={false} />
            </mesh>
          </group>
        ))}
        {/* ── the vast ivory horns, up and out ── */}
        {[-1, 1].map((sd) => (
          <group key={"horn" + sd}>
            {CDL_HORN.map(([hx, hy, hz, hr], i) => (
              <mesh
                key={"h" + i}
                position={[sd * hx, hy, hz]}
                scale={[0.85, 1.1, 0.85]}
                castShadow={i === 0}
              >
                <sphereGeometry args={[hr, 12, 10]} />
                <meshStandardMaterial {...hide} />
              </mesh>
            ))}
          </group>
        ))}
        {/* ── the tendril fan — the pale radiating crown ── */}
        {CDL_FAN.map((p, i) => (
          <group
            key={"fan" + i}
            ref={(el) => (fan.current[i] = el)}
            position={[
              Math.sin(p.a) * (p.ring ? 0.22 : 0.28),
              Math.cos(p.a) * (p.ring ? 0.18 : 0.22) + 0.02,
              -0.15 - p.ring * 0.07,
            ]}
            rotation={[0.12, 0, -p.a]}
          >
            <mesh position={[0, p.len / 2 - 0.03, 0]}>
              <coneGeometry args={[p.r, p.len, 6]} />
              <meshStandardMaterial color={CDL.tendril} roughness={0.95} emissive={pal.glow} emissiveIntensity={p.ring ? 0.14 : 0.07} />
            </mesh>
          </group>
        ))}
      </group>

      </group>

      {/* ── the vast ivory wings, spread ── */}
      {[-1, 1].map((sd) => (
        <group key={"wing" + sd} position={[sd * 0.24, 0.72, -0.22]} scale={[sd * 1.35, 1.35, 1.35]} rotation={[0.15, sd * -0.55, 0.1]}>
          <group ref={sd < 0 ? wingL : wingR}>
            <CDWing boneMat={hide} membrane={CDL.membrane} mOpacity={0.5} glow={pal.glow} />
          </group>
        </group>
      ))}

      {/* ── ivory forelegs, planted ──
           RIG: the arm slots. Planted today, gripping tomorrow. */}
      {[-1, 1].map((sd, si) => (
        <group key={"fore" + sd} ref={armSlots[si]}>
          <mesh position={[sd * 0.22, 0.3, 0.34]} rotation={[0.28, 0, sd * 0.05]} castShadow>
            <capsuleGeometry args={[0.07, 0.24, 4, 8]} />
            <meshStandardMaterial {...hide} />
          </mesh>
          <mesh position={[sd * 0.23, 0.07, 0.44]} scale={[1, 0.6, 1.25]}>
            <sphereGeometry args={[0.095, 10, 8]} />
            <meshStandardMaterial {...hide} />
          </mesh>
        </group>
      ))}

      {/* ── the tail-coil resting at the base ── */}
      {[[-0.32, 0.12, -0.4, 0.09], [-0.44, 0.09, -0.5, 0.075], [-0.52, 0.07, -0.58, 0.06], [-0.56, 0.06, -0.64, 0.045]].map(([x, y, z, r], i, arr) => {
        const tip = i === arr.length - 1;
        return (
          <mesh key={"coil" + i} position={[x, y, z]}>
            <sphereGeometry args={[r, 10, 8]} />
            <meshStandardMaterial
              {...hide}
              emissive={tip ? pal.glow : "#000000"}
              emissiveIntensity={tip ? 0.8 : 0}
              toneMapped={!tip}
            />
          </mesh>
        );
      })}

      {/* ── the veil, widened to a Threat's measure ── */}
      <mesh position={[0, 0.72, 0.02]} scale={[1.05, 0.95, 1.15]}>
        <sphereGeometry args={[0.85, 16, 12]} />
        <meshBasicMaterial color={pal.glow} transparent opacity={0.09} side={THREE.BackSide} toneMapped={false} depthWrite={false} />
      </mesh>
      {[0, 1, 2, 3, 4].map((i) => (
        <mesh key={"wisp" + i} ref={(el) => (wisps.current[i] = el)}>
          <sphereGeometry args={[0.03, 6, 6]} />
          <meshBasicMaterial color={pal.glow} transparent opacity={0.34} toneMapped={false} depthWrite={false} />
        </mesh>
      ))}

      {/* under-glow (light budget: one, same as every dragon) */}
      <pointLight position={[0, 0.8, 0.4]} color={pal.glow} intensity={0.7} distance={3.2} />
    </group>
  );
}

// ────────────────────────────────────────────────────────────
// Z4Z DEMON — the gorgeous pass (Batch 3D-9). Beb tier.
// Built to the card (Terror TCG ZAZ-01): "they'll destroy
// anything for Khao." A black-iron colossus in a robe of plate —
// twin teal lens-eyes on a slab head, a flat cap with its
// antenna, two crimson crescent horns rising behind, two vast
// black claw-arms curving down like scythes, a burning red
// sigil on the chest (the flame-crown and its eye), and red
// flame-teeth licking up from the hem. A machine, so the slabs
// STAY slabs — boxes are honest geometry here; the card is made
// of them.
//
// Contract: head at the old z4z slab's height (~y 1.35), one
// pointLight at the old spec ([0, 1.4, 0.4], 0.7, 3). Colors
// are display-only dressing over the canon palette.
// ────────────────────────────────────────────────────────────
const Z4D = {
  iron: "#171a1e",      // black iron
  ironDeep: "#0b0d10",
  horn: "#c93a2c",      // the crimson crescents
  lens: "#3ec8d8",      // twin teal lenses
  sigil: "#e8402e",     // the chest flame-crown
  gear: "#d8b24a",      // the small gold watch-gear (canon main)
  flame: "#c93a2c",     // hem flame-teeth
};

// Black iron — riveted panel seams, scratches, a faint red rune
// bleed low in the plate. Cached, seeded, shared.
function z4dIronTex() {
  return canvasTex("z4d_iron_v1", 512, 512, (g, w, h) => {
    const base = g.createLinearGradient(0, 0, 0, h);
    base.addColorStop(0, "#1e2228");
    base.addColorStop(0.55, "#15181d");
    base.addColorStop(1, "#0b0d10");
    g.fillStyle = base; g.fillRect(0, 0, w, h);
    const R = mulberry32(3313);
    // panel seams
    g.strokeStyle = "rgba(0,0,0,0.45)"; g.lineWidth = 2;
    for (let i = 0; i < 7; i++) {
      const y = (i / 7) * h + R() * 24;
      g.beginPath(); g.moveTo(0, y); g.lineTo(w, y); g.stroke();
    }
    for (let i = 0; i < 5; i++) {
      const x = (i / 5) * w + R() * 30;
      g.beginPath(); g.moveTo(x, 0); g.lineTo(x, h); g.stroke();
    }
    // rivets along the seams
    for (let i = 0; i < 120; i++) {
      g.fillStyle = R() > 0.5 ? "rgba(120,130,145,0.35)" : "rgba(0,0,0,0.5)";
      g.beginPath(); g.arc(R() * w, R() * h, 1.6 + R() * 1.6, 0, 7); g.fill();
    }
    // scratches + the red bleed
    g.strokeStyle = "rgba(150,160,175,0.12)"; g.lineWidth = 1;
    for (let i = 0; i < 40; i++) {
      const x = R() * w, y = R() * h;
      g.beginPath(); g.moveTo(x, y); g.lineTo(x + (R() - 0.5) * 60, y + (R() - 0.5) * 24); g.stroke();
    }
    for (let i = 0; i < 26; i++) {
      g.fillStyle = "rgba(200,58,44,0.05)";
      g.fillRect(R() * w, h * 0.55 + R() * h * 0.45, 3 + R() * 8, 3 + R() * 8);
    }
  });
}

// The twin lenses — cold teal optics, a dark aperture, one hard
// specular. Machine eyes: perfect circles, no soul offered.
function z4dLensTex() {
  return canvasTex("z4d_lens_v1", 128, 128, (g) => {
    const r = g.createRadialGradient(64, 60, 4, 64, 64, 62);
    r.addColorStop(0, "#d8fbff");
    r.addColorStop(0.3, "#3ec8d8");
    r.addColorStop(0.66, "#177785");
    r.addColorStop(0.9, "#062a30");
    r.addColorStop(1, "#02090b");
    g.fillStyle = r; g.beginPath(); g.arc(64, 64, 62, 0, 7); g.fill();
    g.fillStyle = "rgba(2,10,12,0.9)";
    g.beginPath(); g.arc(64, 64, 14, 0, 7); g.fill();
    g.strokeStyle = "rgba(216,251,255,0.5)"; g.lineWidth = 3;
    g.beginPath(); g.arc(64, 64, 44, 0, 7); g.stroke();
    g.fillStyle = "rgba(255,255,255,0.9)";
    g.beginPath(); g.arc(48, 44, 6, 0, 7); g.fill();
  });
}

// The chest sigil — the flame-crown and its eye, painted on a
// transparent ground so it rides the robe as a living decal.
function z4dSigilTex() {
  return canvasTex("z4d_sigil_v1", 256, 256, (g) => {
    const stroke = (x, y, len, wd, ang, fill, alpha) => {
      g.save(); g.translate(x, y); g.rotate(ang);
      g.beginPath();
      g.moveTo(0, -len / 2);
      g.quadraticCurveTo(wd, -len * 0.1, 0, len / 2);
      g.quadraticCurveTo(-wd, -len * 0.1, 0, -len / 2);
      g.closePath();
      g.globalAlpha = alpha; g.fillStyle = fill; g.fill();
      g.restore(); g.globalAlpha = 1;
    };
    // the radiating flame-crown
    for (let i = 0; i < 9; i++) {
      const a = -1.6 + (3.2 * i) / 8;
      stroke(128 + Math.sin(a) * 52, 108 - Math.cos(a) * 40, 64 - Math.abs(a) * 12, 9, a, "#e8402e", 0.9);
      stroke(128 + Math.sin(a) * 52, 108 - Math.cos(a) * 40, 36 - Math.abs(a) * 7, 4.5, a, "#ff8a5c", 0.7);
    }
    // the eye orb at the crown's heart
    const r = g.createRadialGradient(128, 140, 3, 128, 140, 34);
    r.addColorStop(0, "#ffd8a0");
    r.addColorStop(0.4, "#ff7040");
    r.addColorStop(0.75, "#a01f10");
    r.addColorStop(1, "rgba(90,16,8,0)");
    g.fillStyle = r; g.beginPath(); g.arc(128, 140, 34, 0, 7); g.fill();
    g.fillStyle = "rgba(20,4,2,0.95)";
    g.beginPath(); g.arc(128, 140, 9, 0, 7); g.fill();
    // the fangs beneath
    for (let i = 0; i < 4; i++) {
      const x = 100 + i * 19;
      stroke(x, 190, 30, 5.5, Math.PI, "#e8402e", 0.8);
    }
  });
}

// The crimson crescents — bead-chains rising and curling inward
// behind the head, per the card. Deterministic curve.
const Z4D_HORN = (() => {
  const N = 5;
  const out = [];
  for (let i = 0; i <= N; i++) {
    const f = i / N;
    out.push([
      0.30 + Math.sin(f * Math.PI * 0.9) * 0.18,
      1.40 + f * 0.44,
      -0.04 - f * 0.05,
      0.05 * (1 - f * 0.62),
    ]);
  }
  return out;
})();

// The claw-arms — vast black scythes off the shoulders, curving
// out, down, and forward to the pincer tips.
const Z4D_CLAW = [
  [0.36, 0.96, 0.05, 0.095],
  [0.47, 0.81, 0.09, 0.085],
  [0.54, 0.63, 0.12, 0.078],
  [0.57, 0.45, 0.12, 0.068],
  [0.55, 0.29, 0.10, 0.058],
];

export function Z4ZBody({ pal }) {
  pal = pal || getBeingPalette("z4z");
  const iron = z4dIronTex();
  const lens = z4dLensTex();
  const sigilT = z4dSigilTex();
  const rig = useBeingRig();
  // NO LEGS. A robe of plate from hem to shoulder — this body does
  // not fold, it settles. The claw-arms ARE the arm slots and were
  // already grouped for their idle, so they simply adopt the rig.
  const sigil = useRef();
  const clawL = rig.armL;
  const clawR = rig.armR;
  const orb = useRef();
  const embers = useRef([]);

  useFrame((s) => {
    const t = s.clock.elapsedTime;
    // the sigil burns — never steady
    if (sigil.current) sigil.current.material.opacity = 0.8 + Math.sin(t * 2.3) * 0.14;
    // the claws breathe open and shut, half a phase apart
    if (clawL.current) clawL.current.rotation.z = Math.sin(t * 0.9) * 0.045;
    if (clawR.current) clawR.current.rotation.z = -Math.sin(t * 0.9 + Math.PI) * 0.045;
    // the antenna orb bobs
    if (orb.current) orb.current.position.y = 1.78 + Math.sin(t * 1.7) * 0.02;
    embers.current.forEach((m, i) => {
      if (!m) return;
      const p = t * 0.5 + i * 2.1;
      m.position.set(Math.cos(p) * 0.6, 0.5 + Math.sin(t * 1.3 + i) * 0.2, Math.sin(p) * 0.6);
      m.material.opacity = 0.3 + Math.sin(t * 1.9 + i) * 0.16;
    });
  });

  const plate = { map: iron, metalness: 0.35, roughness: 0.6 };

  return (
    <group scale={0.92}>{/* lowered a notch so the Codex frames the head (caP'n, r3) */}
      {/* ── the robe of plate, hem to shoulder ──
           RIG: torso slot. Shoulders ride it; the head is its own. */}
      <group ref={rig.torso}>
      <mesh position={[0, 0.55, 0]} castShadow>
        <coneGeometry args={[0.36, 1.05, 12]} />
        <meshStandardMaterial {...plate} />
      </mesh>
      {/* flame-teeth licking up from the hem */}
      {Array.from({ length: 6 }, (_, i) => {
        const a = (i / 6) * Math.PI * 2 + 0.3;
        return (
          <mesh
            key={"hemflame" + i}
            position={[Math.cos(a) * 0.29, 0.14, Math.sin(a) * 0.29]}
            rotation={[Math.sin(a) * -0.15, 0, Math.cos(a) * 0.15]}
          >
            <coneGeometry args={[0.05, 0.18, 5]} />
            <meshStandardMaterial color={Z4D.flame} emissive={Z4D.flame} emissiveIntensity={0.35} roughness={0.7} />
          </mesh>
        );
      })}
      {/* the burning chest sigil — flame-crown and eye */}
      <mesh ref={sigil} position={[0, 0.78, 0.285]} rotation={[-0.16, 0, 0]}>
        <circleGeometry args={[0.155, 24]} />
        <meshBasicMaterial map={sigilT} transparent opacity={0.9} toneMapped={false} depthWrite={false} />
      </mesh>

      {/* ── shoulders + the slab head ── */}
      {[-1, 1].map((sd) => (
        <mesh key={"sh" + sd} position={[sd * 0.30, 1.06, 0]} castShadow>
          <sphereGeometry args={[0.13, 12, 10]} />
          <meshStandardMaterial {...plate} />
        </mesh>
      ))}
      </group>

      {/* RIG: head slot — slab, lenses and watch-gear. */}
      <group ref={rig.head}>
      <mesh position={[0, 1.34, 0]} castShadow>
        <boxGeometry args={[0.44, 0.32, 0.34]} />
        <meshStandardMaterial {...plate} />
      </mesh>
      {/* the twin teal lenses */}
      {[-1, 1].map((sd) => (
        <mesh key={"lens" + sd} position={[sd * 0.10, 1.35, 0.176]}>
          <circleGeometry args={[0.068, 20]} />
          <meshBasicMaterial map={lens} toneMapped={false} />
        </mesh>
      ))}
      {/* the small gold watch-gear at the head's corner */}
      <group position={[0.165, 1.44, 0.173]}>
        <mesh>
          <circleGeometry args={[0.032, 12]} />
          <meshBasicMaterial color={Z4D.gear} toneMapped={false} />
        </mesh>
        <mesh position={[0, 0, 0.002]}>
          <circleGeometry args={[0.013, 8]} />
          <meshBasicMaterial color={Z4D.ironDeep} />
        </mesh>
      </group>
      {/* the flat cap, its riser, and the antenna */}
      <mesh position={[0, 1.532, 0]}>
        <boxGeometry args={[0.52, 0.06, 0.40]} />
        <meshStandardMaterial {...plate} />
      </mesh>
      <mesh position={[0, 1.585, 0]}>
        <boxGeometry args={[0.28, 0.05, 0.24]} />
        <meshStandardMaterial {...plate} />
      </mesh>
      <mesh position={[0, 1.68, 0]}>
        <cylinderGeometry args={[0.012, 0.012, 0.15, 5]} />
        <meshStandardMaterial color={pal.accent} metalness={0.5} roughness={0.5} />
      </mesh>
      <mesh ref={orb} position={[0, 1.78, 0]}>
        <sphereGeometry args={[0.034, 8, 8]} />
        <meshBasicMaterial color={Z4D.sigil} toneMapped={false} />
      </mesh>

      </group>

      {/* ── the crimson crescent horns, rising behind ── */}
      {[-1, 1].map((sd) => (
        <group key={"horn" + sd}>
          {Z4D_HORN.map(([hx, hy, hz, hr], i) => (
            <mesh key={"h" + i} position={[sd * hx, hy, hz]}>
              <sphereGeometry args={[hr, 10, 8]} />
              <meshStandardMaterial color={Z4D.horn} emissive={Z4D.horn} emissiveIntensity={0.28} roughness={0.6} />
            </mesh>
          ))}
        </group>
      ))}

      {/* ── the vast claw-arms, out, down, and forward ── */}
      {[-1, 1].map((sd) => (
        <group key={"claw" + sd} ref={sd < 0 ? clawL : clawR}>
          {Z4D_CLAW.map(([cx, cy, cz, cr], i) => (
            <mesh key={"c" + i} position={[sd * cx, cy, cz]} castShadow={i === 0}>
              <sphereGeometry args={[cr, 10, 8]} />
              <meshStandardMaterial {...plate} />
            </mesh>
          ))}
          {/* the pincer tips */}
          {[[0.51, 0.09], [0.58, 0.14]].map(([px, pz], k) => (
            <mesh key={"tip" + k} position={[sd * px, 0.18, pz]} rotation={[0.2, 0, sd * (k === 0 ? 0.35 : -0.25)]}>
              <coneGeometry args={[0.032, 0.16, 6]} />
              <meshStandardMaterial color={Z4D.ironDeep} metalness={0.4} roughness={0.5} />
            </mesh>
          ))}
        </group>
      ))}

      {/* ── three red embers drifting the plate ── */}
      {[0, 1, 2].map((i) => (
        <mesh key={"ember" + i} ref={(el) => (embers.current[i] = el)}>
          <sphereGeometry args={[0.024, 6, 6]} />
          <meshBasicMaterial color={Z4D.sigil} transparent opacity={0.35} toneMapped={false} depthWrite={false} />
        </mesh>
      ))}

      {/* under-glow (kept from the old body — light budget unchanged) */}
      <pointLight position={[0, 1.4, 0.4]} color={pal.glow} intensity={0.7} distance={3} />
    </group>
  );
}

// ────────────────────────────────────────────────────────────
// Z4Z WIZARD — the Ascent worn as flesh (Batch 3D-9). Champion.
// Built to the card (Terror TCG ZAZ-04): "Time is a cog in this
// machine's design." A verdigris enamel machine in scholar's
// dress — the wide flat hat with its watching finial, twin
// amber lenses on a slab face, a dark cloak over the shoulders,
// a rune column falling down the robe front, one articulated
// hand holding its instrument, and the pale moon-halo behind
// the head that the whole lineage carries.
//
// Contract: head at the old slab height (~y 1.36), one
// pointLight at the old spec. Display colors over canon palette.
// ────────────────────────────────────────────────────────────
const Z4W = {
  enamel: "#2e6e72",    // verdigris enamel
  enamelDeep: "#123638",
  cloak: "#0f2c30",
  lens: "#ffb547",      // twin amber lenses
  runeGold: "#d8b24a",  // canon main
  runeRed: "#c93a2c",
  halo: "#e8e2c8",
};

// Verdigris enamel — paneled teal with wear at the edges and a
// faint gold pinstripe. Cached, seeded, shared.
function z4wEnamelTex() {
  return canvasTex("z4w_enamel_v1", 512, 512, (g, w, h) => {
    const base = g.createLinearGradient(0, 0, 0, h);
    base.addColorStop(0, "#38838a");
    base.addColorStop(0.5, "#2a666b");
    base.addColorStop(1, "#164044");
    g.fillStyle = base; g.fillRect(0, 0, w, h);
    const R = mulberry32(6069);
    // panel seams
    g.strokeStyle = "rgba(6,20,22,0.4)"; g.lineWidth = 2;
    for (let i = 0; i < 6; i++) {
      const y = (i / 6) * h + R() * 26;
      g.beginPath(); g.moveTo(0, y); g.lineTo(w, y); g.stroke();
    }
    // enamel wear
    for (let i = 0; i < 700; i++) {
      g.fillStyle = R() > 0.5 ? "rgba(220,245,240,0.05)" : "rgba(6,20,22,0.08)";
      g.fillRect(R() * w, R() * h, 2 + R() * 4, 2 + R() * 4);
    }
    // faint gold pinstripes
    g.strokeStyle = "rgba(216,178,74,0.16)"; g.lineWidth = 1.4;
    for (let i = 0; i < 4; i++) {
      const x = (i / 4) * w + 30 + R() * 40;
      g.beginPath(); g.moveTo(x, 0); g.lineTo(x, h); g.stroke();
    }
  });
}

// The amber lenses — warm optics over a dark aperture. Kin to
// the Demon's but lit like a reading lamp, not a hunt.
function z4wLensTex() {
  return canvasTex("z4w_lens_v1", 128, 128, (g) => {
    const r = g.createRadialGradient(64, 60, 4, 64, 64, 62);
    r.addColorStop(0, "#fff2c8");
    r.addColorStop(0.32, "#ffb547");
    r.addColorStop(0.68, "#b06a12");
    r.addColorStop(0.9, "#3a2204");
    r.addColorStop(1, "#0c0702");
    g.fillStyle = r; g.beginPath(); g.arc(64, 64, 62, 0, 7); g.fill();
    g.fillStyle = "rgba(12,7,2,0.9)";
    g.beginPath(); g.arc(64, 64, 13, 0, 7); g.fill();
    g.strokeStyle = "rgba(255,242,200,0.45)"; g.lineWidth = 3;
    g.beginPath(); g.arc(64, 64, 42, 0, 7); g.stroke();
    g.fillStyle = "rgba(255,255,255,0.85)";
    g.beginPath(); g.arc(47, 44, 5.5, 0, 7); g.fill();
  });
}

export function Z4ZChampionBody({ pal }) {
  // RIG NOTE: NO LEGS (robe, hem to collar) and only ONE free arm —
  // the working hand holds its instrument at the chest, which is
  // card canon and is never taken away. This tier therefore drives
  // ONE-HANDED: the instrument stays, the other side grips.
  pal = pal || getBeingPalette("z4z");
  const enamel = z4wEnamelTex();
  const lens = z4wLensTex();
  const runes = useRef([]);
  const rig = useBeingRig();
  const finial = useRef();
  const halo = useRef();

  useFrame((s) => {
    const t = s.clock.elapsedTime;
    // the rune column pulses, one after another down the robe
    runes.current.forEach((m, i) => {
      if (!m) return;
      m.material.emissiveIntensity = 0.25 + Math.sin(t * 1.4 - i * 1.1) * 0.2;
    });
    // the finial watches — a slow turn, a small bob
    if (finial.current) {
      finial.current.rotation.y = t * 0.4;
      finial.current.position.y = 1.80 + Math.sin(t * 1.1) * 0.015;
    }
    // the moon-halo breathes
    if (halo.current) halo.current.material.opacity = 0.11 + Math.sin(t * 0.7) * 0.03;
  });

  const shell = { map: enamel, metalness: 0.3, roughness: 0.5 };

  return (
    <group scale={0.92}>{/* lowered a notch so the Codex frames the head (caP'n, r3) */}
      {/* ── the robe, hem to collar ──
           RIG: torso slot. The robe is the whole lower body. */}
      <group ref={rig.torso}>
      <mesh position={[0, 0.58, 0]} castShadow>
        <coneGeometry args={[0.33, 1.1, 12]} />
        <meshStandardMaterial {...shell} />
      </mesh>
      {/* the dark cloak over the shoulders */}
      <mesh position={[0, 1.02, -0.02]} castShadow>
        <coneGeometry args={[0.35, 0.52, 12]} />
        <meshStandardMaterial color={Z4W.cloak} roughness={0.95} />
      </mesh>
      {/* the rune column down the front */}
      {[[0.965, Z4W.runeGold], [0.80, Z4W.runeRed], [0.635, Z4W.runeGold]].map(([y, c], i) => (
        <mesh
          key={"rune" + i}
          ref={(el) => (runes.current[i] = el)}
          position={[0, y, 0.155 + (0.965 - y) * 0.3]}
          scale={[1, 1.5, 0.4]}
        >
          <sphereGeometry args={[0.042, 8, 8]} />
          <meshStandardMaterial color={c} emissive={c} emissiveIntensity={0.25} roughness={0.5} />
        </mesh>
      ))}
      {/* the working hand, holding its instrument at the chest
           RIG: the prop slot — held, never removed to free a hand. */}
      <group ref={rig.prop}>
      <mesh position={[0.17, 0.95, 0.13]} rotation={[0.4, 0, -0.9]}>
        <capsuleGeometry args={[0.035, 0.18, 4, 8]} />
        <meshStandardMaterial {...shell} />
      </mesh>
      <mesh position={[0.09, 0.88, 0.22]}>
        <sphereGeometry args={[0.042, 8, 8]} />
        <meshStandardMaterial color={Z4W.enamelDeep} metalness={0.3} roughness={0.6} />
      </mesh>
      <mesh position={[0.07, 0.88, 0.24]} rotation={[0.5, 0, 1.15]}>
        <cylinderGeometry args={[0.008, 0.008, 0.24, 5]} />
        <meshStandardMaterial color={Z4W.runeGold} metalness={0.6} roughness={0.4} emissive={Z4W.runeGold} emissiveIntensity={0.2} />
      </mesh>

      </group>
      </group>

      {/* ── the slab head + the amber regard ──
           RIG: head slot. */}
      <group ref={rig.head}>
      <mesh position={[0, 1.36, 0]} castShadow>
        <boxGeometry args={[0.36, 0.27, 0.27]} />
        <meshStandardMaterial {...shell} />
      </mesh>
      <mesh position={[0, 1.355, 0.137]}>
        <boxGeometry args={[0.30, 0.20, 0.012]} />
        <meshStandardMaterial color={Z4W.enamelDeep} metalness={0.3} roughness={0.6} />
      </mesh>
      {[-1, 1].map((sd) => (
        <mesh key={"lens" + sd} position={[sd * 0.082, 1.36, 0.148]}>
          <circleGeometry args={[0.062, 20]} />
          <meshBasicMaterial map={lens} toneMapped={false} />
        </mesh>
      ))}

      </group>

      {/* ── the wide flat hat + the watching finial ── */}
      <mesh position={[0, 1.545, 0]}>
        <boxGeometry args={[0.58, 0.045, 0.58]} />
        <meshStandardMaterial {...shell} />
      </mesh>
      <mesh position={[0, 1.625, 0]}>
        <boxGeometry args={[0.24, 0.13, 0.24]} />
        <meshStandardMaterial {...shell} />
      </mesh>
      <mesh position={[0, 1.72, 0]}>
        <cylinderGeometry args={[0.014, 0.02, 0.1, 6]} />
        <meshStandardMaterial color={pal.accent} metalness={0.5} roughness={0.5} />
      </mesh>
      <group ref={finial} position={[0, 1.80, 0]}>
        <mesh>
          <sphereGeometry args={[0.038, 10, 8]} />
          <meshStandardMaterial color={Z4W.runeGold} metalness={0.5} roughness={0.4} emissive={Z4W.runeGold} emissiveIntensity={0.3} />
        </mesh>
        <mesh position={[0, 0, 0.032]}>
          <circleGeometry args={[0.014, 8]} />
          <meshBasicMaterial color={Z4W.enamelDeep} />
        </mesh>
      </group>

      {/* ── the moon-halo behind the head ── */}
      <mesh ref={halo} position={[0, 1.42, -0.30]}>
        <circleGeometry args={[0.48, 28]} />
        <meshBasicMaterial color={Z4W.halo} transparent opacity={0.12} side={THREE.DoubleSide} toneMapped={false} depthWrite={false} />
      </mesh>

      {/* under-glow (light budget: one, same as the Demon) */}
      <pointLight position={[0, 1.4, 0.4]} color={pal.glow} intensity={0.7} distance={3} />
    </group>
  );
}

// ────────────────────────────────────────────────────────────
// Z4Z H'EHLL'S SWORD — the Ascent completed (Batch 3D-9). Lord.
// Built to the card (Terror TCG ZAZ-07): "No one has ever been
// able to have a conversation with one of these beings." An
// ivory-steel knight standing in judgment — the rounded helm
// with its barred visor, vast pale pauldrons, the golden
// halo-ring emblem on the chest with its dark core and red
// point, a dark faulds-skirt with white veils falling at the
// front, articulated arms at rest, and behind it the card's two
// vast golden arcs, sweeping up like the memory of wings.
//
// Contract: helm at ~y 1.5 (the Lord crown at y 1.5 seats onto
// the helm crest — the knight is crowned where he stands). One
// pointLight at the old spec.
// ────────────────────────────────────────────────────────────
const Z4S = {
  steel: "#d8dcd2",     // ivory steel
  steelDeep: "#8a9088",
  skirt: "#232a30",
  veil: "#eef2ea",
  emblem: "#d8b24a",    // the golden halo-ring (canon main)
  core: "#171a1e",
  point: "#c93a2c",
  arc: "#e8c86a",       // the vast golden arcs
};

// Ivory steel — brushed grain, faint panel seams, gold flecks.
// Cached, seeded, shared.
function z4sSteelTex() {
  return canvasTex("z4s_steel_v1", 512, 512, (g, w, h) => {
    const base = g.createLinearGradient(0, 0, 0, h);
    base.addColorStop(0, "#e6eae0");
    base.addColorStop(0.5, "#d2d6cc");
    base.addColorStop(1, "#aab0a6");
    g.fillStyle = base; g.fillRect(0, 0, w, h);
    const R = mulberry32(8887);
    // brushed grain
    g.strokeStyle = "rgba(120,128,118,0.10)"; g.lineWidth = 1;
    for (let i = 0; i < 90; i++) {
      const y = R() * h;
      g.beginPath(); g.moveTo(0, y); g.lineTo(w, y + (R() - 0.5) * 8); g.stroke();
    }
    // panel seams
    g.strokeStyle = "rgba(60,66,58,0.28)"; g.lineWidth = 2;
    for (let i = 0; i < 5; i++) {
      const y = (i / 5) * h + R() * 30;
      g.beginPath(); g.moveTo(0, y); g.lineTo(w, y); g.stroke();
    }
    // gold flecks
    for (let i = 0; i < 46; i++) {
      g.fillStyle = "rgba(216,178,74,0.25)";
      g.beginPath(); g.arc(R() * w, R() * h, 1 + R() * 1.8, 0, 7); g.fill();
    }
  });
}

export function Z4ZLordBody({ pal }) {
  pal = pal || getBeingPalette("z4z");
  const steel = z4sSteelTex();
  const rig = useBeingRig();
  // NO LEGS. A faulds-skirt to the ground — nothing folds.
  const armSlots = [rig.armL, rig.armR];
  const emblem = useRef();
  const arcL = useRef();
  const arcR = useRef();
  const veils = useRef([]);

  useFrame((s) => {
    const t = s.clock.elapsedTime;
    // the chest halo-ring pulses like a patient engine
    if (emblem.current) emblem.current.material.emissiveIntensity = 0.5 + Math.sin(t * 1.2) * 0.22;
    // the golden arcs shimmer and lean, tide-slow
    if (arcL.current) {
      arcL.current.material.opacity = 0.34 + Math.sin(t * 0.6) * 0.08;
      arcL.current.rotation.z = 0.5 + Math.sin(t * 0.4) * 0.02;
    }
    if (arcR.current) {
      arcR.current.material.opacity = 0.34 + Math.sin(t * 0.6 + 1.1) * 0.08;
      arcR.current.rotation.z = -0.5 - Math.sin(t * 0.4 + 0.8) * 0.02;
    }
    // the white veils sway barely
    veils.current.forEach((m, i) => {
      if (!m) return;
      m.rotation.x = 0.08 + Math.sin(t * 0.75 + i * 2.2) * 0.035;
    });
  });

  const armor = { map: steel, metalness: 0.5, roughness: 0.38 };

  return (
    <group scale={0.92}>{/* lowered a notch so the Codex frames the head (caP'n, r3) */}
      {/* ── the faulds-skirt, dark, to the ground ──
           RIG: torso slot. Skirt, chest and pauldrons ride it. */}
      <group ref={rig.torso}>
      <mesh position={[0, 0.44, 0]} castShadow>
        <coneGeometry args={[0.34, 0.86, 12]} />
        <meshStandardMaterial color={Z4S.skirt} metalness={0.25} roughness={0.7} />
      </mesh>
      {/* the white veils falling at the front */}
      {[-1, 1].map((sd, i) => (
        <mesh
          key={"veil" + sd}
          ref={(el) => (veils.current[i] = el)}
          position={[sd * 0.15, 0.62, 0.22]}
          rotation={[0.08, 0, sd * 0.06]}
          scale={[1, 1, 0.4]}
        >
          <coneGeometry args={[0.10, 0.56, 8]} />
          <meshStandardMaterial color={Z4S.veil} roughness={0.9} transparent opacity={0.85} />
        </mesh>
      ))}
      {/* the belt ornament */}
      <mesh position={[0, 0.86, 0.24]}>
        <sphereGeometry args={[0.042, 10, 8]} />
        <meshStandardMaterial color={Z4S.emblem} metalness={0.6} roughness={0.35} emissive={Z4S.emblem} emissiveIntensity={0.2} />
      </mesh>

      {/* ── the chest, the pauldrons, the halo-ring emblem ── */}
      <mesh position={[0, 1.06, 0]} castShadow>
        <boxGeometry args={[0.54, 0.42, 0.32]} />
        <meshStandardMaterial {...armor} />
      </mesh>
      {[-1, 1].map((sd) => (
        <mesh key={"pld" + sd} position={[sd * 0.37, 1.25, 0]} scale={[1.1, 0.8, 1]} castShadow>
          <sphereGeometry args={[0.16, 14, 12]} />
          <meshStandardMaterial {...armor} />
        </mesh>
      ))}
      <group position={[0, 1.07, 0.168]}>
        <mesh ref={emblem} rotation={[Math.PI / 2, 0, 0]}>
          <torusGeometry args={[0.10, 0.018, 8, 24]} />
          <meshStandardMaterial color={Z4S.emblem} metalness={0.6} roughness={0.3} emissive={Z4S.emblem} emissiveIntensity={0.5} toneMapped={false} />
        </mesh>
        <mesh>
          <sphereGeometry args={[0.052, 12, 10]} />
          <meshStandardMaterial color={Z4S.core} metalness={0.3} roughness={0.6} />
        </mesh>
        <mesh position={[0, -0.15, 0.01]}>
          <sphereGeometry args={[0.018, 8, 6]} />
          <meshBasicMaterial color={Z4S.point} toneMapped={false} />
        </mesh>
      </group>

      </group>

      {/* ── articulated arms, at rest ──
           RIG: the arm slots — segmented, so they articulate. */}
      {[-1, 1].map((sd, si) => (
        <group key={"arm" + sd} ref={armSlots[si]}>
          <mesh position={[sd * 0.37, 1.05, 0.02]} rotation={[0.06, 0, sd * 0.12]}>
            <capsuleGeometry args={[0.05, 0.22, 4, 8]} />
            <meshStandardMaterial {...armor} />
          </mesh>
          <mesh position={[sd * 0.40, 0.88, 0.03]}>
            <sphereGeometry args={[0.055, 10, 8]} />
            <meshStandardMaterial color={Z4S.steelDeep} metalness={0.5} roughness={0.4} />
          </mesh>
          <mesh position={[sd * 0.40, 0.72, 0.06]} rotation={[0.14, 0, 0]}>
            <capsuleGeometry args={[0.042, 0.18, 4, 8]} />
            <meshStandardMaterial {...armor} />
          </mesh>
          <mesh position={[sd * 0.40, 0.58, 0.09]}>
            <sphereGeometry args={[0.05, 10, 8]} />
            <meshStandardMaterial {...armor} />
          </mesh>
        </group>
      ))}

      {/* ── the helm and its barred visor ──
           RIG: head slot. */}
      <group ref={rig.head}>
      <mesh position={[0, 1.50, 0]} scale={[0.95, 1.05, 0.9]} castShadow>
        <sphereGeometry args={[0.17, 16, 14]} />
        <meshStandardMaterial {...armor} />
      </mesh>
      <mesh position={[0, 1.48, 0.115]} scale={[0.78, 0.85, 0.45]}>
        <sphereGeometry args={[0.12, 12, 10]} />
        <meshStandardMaterial color={Z4S.core} metalness={0.3} roughness={0.7} />
      </mesh>
      {[-0.032, 0, 0.032].map((ox, i) => (
        <mesh key={"bar" + i} position={[ox, 1.48, 0.168]}>
          <boxGeometry args={[0.011, 0.13, 0.008]} />
          <meshStandardMaterial color={Z4S.steel} metalness={0.5} roughness={0.4} />
        </mesh>
      ))}

      </group>

      {/* ── the vast golden arcs, the memory of wings ── */}
      <mesh ref={arcL} position={[-0.16, 1.05, -0.30]} rotation={[0.1, 0.2, 0.5]}>
        <torusGeometry args={[0.58, 0.024, 8, 26, Math.PI * 0.55]} />
        <meshBasicMaterial color={Z4S.arc} transparent opacity={0.36} toneMapped={false} depthWrite={false} />
      </mesh>
      <mesh ref={arcR} position={[0.16, 1.05, -0.30]} rotation={[0.1, -0.2, -0.5]} scale={[-1, 1, 1]}>
        <torusGeometry args={[0.58, 0.024, 8, 26, Math.PI * 0.55]} />
        <meshBasicMaterial color={Z4S.arc} transparent opacity={0.36} toneMapped={false} depthWrite={false} />
      </mesh>

      {/* under-glow (light budget: one, same as every Z4Z) */}
      <pointLight position={[0, 1.4, 0.4]} color={pal.glow} intensity={0.7} distance={3} />
    </group>
  );
}

// ────────────────────────────────────────────────────────────
// XULAN WANDERER — the gorgeous pass (Batch 3D-9). Beb tier.
// Built to the card (Terror TCG XLS-01): "a race of assassins
// that drift through the Void, in search of revenge." A tall
// hooded figure in deep teal robes sown with red blossoms, a
// gold-veiled face in the hood's shadow, the branch-staff held
// across the body still budding flowers, and the pale moon-halo
// behind the head. It does not walk — the lineage floats on its
// ring, and the ring stays exactly where it has always been.
//
// Contract: hood at the old cone-body's height (~y 1.5), the
// floating ring untouched at y 0.28, one pointLight at the old
// spec ([0, 1.4, 0.3], 0.7, 3). Display colors over canon.
// ────────────────────────────────────────────────────────────
const XLW = {
  robe: "#1d4a52",      // deep teal cloth
  robeDeep: "#0d262c",
  blossom: "#c93a52",   // the sown flowers
  blossomHi: "#e88aa0",
  veil: "#d8b24a",      // the gold face-veil
  branch: "#3a2c22",    // the budding staff
  halo: "#e8e6dc",
};

// The sown robe — deep teal cloth grain, stitch runs, and the
// red blossom clusters scattered like the card's. Cached, seeded.
function xlwRobeTex() {
  return canvasTex("xlw_robe_v1", 512, 512, (g, w, h) => {
    const base = g.createLinearGradient(0, 0, 0, h);
    base.addColorStop(0, "#265962");
    base.addColorStop(0.5, "#1b454d");
    base.addColorStop(1, "#0d262c");
    g.fillStyle = base; g.fillRect(0, 0, w, h);
    const R = mulberry32(2251);
    // cloth grain
    for (let i = 0; i < 800; i++) {
      g.fillStyle = R() > 0.5 ? "rgba(220,245,240,0.03)" : "rgba(4,14,17,0.08)";
      g.fillRect(R() * w, R() * h, 2 + R() * 3, 2 + R() * 3);
    }
    // stitch runs — long faint verticals
    g.strokeStyle = "rgba(180,210,205,0.08)"; g.lineWidth = 1;
    for (let i = 0; i < 14; i++) {
      const x = R() * w;
      g.beginPath(); g.moveTo(x, 0); g.lineTo(x + (R() - 0.5) * 30, h); g.stroke();
    }
    // the blossom clusters
    for (let i = 0; i < 22; i++) {
      const cx = R() * w, cy = R() * h;
      for (let b = 0; b < 4 + Math.floor(R() * 3); b++) {
        const a = R() * Math.PI * 2, rr = R() * 12;
        g.fillStyle = R() > 0.4 ? "rgba(201,58,82,0.75)" : "rgba(232,138,160,0.6)";
        g.beginPath();
        g.arc(cx + Math.cos(a) * rr, cy + Math.sin(a) * rr, 2.4 + R() * 2.6, 0, 7);
        g.fill();
      }
      g.fillStyle = "rgba(216,178,74,0.5)";
      g.beginPath(); g.arc(cx, cy, 1.8, 0, 7); g.fill();
    }
  });
}

export function XulanBody({ pal }) {
  // RIG NOTE: XULAN DRIFTS. No legs at any tier, and it never had
  // them — all three tiers carry the void-ring marked "kept exactly"
  // in the source, and that ring IS the drift. This body does not
  // sit and does not stand. A seated pose HOVERS it at seat height
  // and leaves the drift running. The void-ring is canon-locked:
  // this pass does not touch it, and no pose may.

  pal = pal || getBeingPalette("xulan");
  const robe = xlwRobeTex();
  const rig = useBeingRig();
  const ring = useRef();
  const staff = rig.prop;   // the branch-staff — held, never removed
  const halo = useRef();

  useFrame((s) => {
    const t = s.clock.elapsedTime;
    // the void-ring turns, patient
    if (ring.current) ring.current.rotation.z = t * 0.3;
    // the branch-staff sways with the drift
    if (staff.current) staff.current.rotation.z = 1.28 + Math.sin(t * 0.8) * 0.03;
    // the moon-halo breathes
    if (halo.current) halo.current.material.opacity = 0.13 + Math.sin(t * 0.65) * 0.03;
  });

  const cloth = { map: robe, roughness: 0.9, transparent: true, opacity: 0.95 };

  return (
    <group scale={0.92}>{/* lowered a notch so the Codex frames the head (caP'n, r3) */}
      {/* ── the robe, hem to hood ──
           RIG: torso slot. The robe is the whole body. */}
      <group ref={rig.torso}>
      <mesh position={[0, 0.9, 0]} castShadow>
        <coneGeometry args={[0.4, 1.3, 12]} />
        <meshStandardMaterial {...cloth} />
      </mesh>
      {/* shoulder drapes */}
      {[-1, 1].map((sd) => (
        <mesh key={"drape" + sd} position={[sd * 0.21, 1.3, 0.02]} scale={[1, 1.25, 0.9]}>
          <sphereGeometry args={[0.13, 12, 10]} />
          <meshStandardMaterial {...cloth} />
        </mesh>
      ))}
      {/* raised blossoms riding the cloth */}
      {[[-0.16, 1.18, 0.20], [0.12, 1.05, 0.26], [-0.06, 0.82, 0.31], [0.19, 0.68, 0.30], [-0.2, 0.55, 0.30], [0.05, 0.42, 0.37]].map(([x, y, z], i) => (
        <mesh key={"bl" + i} position={[x, y, z]}>
          <sphereGeometry args={[0.026, 8, 6]} />
          <meshStandardMaterial color={i % 2 ? XLW.blossom : XLW.blossomHi} roughness={0.7} emissive={XLW.blossom} emissiveIntensity={0.12} />
        </mesh>
      ))}

      </group>

      {/* ── the hood and the gold-veiled face ──
           RIG: head slot. */}
      <group ref={rig.head}>
      <mesh position={[0, 1.5, -0.02]}>
        <sphereGeometry args={[0.32, 12, 12, 0, Math.PI * 2, 0, Math.PI / 1.6]} />
        <meshStandardMaterial {...cloth} />
      </mesh>
      <mesh position={[0, 1.46, 0.10]}>
        <sphereGeometry args={[0.13, 12, 10]} />
        <meshStandardMaterial color={XLW.robeDeep} roughness={1} />
      </mesh>
      <mesh position={[0, 1.44, 0.17]} scale={[0.85, 1, 0.45]}>
        <sphereGeometry args={[0.115, 12, 10]} />
        <meshStandardMaterial color={XLW.veil} roughness={0.6} emissive={XLW.veil} emissiveIntensity={0.25} />
      </mesh>

      </group>

      {/* ── the branch-staff, held across the drift, budding ── */}
      <group ref={staff} position={[0.04, 1.0, 0.30]} rotation={[0.15, 0, 1.28]}>
        <mesh>
          <cylinderGeometry args={[0.012, 0.016, 0.9, 6]} />
          <meshStandardMaterial color={XLW.branch} roughness={0.95} />
        </mesh>
        {[[-0.3, 0.02], [0.05, 0.025], [0.32, 0.02]].map(([oy, r], i) => (
          <mesh key={"bud" + i} position={[0.02, oy, 0.015]}>
            <sphereGeometry args={[r, 8, 6]} />
            <meshStandardMaterial color={XLW.blossom} roughness={0.7} emissive={XLW.blossom} emissiveIntensity={0.15} />
          </mesh>
        ))}
      </group>
      {/* the sleeve reaching to the staff */}
      <mesh position={[0.14, 1.06, 0.18]} rotation={[0.5, 0, -0.7]}>
        <capsuleGeometry args={[0.05, 0.2, 4, 8]} />
        <meshStandardMaterial {...cloth} />
      </mesh>

      {/* ── the moon-halo behind the hood ── */}
      <mesh ref={halo} position={[0, 1.5, -0.26]}>
        <circleGeometry args={[0.40, 28]} />
        <meshBasicMaterial color={XLW.halo} transparent opacity={0.14} side={THREE.DoubleSide} toneMapped={false} depthWrite={false} />
      </mesh>

      {/* ── the void-ring — the drift itself, kept exactly ── */}
      <mesh ref={ring} rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.28, 0]}>
        <ringGeometry args={[0.4, 0.5, 20]} />
        <meshBasicMaterial color={pal.glow} transparent opacity={0.5} side={THREE.DoubleSide} toneMapped={false} />
      </mesh>

      {/* under-glow (kept from the old body — light budget unchanged) */}
      <pointLight position={[0, 1.4, 0.3]} color={pal.glow} intensity={0.7} distance={3} />
    </group>
  );
}

// ────────────────────────────────────────────────────────────
// XULAN SURGEON — the Ascent worn as flesh (Batch 3D-9).
// Champion. Built to the card (Terror TCG XLS-04): "Guardians
// of their weakest territories... these beings often abandon
// revenge." The wanderer, stilled — a vast-brimmed hat over a
// face of plain void, wide flowing blue-teal robes with hanging
// sleeves, the golden Y-stole falling from the shoulders to the
// hem, a warm ember-glow rising inside the robe's base, and the
// sun-halo behind. It has nothing to avenge anymore.
//
// Contract: hood-height preserved (~y 1.5 face, hat above), the
// floating ring untouched at y 0.28, one pointLight at the old
// spec. Display colors over canon.
// ────────────────────────────────────────────────────────────
const XLS = {
  robe: "#2a5a6e",      // flowing blue-teal
  robeDeep: "#10303c",
  hat: "#1a3a48",
  stole: "#d8a83a",     // the golden Y
  emberGlow: "#ff9b4a", // the warm hem light
  voidFace: "#0a1218",
  halo: "#efe3c2",
};

// The flowing robe — long soft fold streaks over blue-teal, and
// the warm glow painted rising from the hem. Cached, seeded.
function xlsRobeTex() {
  return canvasTex("xls_robe_v1", 512, 512, (g, w, h) => {
    const base = g.createLinearGradient(0, 0, 0, h);
    base.addColorStop(0, "#336a80");
    base.addColorStop(0.55, "#265468");
    base.addColorStop(0.82, "#2e5060");
    base.addColorStop(1, "#7a5a3c");
    g.fillStyle = base; g.fillRect(0, 0, w, h);
    const R = mulberry32(1481);
    // cloth grain
    for (let i = 0; i < 700; i++) {
      g.fillStyle = R() > 0.5 ? "rgba(230,245,250,0.03)" : "rgba(6,18,24,0.07)";
      g.fillRect(R() * w, R() * h, 2 + R() * 3, 2 + R() * 3);
    }
    // long fold streaks
    const streak = (x, y, len, wd, fill, alpha) => {
      g.save(); g.translate(x, y);
      g.beginPath();
      g.moveTo(0, -len / 2);
      g.quadraticCurveTo(wd, -len * 0.1, 0, len / 2);
      g.quadraticCurveTo(-wd, -len * 0.1, 0, -len / 2);
      g.closePath();
      g.globalAlpha = alpha; g.fillStyle = fill; g.fill();
      g.restore(); g.globalAlpha = 1;
    };
    for (let i = 0; i < 16; i++) {
      const x = R() * w, y = R() * h;
      streak(x, y, 140 + R() * 100, 9 + R() * 6, "#12333f", 0.4);
      streak(x + 4, y, 90 + R() * 60, 4 + R() * 3, "#4a8296", 0.25);
    }
    // the warm rise from the hem
    const glow = g.createLinearGradient(0, h * 0.7, 0, h);
    glow.addColorStop(0, "rgba(255,155,74,0)");
    glow.addColorStop(1, "rgba(255,155,74,0.35)");
    g.fillStyle = glow; g.fillRect(0, h * 0.7, w, h * 0.3);
  });
}

export function XulanChampionBody({ pal }) {
  // RIG NOTE: XULAN DRIFTS. No legs at any tier, and it never had
  // them — all three tiers carry the void-ring marked "kept exactly"
  // in the source, and that ring IS the drift. This body does not
  // sit and does not stand. A seated pose HOVERS it at seat height
  // and leaves the drift running. The void-ring is canon-locked:
  // this pass does not touch it, and no pose may.

  pal = pal || getBeingPalette("xulan");
  const robe = xlsRobeTex();
  const rig = useBeingRig();
  const ring = useRef();
  const sleeves = useRef([]);
  const stole = useRef([]);
  const halo = useRef();
  const hemGlow = useRef();

  useFrame((s) => {
    const t = s.clock.elapsedTime;
    if (ring.current) ring.current.rotation.z = t * 0.28;
    // the hanging sleeves sway with the drift
    sleeves.current.forEach((m, i) => {
      if (!m) return;
      m.rotation.z = (i === 0 ? 0.5 : -0.5) + Math.sin(t * 0.7 + i * 2.4) * 0.05;
    });
    // the golden Y shimmers
    stole.current.forEach((m, i) => {
      if (!m) return;
      m.material.emissiveIntensity = 0.3 + Math.sin(t * 1.1 + i * 0.9) * 0.15;
    });
    // the sun-halo and the hem-ember breathe together, offset
    if (halo.current) halo.current.material.opacity = 0.13 + Math.sin(t * 0.6) * 0.03;
    if (hemGlow.current) hemGlow.current.material.opacity = 0.2 + Math.sin(t * 0.9 + 1.2) * 0.06;
  });

  const cloth = { map: robe, roughness: 0.9, transparent: true, opacity: 0.96 };

  return (
    <group scale={0.92}>{/* lowered a notch so the Codex frames the head (caP'n, r3) */}
      {/* ── the wide robe, hem to collar ──
           RIG: torso slot. */}
      <group ref={rig.torso}>
      <mesh position={[0, 0.9, 0]} castShadow>
        <coneGeometry args={[0.44, 1.3, 14]} />
        <meshStandardMaterial {...cloth} />
      </mesh>
      {/* the hanging sleeves, wide as the card's ── */}
      {[-1, 1].map((sd, i) => (
        <mesh
          key={"slv" + sd}
          ref={(el) => (sleeves.current[i] = el)}
          position={[sd * 0.30, 1.06, 0.06]}
          rotation={[0.08, 0, sd * 0.5]}
          scale={[0.72, 1, 0.5]}
        >
          <coneGeometry args={[0.16, 0.6, 10]} />
          <meshStandardMaterial {...cloth} />
        </mesh>
      ))}
      {/* the golden Y-stole — shoulders to meeting, meeting to hem */}
      {[[-1, 0.42], [1, -0.42]].map(([sd, rz], i) => (
        <mesh
          key={"yarm" + i}
          ref={(el) => (stole.current[i] = el)}
          position={[sd * 0.085, 1.22, 0.185]}
          rotation={[-0.12, 0, rz]}
        >
          <boxGeometry args={[0.036, 0.30, 0.012]} />
          <meshStandardMaterial color={XLS.stole} metalness={0.4} roughness={0.45} emissive={XLS.stole} emissiveIntensity={0.3} />
        </mesh>
      ))}
      <mesh
        ref={(el) => (stole.current[2] = el)}
        position={[0, 0.72, 0.275]}
        rotation={[-0.32, 0, 0]}
      >
        <boxGeometry args={[0.036, 0.72, 0.012]} />
        <meshStandardMaterial color={XLS.stole} metalness={0.4} roughness={0.45} emissive={XLS.stole} emissiveIntensity={0.3} />
      </mesh>
      {/* the ember-glow rising inside the hem */}
      <mesh ref={hemGlow} position={[0, 0.16, 0]}>
        <coneGeometry args={[0.30, 0.26, 12, 1, true]} />
        <meshBasicMaterial color={XLS.emberGlow} transparent opacity={0.2} side={THREE.DoubleSide} toneMapped={false} depthWrite={false} />
      </mesh>

      </group>

      {/* ── the void face beneath the hat ──
           RIG: head slot — face and hat together. */}
      <group ref={rig.head}>
      <mesh position={[0, 1.5, 0.08]}>
        <sphereGeometry args={[0.12, 12, 10]} />
        <meshStandardMaterial color={XLS.voidFace} roughness={1} />
      </mesh>
      {/* the collar closing around it */}
      <mesh position={[0, 1.38, 0.02]} scale={[1, 0.6, 0.95]}>
        <sphereGeometry args={[0.17, 12, 10]} />
        <meshStandardMaterial {...cloth} />
      </mesh>

      {/* ── the vast hat ── */}
      <mesh position={[0, 1.65, 0]}>
        <cylinderGeometry args={[0.5, 0.5, 0.032, 22]} />
        <meshStandardMaterial color={XLS.hat} roughness={0.85} />
      </mesh>
      <mesh position={[0, 1.76, 0]}>
        <coneGeometry args={[0.15, 0.22, 12]} />
        <meshStandardMaterial color={XLS.hat} roughness={0.85} />
      </mesh>

      </group>

      {/* ── the sun-halo behind ── */}
      <mesh ref={halo} position={[0, 1.55, -0.28]}>
        <circleGeometry args={[0.5, 28]} />
        <meshBasicMaterial color={XLS.halo} transparent opacity={0.13} side={THREE.DoubleSide} toneMapped={false} depthWrite={false} />
      </mesh>

      {/* ── the void-ring, kept exactly ── */}
      <mesh ref={ring} rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.28, 0]}>
        <ringGeometry args={[0.4, 0.5, 20]} />
        <meshBasicMaterial color={pal.glow} transparent opacity={0.5} side={THREE.DoubleSide} toneMapped={false} />
      </mesh>

      {/* under-glow (light budget: one, same as the Wanderer) */}
      <pointLight position={[0, 1.4, 0.3]} color={pal.glow} intensity={0.7} distance={3} />
    </group>
  );
}

// ────────────────────────────────────────────────────────────
// XULAN SHADOW — the Ascent completed (Batch 3D-9). Lord.
// Built to the card (Terror TCG XLS-07): "Hate reverts the
// Surgeons to what they once were." A mountain of black cloth
// under a vast pale moon — the teal skull watching from the
// hood's dark hair, the golden filigree-tree emblem at the
// chest, and where the cloak parts, the spectral ribcage of
// what it once was, with a small skull resting at the hem.
// The card shows the dead thing wearing its own ghost — the
// strangeness stays.
//
// Contract: skull at ~y 1.52 (the Lord crown at y 1.5 seats
// into the hood's crest), the floating ring untouched at
// y 0.28, one pointLight at the old spec. Display colors over
// canon.
// ────────────────────────────────────────────────────────────
// Rear ghost-curls — two bead-chains coiling off the base, the
// Shadow's wake. Hand-tuned, rearward.
const XLH_CURLS = [
  [[-0.34, 0.20, -0.42], [-0.44, 0.15, -0.52], [-0.52, 0.11, -0.60], [-0.57, 0.09, -0.66], [-0.60, 0.08, -0.70]],
  [[0.34, 0.20, -0.42], [0.44, 0.15, -0.52], [0.52, 0.11, -0.60], [0.57, 0.09, -0.66], [0.60, 0.08, -0.70]],
];

const XLH = {
  cloak: "#0b1418",     // the black mountain
  cloakHi: "#16262c",
  skull: "#9fc4be",     // the teal skull
  socket: "#050a0c",
  hair: "#0a1013",
  tree: "#d8a83a",      // the filigree tree
  ribs: "#cfe0da",      // the spectral ribcage
  halo: "#ece9de",
};

// The mountain cloak — near-black with teal sheen streaks
// running like weather down a slope. Cached, seeded.
function xlhCloakTex() {
  return canvasTex("xlh_cloak_v1", 512, 512, (g, w, h) => {
    const base = g.createLinearGradient(0, 0, 0, h);
    base.addColorStop(0, "#16262c");
    base.addColorStop(0.5, "#0d181d");
    base.addColorStop(1, "#050a0c");
    g.fillStyle = base; g.fillRect(0, 0, w, h);
    const R = mulberry32(7333);
    for (let i = 0; i < 700; i++) {
      g.fillStyle = R() > 0.5 ? "rgba(120,180,175,0.03)" : "rgba(0,0,0,0.1)";
      g.fillRect(R() * w, R() * h, 2 + R() * 4, 2 + R() * 4);
    }
    // sheen streaks down the slope
    g.strokeStyle = "rgba(110,170,165,0.07)"; g.lineWidth = 2;
    for (let i = 0; i < 22; i++) {
      const x = R() * w;
      g.beginPath(); g.moveTo(x, R() * h * 0.3);
      g.quadraticCurveTo(x + (R() - 0.5) * 40, h * 0.6, x + (R() - 0.5) * 60, h);
      g.stroke();
    }
  });
}

// The spectral ribcage — spine, rib arcs, and the small pelvis
// bloom, painted pale on a transparent ground: the ghost the
// cloak parts to show.
function xlhRibsTex() {
  return canvasTex("xlh_ribs_v1", 256, 256, (g) => {
    g.strokeStyle = "rgba(207,224,218,0.85)"; g.lineWidth = 4;
    // the spine
    g.beginPath(); g.moveTo(128, 20); g.lineTo(128, 210); g.stroke();
    // rib arcs, symmetric, widening then closing
    g.lineWidth = 3;
    for (let i = 0; i < 7; i++) {
      const y = 44 + i * 24;
      const span = 34 + Math.sin((i / 6) * Math.PI) * 46;
      for (const sd of [-1, 1]) {
        g.beginPath();
        g.moveTo(128, y);
        g.quadraticCurveTo(128 + sd * span, y + 8, 128 + sd * span * 0.72, y + 22);
        g.stroke();
      }
    }
    // faint inner glow lines
    g.strokeStyle = "rgba(207,224,218,0.3)"; g.lineWidth = 7;
    g.beginPath(); g.moveTo(128, 30); g.lineTo(128, 205); g.stroke();
    // the small pelvis bloom
    g.strokeStyle = "rgba(207,224,218,0.7)"; g.lineWidth = 3;
    g.beginPath(); g.arc(128, 222, 16, Math.PI * 1.1, Math.PI * 1.9); g.stroke();
  });
}

// The filigree tree — gold branching on a transparent ground,
// the emblem the card sets at the Shadow's heart.
function xlhTreeTex() {
  return canvasTex("xlh_tree_v1", 128, 128, (g) => {
    g.strokeStyle = "rgba(216,168,58,0.9)"; g.lineWidth = 3;
    const branch = (x, y, ang, len, depth) => {
      if (depth === 0 || len < 4) return;
      const nx = x + Math.cos(ang) * len;
      const ny = y - Math.sin(ang) * len;
      g.lineWidth = Math.max(1, depth);
      g.beginPath(); g.moveTo(x, y); g.lineTo(nx, ny); g.stroke();
      branch(nx, ny, ang + 0.55, len * 0.62, depth - 1);
      branch(nx, ny, ang - 0.55, len * 0.62, depth - 1);
    };
    branch(64, 118, Math.PI / 2, 34, 5);
    // the ring around it
    g.strokeStyle = "rgba(216,168,58,0.5)"; g.lineWidth = 2.5;
    g.beginPath(); g.arc(64, 64, 58, 0, 7); g.stroke();
  });
}

export function XulanLordBody({ pal }) {
  // RIG NOTE: XULAN DRIFTS. No legs at any tier, and it never had
  // them — all three tiers carry the void-ring marked "kept exactly"
  // in the source, and that ring IS the drift. This body does not
  // sit and does not stand. A seated pose HOVERS it at seat height
  // and leaves the drift running. The void-ring is canon-locked:
  // this pass does not touch it, and no pose may.

  pal = pal || getBeingPalette("xulan");
  const cloak = xlhCloakTex();
  const ribsT = xlhRibsTex();
  const treeT = xlhTreeTex();
  const rig = useBeingRig();
  const ring = useRef();
  const ribs = useRef();
  const halo = useRef();
  const hairL = useRef();
  const hairR = useRef();
  const curls = useRef([]);
  const wisps = useRef([]);

  useFrame((s) => {
    const t = s.clock.elapsedTime;
    if (ring.current) ring.current.rotation.z = t * 0.24;
    // the wake-curls breathe, ghost-faint
    curls.current.forEach((m, i) => {
      if (!m) return;
      m.material.opacity = 0.26 + Math.sin(t * 1.1 + i * 0.7) * 0.1;
    });
    // the ghost within shimmers
    if (ribs.current) ribs.current.material.opacity = 0.55 + Math.sin(t * 0.9) * 0.14;
    // the vast moon breathes
    if (halo.current) halo.current.material.opacity = 0.15 + Math.sin(t * 0.5) * 0.035;
    // the hood's hair sways barely
    if (hairL.current) hairL.current.rotation.z = 0.12 + Math.sin(t * 0.6) * 0.03;
    if (hairR.current) hairR.current.rotation.z = -0.12 - Math.sin(t * 0.6 + 1.9) * 0.03;
    wisps.current.forEach((m, i) => {
      if (!m) return;
      const p = t * 0.4 + i * 1.3;
      m.position.set(Math.cos(p) * 0.8, 0.8 + Math.sin(t * 1.0 + i) * 0.24, Math.sin(p) * 0.8);
      m.material.opacity = 0.26 + Math.sin(t * 1.4 + i) * 0.13;
    });
  });

  const shroud = { map: cloak, roughness: 0.95 };

  return (
    <group scale={0.92}>{/* lowered a notch so the Codex frames the head (caP'n, r3) */}
      {/* ── the mountain: cloak + base flare ──
           RIG: torso slot. Ribcage and heart-tree ride inside. */}
      <group ref={rig.torso}>
      <mesh position={[0, 0.85, 0]} castShadow>
        <coneGeometry args={[0.55, 1.3, 16]} />
        <meshStandardMaterial {...shroud} />
      </mesh>
      <mesh position={[0, 0.2, 0]} castShadow>
        <coneGeometry args={[0.62, 0.38, 16]} />
        <meshStandardMaterial {...shroud} />
      </mesh>

      {/* ── where the cloak parts: the spectral ribcage ── */}
      <mesh ref={ribs} position={[0, 0.60, 0.375]} rotation={[-0.4, 0, 0]}>
        <planeGeometry args={[0.36, 0.72]} />
        <meshBasicMaterial map={ribsT} transparent opacity={0.6} toneMapped={false} depthWrite={false} />
      </mesh>
      {/* the small skull resting at the hem */}
      <group position={[0, 0.15, 0.55]}>
        <mesh scale={[0.95, 1, 0.85]}>
          <sphereGeometry args={[0.05, 10, 8]} />
          <meshStandardMaterial color={XLH.ribs} roughness={0.9} />
        </mesh>
        {[-1, 1].map((sd) => (
          <mesh key={"bs" + sd} position={[sd * 0.018, 0.005, 0.038]}>
            <sphereGeometry args={[0.012, 6, 6]} />
            <meshStandardMaterial color={XLH.socket} roughness={1} />
          </mesh>
        ))}
      </group>

      {/* ── the filigree tree at the heart ── */}
      <mesh position={[0, 1.10, 0.185]} rotation={[-0.4, 0, 0]}>
        <circleGeometry args={[0.095, 22]} />
        <meshBasicMaterial map={treeT} transparent toneMapped={false} depthWrite={false} />
      </mesh>

      </group>

      {/* ── the hood, its hair, and the watching skull ──
           RIG: head slot. */}
      <group ref={rig.head}>
      <mesh position={[0, 1.56, -0.03]}>
        <sphereGeometry args={[0.20, 14, 12, 0, Math.PI * 2, 0, Math.PI / 1.5]} />
        <meshStandardMaterial {...shroud} />
      </mesh>
      <group ref={hairL} position={[-0.14, 1.46, 0.05]}>
        <mesh rotation={[0, 0, 0.12]}>
          <coneGeometry args={[0.05, 0.34, 6]} />
          <meshStandardMaterial color={XLH.hair} roughness={1} />
        </mesh>
      </group>
      <group ref={hairR} position={[0.14, 1.46, 0.05]}>
        <mesh rotation={[0, 0, -0.12]}>
          <coneGeometry args={[0.05, 0.34, 6]} />
          <meshStandardMaterial color={XLH.hair} roughness={1} />
        </mesh>
      </group>
      <mesh position={[0, 1.52, 0.06]} scale={[0.9, 1.05, 0.85]}>
        <sphereGeometry args={[0.14, 14, 12]} />
        <meshStandardMaterial color={XLH.skull} roughness={0.85} />
      </mesh>
      {[-1, 1].map((sd) => (
        <mesh key={"socket" + sd} position={[sd * 0.05, 1.54, 0.165]} scale={[1, 1.15, 0.5]}>
          <sphereGeometry args={[0.038, 8, 6]} />
          <meshStandardMaterial color={XLH.socket} roughness={1} />
        </mesh>
      ))}
      <mesh position={[0, 1.475, 0.175]} scale={[0.8, 1.2, 0.5]}>
        <sphereGeometry args={[0.014, 6, 6]} />
        <meshStandardMaterial color={XLH.socket} roughness={1} />
      </mesh>

      </group>

      {/* ── the cloak's back — fold ridges and the hood veil ── */}
      {[[-0.24, -0.30], [0, -0.38], [0.24, -0.30]].map(([x, z], i) => (
        <mesh key={"ridge" + i} position={[x, 0.78, z]} rotation={[0.32, 0, x * -0.35]} scale={[0.55, 1, 0.5]}>
          <coneGeometry args={[0.14, 1.0, 8]} />
          <meshStandardMaterial color={XLH.cloakHi} roughness={0.95} />
        </mesh>
      ))}
      <mesh position={[0, 1.28, -0.20]} rotation={[0.42, 0, 0]} scale={[1, 1, 0.4]}>
        <coneGeometry args={[0.17, 0.62, 10]} />
        <meshStandardMaterial map={xlhCloakTex()} roughness={0.95} transparent opacity={0.92} />
      </mesh>
      {/* ── two ghost-curls coiling off the base, rearward ── */}
      {XLH_CURLS.map((path, ci) => (
        <group key={"curl" + ci}>
          {path.map(([x, y, z], bi) => (
            <mesh key={"cb" + bi} ref={(el) => (curls.current[ci * 5 + bi] = el)} position={[x, y, z]}>
              <sphereGeometry args={[Math.max(0.018, 0.048 - bi * 0.006), 8, 6]} />
              <meshBasicMaterial color={XLH.ribs} transparent opacity={0.3} toneMapped={false} depthWrite={false} />
            </mesh>
          ))}
        </group>
      ))}

      {/* ── the vast moon behind ── */}
      <mesh ref={halo} position={[0, 1.52, -0.30]}>
        <circleGeometry args={[0.58, 30]} />
        <meshBasicMaterial color={XLH.halo} transparent opacity={0.15} side={THREE.DoubleSide} toneMapped={false} depthWrite={false} />
      </mesh>

      {/* ── the spectral veil: rim aura + five wisps ── */}
      <mesh position={[0, 0.85, 0]} scale={[1.0, 1.0, 1.0]}>
        <sphereGeometry args={[0.82, 16, 12]} />
        <meshBasicMaterial color={pal.glow} transparent opacity={0.07} side={THREE.BackSide} toneMapped={false} depthWrite={false} />
      </mesh>
      {[0, 1, 2, 3, 4].map((i) => (
        <mesh key={"wisp" + i} ref={(el) => (wisps.current[i] = el)}>
          <sphereGeometry args={[0.028, 6, 6]} />
          <meshBasicMaterial color={pal.glow} transparent opacity={0.32} toneMapped={false} depthWrite={false} />
        </mesh>
      ))}

      {/* ── the void-ring, kept exactly ── */}
      <mesh ref={ring} rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.28, 0]}>
        <ringGeometry args={[0.4, 0.5, 20]} />
        <meshBasicMaterial color={pal.glow} transparent opacity={0.5} side={THREE.DoubleSide} toneMapped={false} />
      </mesh>

      {/* under-glow (light budget: one, same as every Xulan) */}
      <pointLight position={[0, 1.4, 0.3]} color={pal.glow} intensity={0.7} distance={3} />
    </group>
  );
}

// ════════════════════════════════════════════════════════════
//  THE OTHER EIGHT (Batch 3D-23) — Beb tier
//  ------------------------------------------------------------
//  Terror has TWELVE lineages. Four have worn flesh since 3D-9.
//  These are the other eight, each translated from its own Beb
//  card — read directly out of Assets/terror-assets, not from
//  memory. The stat line under each is the card's, transcribed.
//
//  Tier note: these ship at BEB only. BeingMesh's dispatch sends
//  Champion and Lord to the Beb body for these eight, exactly as
//  it did for the Dragon/Z4Z/Xulan between 3D-8 and 3D-9 — the
//  Ascent still READS on them (ring at Champion, crown at Lord,
//  worn by PlayerBody), so this is a polish gap and not a hole.
//  Sixteen bodies remain and each is an ADD, never a refactor.
// ════════════════════════════════════════════════════════════

// one shared painted eye — every lineage tunes the four stops
function eyeDisc(key, core, iris, rim, pupil, slit) {
  return canvasTex(key, 128, 128, (g) => {
    const r = g.createRadialGradient(64, 58, 3, 64, 64, 62);
    r.addColorStop(0, core); r.addColorStop(0.36, iris);
    r.addColorStop(0.8, rim); r.addColorStop(1, "#0d0a08");
    g.fillStyle = r; g.beginPath(); g.arc(64, 64, 62, 0, 7); g.fill();
    if (pupil) {
      g.fillStyle = pupil; g.beginPath();
      if (slit) g.ellipse(64, 66, 8, 32, 0, 0, 7); else g.ellipse(64, 66, 20, 22, 0, 0, 7);
      g.fill();
    }
    g.fillStyle = "rgba(255,255,255,0.9)"; g.beginPath(); g.arc(47, 43, 7, 0, 7); g.fill();
    g.fillStyle = "rgba(255,255,255,0.45)"; g.beginPath(); g.arc(79, 83, 3.2, 0, 7); g.fill();
  });
}
const leaf = (g, x, y, len, wd, ang, fill, alpha) => {
  g.save(); g.translate(x, y); g.rotate(ang);
  g.beginPath(); g.moveTo(0, -len / 2);
  g.quadraticCurveTo(wd, -len * 0.1, 0, len / 2);
  g.quadraticCurveTo(-wd, -len * 0.1, 0, -len / 2);
  g.closePath(); g.globalAlpha = alpha; g.fillStyle = fill; g.fill();
  g.restore(); g.globalAlpha = 1;
};
// riveted metal plate — shared by both Robot-ilk lineages
function plateTex(key, hi, mid, lo, rivet) {
  return canvasTex(key, 256, 256, (g, w, h) => {
    const b = g.createLinearGradient(0, 0, 0, h);
    b.addColorStop(0, hi); b.addColorStop(0.55, mid); b.addColorStop(1, lo);
    g.fillStyle = b; g.fillRect(0, 0, w, h);
    const R = mulberry32(hashId(key) + 5);
    for (let i = 0; i < 700; i++) {
      g.fillStyle = `rgba(${R() > 0.5 ? "255,255,255" : "0,0,0"},${0.02 + R() * 0.05})`;
      g.fillRect(R() * w, R() * h, 2 + R() * 6, 1 + R() * 2);
    }
    for (let i = 0; i < 8; i++) {
      const y = R() * h;
      g.strokeStyle = "rgba(0,0,0,0.45)"; g.lineWidth = 2.5;
      g.beginPath(); g.moveTo(0, y); g.lineTo(w, y); g.stroke();
    }
    for (let i = 0; i < 70; i++) {
      const x = R() * w, y = R() * h, rr = 2.5 + R() * 2;
      const s = g.createRadialGradient(x - rr * .3, y - rr * .3, .5, x, y, rr);
      s.addColorStop(0, rivet); s.addColorStop(1, "rgba(0,0,0,0.55)");
      g.fillStyle = s; g.beginPath(); g.arc(x, y, rr, 0, 7); g.fill();
    }
  });
}

// ── SHADOW FOX · Beast · Phantom · Lyf 6 / Lyt 2 ─────────────
// "Mischievous pups from birth, born at the Bottomless Depths of
//  the darkest Shadow Spheres... but where will you find them?
//  Perhaps lurking in a nearby shadow..."
// The card: a near-black pup flecked like a night sky, ears lined
// hot orange, feet burning out at the ankle — and THREE tails,
// spiky and weightless. The tails are the whole silhouette.
const SFX = { coat: "#0b0b12", fleck: "#e8e0ff", ember: "#ff8a3a", hot: "#ffc46a", inner: "#ff7a2e" };
function sfxCoatTex() {
  return canvasTex("sfx_coat_v1", 512, 512, (g, w, h) => {
    const b = g.createLinearGradient(0, 0, 0, h);
    b.addColorStop(0, "#15151f"); b.addColorStop(0.6, "#0c0c14"); b.addColorStop(1, "#060609");
    g.fillStyle = b; g.fillRect(0, 0, w, h);
    const R = mulberry32(5309);
    for (let i = 0; i < 900; i++) {
      g.fillStyle = `rgba(4,4,8,${0.06 + R() * 0.10})`;
      g.fillRect(R() * w, R() * h, 3 + R() * 6, 2 + R() * 4);
    }
    // it is not a pattern, it is a NIGHT SKY worn as a coat
    for (let i = 0; i < 210; i++) {
      const x = R() * w, y = R() * h, s = 0.7 + R() * 2.1;
      g.fillStyle = `rgba(232,224,255,${0.25 + R() * 0.6})`;
      g.beginPath(); g.arc(x, y, s, 0, 7); g.fill();
      if (R() > 0.9) {
        g.strokeStyle = `rgba(232,224,255,0.25)`; g.lineWidth = 0.8;
        g.beginPath(); g.moveTo(x - s * 3, y); g.lineTo(x + s * 3, y);
        g.moveTo(x, y - s * 3); g.lineTo(x, y + s * 3); g.stroke();
      }
    }
    for (let i = 0; i < 10; i++) leaf(g, R() * w, R() * h, 90 + R() * 120, 7 + R() * 8, (R() - .5) * 1.1, "#2a1a2e", 0.4);
  });
}
const SFX_TAIL = [
  [0, 0.00, -0.03, 0.070], [0, 0.09, -0.14, 0.064], [0, 0.20, -0.22, 0.055],
  [0, 0.32, -0.26, 0.045], [0, 0.43, -0.25, 0.034], [0, 0.52, -0.20, 0.022],
];
export function ShadowFoxBody({ pal }) {
  pal = pal || getBeingPalette("shadow_fox");
  const coat = sfxCoatTex();
  const eye = eyeDisc("sfx_eye_v1", "#fff4d8", "#ffb02e", "#5a2a06", "rgba(10,6,4,0.9)", true);
  const rig = useBeingRig();
  const legSlots = [rig.armL, rig.armR, rig.legL, rig.legR];
  const tails = useRef([]);
  const earL = useRef(); const earR = useRef();
  useFrame((s) => {
    const t = s.clock.elapsedTime;
    // three tails, three rates — they never move as one object
    tails.current.forEach((g0, i) => {
      if (!g0) return;
      g0.rotation.z = Math.sin(t * (1.3 + i * 0.29) + i * 2.1) * 0.30;
      g0.rotation.x = Math.sin(t * (0.9 + i * 0.21) + i * 1.3) * 0.16;
    });
    if (earL.current) earL.current.rotation.z = 0.26 + Math.sin(t * 2.2) * 0.08;
    if (earR.current) earR.current.rotation.z = -0.26 - Math.sin(t * 2.2 + 1.8) * 0.08;
  });
  const fur = { map: coat, roughness: 1, emissive: SFX.fleck, emissiveIntensity: 0.05 };
  const emb = { color: SFX.ember, emissive: SFX.ember, emissiveIntensity: 0.7, roughness: 0.7, toneMapped: false };
  return (
    <group>
      <mesh position={[0, 0.40, -0.20]} scale={[0.95, 1.0, 1.0]} castShadow>
        <sphereGeometry args={[0.185, 14, 12]} /><meshStandardMaterial {...fur} />
      </mesh>
      <mesh position={[0, 0.41, 0.03]} scale={[0.88, 0.84, 1.35]} castShadow>
        <sphereGeometry args={[0.190, 16, 12]} /><meshStandardMaterial {...fur} />
      </mesh>
      <mesh position={[0, 0.44, 0.26]} scale={[1.0, 0.96, 0.92]}>
        <sphereGeometry args={[0.180, 14, 12]} /><meshStandardMaterial {...fur} />
      </mesh>
      <group ref={rig.head} position={[0, 0.60, 0.40]}>
        <mesh scale={[0.94, 0.90, 1.0]}><sphereGeometry args={[0.140, 14, 12]} /><meshStandardMaterial {...fur} /></mesh>
        <mesh position={[0, -0.048, 0.140]} scale={[0.60, 0.55, 1.75]}>
          <sphereGeometry args={[0.078, 12, 10]} /><meshStandardMaterial {...fur} />
        </mesh>
        <mesh position={[0, -0.044, 0.250]}><sphereGeometry args={[0.024, 8, 8]} /><meshStandardMaterial color="#241820" roughness={0.55} /></mesh>
        <group ref={earL} position={[-0.088, 0.125, -0.012]}>
          <mesh rotation={[-0.08, -0.16, 0]}><coneGeometry args={[0.062, 0.230, 9]} /><meshStandardMaterial {...fur} /></mesh>
          <mesh position={[0, 0.004, 0.030]} rotation={[-0.08, -0.16, 0]}>
            <coneGeometry args={[0.040, 0.180, 8]} /><meshStandardMaterial color={SFX.inner} emissive={SFX.inner} emissiveIntensity={0.5} roughness={0.9} />
          </mesh>
        </group>
        <group ref={earR} position={[0.088, 0.125, -0.012]}>
          <mesh rotation={[-0.08, 0.16, 0]}><coneGeometry args={[0.062, 0.230, 9]} /><meshStandardMaterial {...fur} /></mesh>
          <mesh position={[0, 0.004, 0.030]} rotation={[-0.08, 0.16, 0]}>
            <coneGeometry args={[0.040, 0.180, 8]} /><meshStandardMaterial color={SFX.inner} emissive={SFX.inner} emissiveIntensity={0.5} roughness={0.9} />
          </mesh>
        </group>
        {[-1, 1].map((sd) => (
          <group key={"e" + sd} position={[sd * 0.072, 0.024, 0.092]} rotation={[0, sd * 0.44, 0]}>
            <mesh scale={[1, 1, 0.5]}><sphereGeometry args={[0.040, 8, 8]} /><meshStandardMaterial color="#08080c" roughness={1} /></mesh>
            <mesh position={[0, 0, 0.024]}><circleGeometry args={[0.033, 18]} /><meshBasicMaterial map={eye} toneMapped={false} /></mesh>
          </group>
        ))}
      </group>
      {/* legs that burn out at the ankle — the card's ember feet */}
      {[[-0.115, 0.235], [0.115, 0.235], [-0.120, -0.220], [0.120, -0.220]].map(([ox, oz], i) => (
        <group key={"lg" + i} ref={legSlots[i]} position={[ox, 0, oz]}>
          <mesh position={[0, 0.255, 0]}><capsuleGeometry args={[0.045, 0.15, 4, 8]} /><meshStandardMaterial {...fur} /></mesh>
          <mesh position={[0, 0.115, 0.010]}><capsuleGeometry args={[0.034, 0.13, 4, 8]} /><meshStandardMaterial {...emb} /></mesh>
          <mesh position={[0, 0.038, 0.026]} scale={[1, 0.7, 1.25]}><sphereGeometry args={[0.042, 10, 8]} /><meshStandardMaterial {...emb} /></mesh>
          <mesh position={[0, 0.075, 0.010]}><sphereGeometry args={[0.075, 8, 8]} /><meshBasicMaterial color={SFX.hot} transparent opacity={0.16} toneMapped={false} depthWrite={false} /></mesh>
        </group>
      ))}
      {/* THE THREE TAILS — held up, spiky, ember at the root */}
      {[-0.30, 0, 0.30].map((yaw, ti) => (
        <group key={"tl" + ti} ref={(el) => (tails.current[ti] = el)} position={[0, 0.46, -0.34]} rotation={[0, yaw, 0]}>
          {SFX_TAIL.map(([x, y, z, r], i) => (
            <mesh key={i} position={[x, y, z]}>
              <coneGeometry args={[r, r * 3.2, 6]} />
              <meshStandardMaterial
                map={coat} color={i === 0 ? SFX.ember : "#ffffff"}
                emissive={i === 0 ? SFX.ember : SFX.fleck}
                emissiveIntensity={i === 0 ? 0.6 : 0.06} roughness={1}
              />
            </mesh>
          ))}
        </group>
      ))}
      <mesh position={[0, 0.45, 0]}>
        <sphereGeometry args={[0.62, 14, 10]} />
        <meshBasicMaterial color={pal.glow} transparent opacity={0.06} side={THREE.BackSide} toneMapped={false} depthWrite={false} />
      </mesh>
    </group>
  );
}

// ── INVERTED SERPENT · Beast · Phantom · Lyf 4 / Lyt 4 ───────
// "Although small, do not be fooled by their appearance; these
//  snakes, born from the Shadow Gardens, are protecters to the
//  entrance of the Shadow Forest."
// The card: a small pale ghost of a snake sitting UP on its own
// coil, enormous dark eyes, two tiny tendril arms held out. The
// coil is the body and the pose — it never stands, it stacks.
const INS = { skin: "#eae6dc", deep: "#b9b2a4", coil: "#241f28", eyeD: "#141018", blush: "#ff8aa0" };
function insSkinTex() {
  return canvasTex("ins_skin_v1", 512, 512, (g, w, h) => {
    const b = g.createLinearGradient(0, 0, 0, h);
    b.addColorStop(0, "#f6f2ea"); b.addColorStop(0.6, "#e2ddd2"); b.addColorStop(1, "#c2bcae");
    g.fillStyle = b; g.fillRect(0, 0, w, h);
    const R = mulberry32(4111);
    // soft scale rows — barely there, this thing is nearly vapour
    for (let row = 0; row < 26; row++) {
      const y = row * (h / 26);
      for (let i = 0; i < 30; i++) {
        const x = i * (w / 30) + (row % 2) * (w / 60);
        g.fillStyle = `rgba(255,255,255,${0.10 + R() * 0.10})`;
        g.beginPath(); g.ellipse(x, y, 9, 6, 0, 0, 7); g.fill();
        g.strokeStyle = `rgba(150,142,130,${0.10 + R() * 0.10})`; g.lineWidth = 1;
        g.beginPath(); g.ellipse(x, y, 9, 6, 0, 0, 7); g.stroke();
      }
    }
    for (let i = 0; i < 14; i++) {
      const x = R() * w, y = R() * h, r = 30 + R() * 50;
      const s = g.createRadialGradient(x, y, 1, x, y, r);
      s.addColorStop(0, "rgba(255,214,224,0.20)"); s.addColorStop(1, "rgba(255,214,224,0)");
      g.fillStyle = s; g.beginPath(); g.arc(x, y, r, 0, 7); g.fill();
    }
  });
}
export function InvertedSerpentBody({ pal }) {
  pal = pal || getBeingPalette("inverted_serpent");
  const skin = insSkinTex();
  const eye = eyeDisc("ins_eye_v1", "#ffe8f0", "#ff8aa0", "#2a1018", "rgba(10,6,10,0.9)", false);
  const rig = useBeingRig();
  const armL = useRef(); const armR = useRef();
  const sway = useRef(); const wisps = useRef([]);
  useFrame((s) => {
    const t = s.clock.elapsedTime;
    // it sways off the coil the way a snake actually holds itself
    if (sway.current) {
      sway.current.rotation.z = Math.sin(t * 0.9) * 0.075;
      sway.current.rotation.x = Math.sin(t * 0.62 + 1.1) * 0.045;
    }
    if (armL.current) armL.current.rotation.z = 0.5 + Math.sin(t * 1.4) * 0.22;
    if (armR.current) armR.current.rotation.z = -0.5 - Math.sin(t * 1.4 + 1.6) * 0.22;
    wisps.current.forEach((m, i) => {
      if (!m) return;
      const p = t * 0.5 + i * 2.2;
      m.position.set(Math.cos(p) * 0.34, 0.42 + Math.sin(t * 1.2 + i) * 0.14, Math.sin(p) * 0.34);
      m.material.opacity = 0.26 + Math.sin(t * 1.9 + i) * 0.16;
    });
  });
  const body = { map: skin, roughness: 0.85, emissive: "#ffffff", emissiveIntensity: 0.06 };
  return (
    <group>
      {/* THE COIL it sits on — dark, heavy, and not the same
          material as the creature. It is the ground it made. */}
      {[0, 1, 2].map((i) => (
        <mesh key={"c" + i} position={[0, 0.055 + i * 0.052, 0]} rotation={[-Math.PI / 2, 0, i * 1.1]}>
          <torusGeometry args={[0.30 - i * 0.045, 0.058, 8, 22]} />
          <meshStandardMaterial color={INS.coil} roughness={0.9} />
        </mesh>
      ))}
      <group ref={sway} position={[0, 0.20, 0]}>
        {/* the stacked body — three tapering masses, no legs ever */}
        <mesh position={[0, 0.10, 0]} scale={[1.0, 0.86, 1.0]} castShadow>
          <sphereGeometry args={[0.200, 14, 12]} /><meshStandardMaterial {...body} />
        </mesh>
        <mesh position={[0, 0.30, 0.01]} scale={[0.92, 0.90, 0.92]} castShadow>
          <sphereGeometry args={[0.180, 14, 12]} /><meshStandardMaterial {...body} />
        </mesh>
        {/* the tiny tendril arms, held out — "do not be fooled" */}
        <group ref={armL} position={[-0.155, 0.30, 0.06]}>
          <mesh rotation={[0, 0, 0.5]}><capsuleGeometry args={[0.030, 0.13, 4, 8]} /><meshStandardMaterial {...body} /></mesh>
          <mesh position={[-0.085, -0.055, 0]}><sphereGeometry args={[0.036, 9, 8]} /><meshStandardMaterial {...body} /></mesh>
        </group>
        <group ref={armR} position={[0.155, 0.30, 0.06]}>
          <mesh rotation={[0, 0, -0.5]}><capsuleGeometry args={[0.030, 0.13, 4, 8]} /><meshStandardMaterial {...body} /></mesh>
          <mesh position={[0.085, -0.055, 0]}><sphereGeometry args={[0.036, 9, 8]} /><meshStandardMaterial {...body} /></mesh>
        </group>
        {/* the head: a soft hood, and eyes far too big for it */}
        <group ref={rig.head} position={[0, 0.545, 0.015]}>
          <mesh scale={[1.10, 0.94, 0.98]} castShadow>
            <sphereGeometry args={[0.175, 16, 14]} /><meshStandardMaterial {...body} />
          </mesh>
          <mesh position={[0, 0.055, -0.03]} scale={[1.32, 0.42, 0.90]}>
            <sphereGeometry args={[0.150, 14, 10]} /><meshStandardMaterial {...body} />
          </mesh>
          <mesh position={[0, -0.052, 0.150]} scale={[0.80, 0.60, 0.75]}>
            <sphereGeometry args={[0.085, 12, 10]} /><meshStandardMaterial {...body} />
          </mesh>
          {[-1, 1].map((sd) => (
            <group key={"e" + sd} position={[sd * 0.078, 0.010, 0.132]} rotation={[0, sd * 0.22, 0]}>
              <mesh scale={[1, 1.05, 0.55]}><sphereGeometry args={[0.062, 10, 10]} /><meshStandardMaterial color={INS.eyeD} roughness={0.6} /></mesh>
              <mesh position={[0, 0, 0.036]}><circleGeometry args={[0.050, 20]} /><meshBasicMaterial map={eye} toneMapped={false} /></mesh>
            </group>
          ))}
          {/* the two little cheek blushes the card gives it */}
          {[-1, 1].map((sd) => (
            <mesh key={"b" + sd} position={[sd * 0.128, -0.048, 0.108]} rotation={[0, sd * 0.5, 0]}>
              <circleGeometry args={[0.034, 12]} />
              <meshBasicMaterial color={INS.blush} transparent opacity={0.35} toneMapped={false} depthWrite={false} />
            </mesh>
          ))}
        </group>
      </group>
      {[0, 1, 2].map((i) => (
        <mesh key={"w" + i} ref={(el) => (wisps.current[i] = el)}>
          <sphereGeometry args={[0.022, 6, 6]} />
          <meshBasicMaterial color={pal.glow} transparent opacity={0.28} toneMapped={false} depthWrite={false} />
        </mesh>
      ))}
      <mesh position={[0, 0.45, 0]}>
        <sphereGeometry args={[0.60, 14, 10]} />
        <meshBasicMaterial color={pal.glow} transparent opacity={0.07} side={THREE.BackSide} toneMapped={false} depthWrite={false} />
      </mesh>
    </group>
  );
}

// ── ANTDROID · Robot · Time · Lyf 5 / Lyt 3 ──────────────────
// "The most common robot Beb in the Unstable World, they only
//  seek Anima and will eat without bias. These little droids are
//  responsible for the consumption of 1% of the entire Unstable
//  World."
// THE MACHINE EXCEPTION: slab-built things keep their slabs. The
// card: a round slate-blue droid head with two glossy black eyes,
// two curved antennae tipped hot, and a burning amber sigil where
// its abdomen should be. That sigil is where the world goes.
const ADR = { shell: "#4a5a6a", dark: "#12161c", gold: "#d8b24a", sigil: "#ffb02e", lens: "#0a0c10" };
export function AntdroidBody({ pal }) {
  pal = pal || getBeingPalette("antdroid");
  const plate = plateTex("adr_plate_v1", "#5e7080", "#465666", "#1e2630", "rgba(200,220,240,0.5)");
  const rig = useBeingRig();
  const antL = useRef(); const antR = useRef();
  const sig = useRef(); const legs = useRef([]);
  useFrame((s) => {
    const t = s.clock.elapsedTime;
    if (antL.current) antL.current.rotation.z = 0.42 + Math.sin(t * 2.4) * 0.14;
    if (antR.current) antR.current.rotation.z = -0.42 - Math.sin(t * 2.4 + 1.3) * 0.14;
    // the sigil is a stomach and it is always working
    if (sig.current) {
      const k = 0.55 + Math.abs(Math.sin(t * 1.15)) * 0.45;
      sig.current.material.opacity = k;
      sig.current.scale.setScalar(0.95 + Math.sin(t * 1.15) * 0.09);
    }
    legs.current.forEach((m, i) => {
      if (m) m.rotation.x = Math.sin(t * 5.2 + (i % 2) * Math.PI) * 0.16;
    });
  });
  const shell = { map: plate, metalness: 0.6, roughness: 0.45 };
  const dark = { color: ADR.dark, metalness: 0.5, roughness: 0.6 };
  return (
    <group>
      {/* thorax + abdomen — two slabs, honestly slabs */}
      <mesh position={[0, 0.50, -0.10]} castShadow>
        <boxGeometry args={[0.34, 0.30, 0.34]} /><meshStandardMaterial {...shell} />
      </mesh>
      <mesh position={[0, 0.44, -0.36]} castShadow>
        <boxGeometry args={[0.40, 0.36, 0.30]} /><meshStandardMaterial {...shell} />
      </mesh>
      {/* THE SIGIL — the amber furnace on the abdomen */}
      <mesh position={[0, 0.44, -0.515]}>
        <circleGeometry args={[0.115, 20]} /><meshBasicMaterial color={ADR.gold} toneMapped={false} />
      </mesh>
      <mesh ref={sig} position={[0, 0.44, -0.522]}>
        <circleGeometry args={[0.150, 22]} />
        <meshBasicMaterial color={ADR.sigil} transparent opacity={0.7} toneMapped={false} depthWrite={false} />
      </mesh>
      {[0, 1, 2, 3, 4, 5].map((i) => {
        const a = (i / 6) * Math.PI * 2;
        return (
          <mesh key={"ry" + i} position={[Math.cos(a) * 0.135, 0.44 + Math.sin(a) * 0.135, -0.505]} rotation={[0, 0, a]}>
            <boxGeometry args={[0.055, 0.014, 0.012]} /><meshBasicMaterial color={ADR.sigil} toneMapped={false} />
          </mesh>
        );
      })}
      {/* head: a round shell with two glossy lenses */}
      <group ref={rig.head} position={[0, 0.60, 0.16]}>
        <mesh scale={[1.05, 1.0, 0.95]} castShadow>
          <sphereGeometry args={[0.215, 16, 14]} /><meshStandardMaterial {...shell} />
        </mesh>
        {[-1, 1].map((sd) => (
          <mesh key={"e" + sd} position={[sd * 0.098, 0.020, 0.170]} scale={[1, 1.15, 0.6]}>
            <sphereGeometry args={[0.070, 12, 12]} />
            <meshStandardMaterial color={ADR.lens} metalness={0.35} roughness={0.08} />
          </mesh>
        ))}
        {/* mandibles */}
        {[-1, 1].map((sd) => (
          <mesh key={"m" + sd} position={[sd * 0.055, -0.135, 0.150]} rotation={[1.25, 0, sd * 0.45]}>
            <coneGeometry args={[0.026, 0.115, 6]} /><meshStandardMaterial color={ADR.gold} metalness={0.7} roughness={0.35} />
          </mesh>
        ))}
        {/* THE ANTENNAE — curved, tipped hot, always tasting */}
        <group ref={antL} position={[-0.085, 0.175, 0.03]}>
          <mesh rotation={[0, 0, 0.42]}><capsuleGeometry args={[0.014, 0.24, 3, 6]} /><meshStandardMaterial color={ADR.gold} metalness={0.7} roughness={0.35} /></mesh>
          <mesh position={[-0.115, 0.135, 0]}><sphereGeometry args={[0.034, 10, 8]} /><meshBasicMaterial color={ADR.sigil} toneMapped={false} /></mesh>
        </group>
        <group ref={antR} position={[0.085, 0.175, 0.03]}>
          <mesh rotation={[0, 0, -0.42]}><capsuleGeometry args={[0.014, 0.24, 3, 6]} /><meshStandardMaterial color={ADR.gold} metalness={0.7} roughness={0.35} /></mesh>
          <mesh position={[0.115, 0.135, 0]}><sphereGeometry args={[0.034, 10, 8]} /><meshBasicMaterial color={ADR.sigil} toneMapped={false} /></mesh>
        </group>
      </group>
      {/* six legs, alternating tripods */}
      {[0, 1, 2, 3, 4, 5].map((i) => {
        const sd = i < 3 ? -1 : 1, k = i % 3;
        return (
          <group key={"l" + i} position={[sd * 0.17, 0.46, 0.06 - k * 0.20]} rotation={[0, sd < 0 ? 0.4 : -0.4, 0]}>
            <group ref={(el) => (legs.current[i] = el)}>
              <mesh position={[sd * 0.10, -0.055, 0]} rotation={[0, 0, sd * -0.7]}>
                <capsuleGeometry args={[0.019, 0.16, 3, 6]} /><meshStandardMaterial {...shell} />
              </mesh>
              <mesh position={[sd * 0.20, -0.24, 0]} rotation={[0, 0, sd * 0.35]}>
                <capsuleGeometry args={[0.014, 0.22, 3, 6]} /><meshStandardMaterial {...dark} />
              </mesh>
            </group>
          </group>
        );
      })}
    </group>
  );
}

// ── DG-0 ANGEL · Robot · Time · Lyf 6 / Lyt 2 ────────────────
// "Through tremendous effort, these robots have, over time,
//  healed 1% of dying beings in the Unstable World."
// The card: a verdigris humanoid droid with a smooth dome head
// marked with an X, a red gem at the heart, and big blue-green
// FEATHERED wings. A machine that grew wings to do mercy with.
const DGA = { hull: "#4a9a94", deep: "#16302e", trim: "#d8c07a", gem: "#e8544a", wing: "#7fc8c0", wingHi: "#cfeee8" };
function dgaWingTex() {
  return canvasTex("dga_wing_v1", 256, 256, (g, w, h) => {
    const b = g.createLinearGradient(0, 0, 0, h);
    b.addColorStop(0, "#a8dcd4"); b.addColorStop(0.55, "#6fb8b0"); b.addColorStop(1, "#2e5e5a");
    g.fillStyle = b; g.fillRect(0, 0, w, h);
    const R = mulberry32(1777);
    for (let rank = 0; rank < 4; rank++) {
      const y0 = 26 + rank * 60;
      for (let i = 0; i < 20; i++) {
        const x = 8 + i * 13 + (rank % 2) * 6;
        leaf(g, x, y0 + R() * 26, 68 + R() * 34, 7 + R() * 4, 0.05, "#2c5c58", 0.45);
        leaf(g, x, y0 + R() * 26, 46 + R() * 24, 3 + R() * 2, 0.05, "#cfeee8", 0.30);
      }
    }
  });
}
export function DG0Body({ pal }) {
  pal = pal || getBeingPalette("dg0");
  const plate = plateTex("dga_plate_v1", "#5cb0a8", "#3f8a84", "#16302e", "rgba(220,240,235,0.5)");
  const wingT = dgaWingTex();
  const rig = useBeingRig();
  const wingL = useRef(); const wingR = useRef(); const gem = useRef();
  useFrame((s) => {
    const t = s.clock.elapsedTime;
    // the wings do not flap. They breathe, and they are always open.
    const k = Math.sin(t * 0.7);
    if (wingL.current) { wingL.current.rotation.y = -0.55 + k * 0.10; wingL.current.rotation.z = 0.16 + k * 0.05; }
    if (wingR.current) { wingR.current.rotation.y = 0.55 - k * 0.10; wingR.current.rotation.z = -0.16 - k * 0.05; }
    if (gem.current) gem.current.material.opacity = 0.65 + Math.sin(t * 1.5) * 0.30;
  });
  const hull = { map: plate, metalness: 0.55, roughness: 0.45 };
  const trim = { color: DGA.trim, metalness: 0.75, roughness: 0.3 };
  return (
    <group>
      {[-1, 1].map((sd) => (
        <group key={"l" + sd} ref={sd < 0 ? rig.legL : rig.legR} position={[sd * 0.125, 0, 0]}>
          <mesh position={[0, 0.34, 0]}><capsuleGeometry args={[0.070, 0.30, 4, 10]} /><meshStandardMaterial {...hull} /></mesh>
          <mesh position={[0, 0.06, 0.035]} scale={[1, 0.6, 1.5]}><sphereGeometry args={[0.080, 10, 8]} /><meshStandardMaterial color={DGA.deep} metalness={0.5} roughness={0.6} /></mesh>
        </group>
      ))}
      <mesh position={[0, 0.82, 0]} scale={[1.05, 1.15, 0.82]} castShadow>
        <boxGeometry args={[0.36, 0.42, 0.30]} /><meshStandardMaterial {...hull} />
      </mesh>
      {/* the ring sigil on the chest, and THE GEM at its heart */}
      <mesh position={[0, 0.88, 0.135]} rotation={[0, 0, 0]}>
        <torusGeometry args={[0.105, 0.014, 8, 22]} /><meshStandardMaterial {...trim} />
      </mesh>
      <mesh position={[0, 0.88, 0.145]}><sphereGeometry args={[0.048, 12, 10]} /><meshStandardMaterial color={DGA.gem} emissive={DGA.gem} emissiveIntensity={0.9} roughness={0.25} /></mesh>
      <mesh ref={gem} position={[0, 0.88, 0.145]}><sphereGeometry args={[0.105, 10, 8]} /><meshBasicMaterial color={DGA.gem} transparent opacity={0.7} toneMapped={false} depthWrite={false} /></mesh>
      {/* boxy shoulders and plain arms */}
      {[-1, 1].map((sd) => (
        <group key={"a" + sd} ref={sd < 0 ? rig.armL : rig.armR} position={[sd * 0.255, 1.00, 0]}>
          <mesh scale={[1, 0.72, 1]}><boxGeometry args={[0.16, 0.16, 0.20]} /><meshStandardMaterial {...hull} /></mesh>
          <mesh position={[sd * 0.03, -0.24, 0]}><capsuleGeometry args={[0.058, 0.26, 4, 8]} /><meshStandardMaterial {...hull} /></mesh>
          <mesh position={[sd * 0.05, -0.44, 0.02]}><sphereGeometry args={[0.068, 10, 8]} /><meshStandardMaterial color={DGA.deep} metalness={0.5} roughness={0.6} /></mesh>
        </group>
      ))}
      {/* THE DOME — smooth, and the X that is its whole face */}
      <group ref={rig.head} position={[0, 1.24, 0.01]}>
        <mesh scale={[1.0, 0.95, 1.0]} castShadow>
          <sphereGeometry args={[0.185, 16, 14]} /><meshStandardMaterial map={plate} metalness={0.5} roughness={0.32} />
        </mesh>
        {[-1, 1].map((sd) => (
          <mesh key={"x" + sd} position={[0, 0.035, 0.175]} rotation={[0, 0, sd * 0.78]}>
            <boxGeometry args={[0.115, 0.020, 0.012]} /><meshStandardMaterial color={DGA.deep} roughness={0.6} />
          </mesh>
        ))}
        {[-1, 1].map((sd) => (
          <mesh key={"o" + sd} position={[sd * 0.088, -0.045, 0.158]}>
            <circleGeometry args={[0.030, 14]} />
            <meshBasicMaterial color={DGA.wingHi} transparent opacity={0.75} toneMapped={false} />
          </mesh>
        ))}
        <mesh position={[0, 0.185, 0]}><cylinderGeometry args={[0.035, 0.048, 0.075, 10]} /><meshStandardMaterial {...trim} /></mesh>
      </group>
      {/* THE WINGS — four painted feather panels a side, always open */}
      {[-1, 1].map((sd) => (
        <group key={"w" + sd} ref={sd < 0 ? wingL : wingR} position={[sd * 0.16, 1.02, -0.16]}>
          {[0, 1, 2, 3].map((i) => (
            <mesh key={i} position={[sd * (0.20 + i * 0.16), 0.16 - i * 0.13, -0.03 - i * 0.02]} rotation={[0, sd * -0.25, sd * (0.5 - i * 0.22)]}>
              <planeGeometry args={[0.44 - i * 0.05, 0.30 - i * 0.045]} />
              <meshStandardMaterial map={wingT} roughness={1} side={THREE.DoubleSide} transparent opacity={0.96} />
            </mesh>
          ))}
        </group>
      ))}
    </group>
  );
}

// ── H-01 BLUE GIANT · Humanoid · Astro · Lyf 6 / Lyt 2 ───────
// "A variant of the H-01 ilk, these beings choose diplomacy as
//  their first resort."
// The card: a heavy blue pressure suit, a round helmet with a lit
// visor, a chest panel with one orange light, standing still on a
// ridge. Nothing on it is a weapon. That is the character.
const H01 = { suit: "#2e6f9e", deep: "#12293c", trim: "#4ac8ff", warm: "#ff8a3a", glass: "#7fd8ff" };
function suitTex(key, hi, mid, lo, stripe) {
  return canvasTex(key, 256, 256, (g, w, h) => {
    const b = g.createLinearGradient(0, 0, 0, h);
    b.addColorStop(0, hi); b.addColorStop(0.55, mid); b.addColorStop(1, lo);
    g.fillStyle = b; g.fillRect(0, 0, w, h);
    const R = mulberry32(hashId(key) + 3);
    // pressure-suit ribbing: horizontal quilted bands
    for (let i = 0; i < 20; i++) {
      const y = i * (h / 20);
      g.fillStyle = "rgba(0,0,0,0.18)"; g.fillRect(0, y, w, 3);
      g.fillStyle = "rgba(255,255,255,0.09)"; g.fillRect(0, y + 3, w, 2);
    }
    for (let i = 0; i < 500; i++) {
      g.fillStyle = `rgba(${R() > 0.5 ? "255,255,255" : "0,0,0"},${0.02 + R() * 0.04})`;
      g.fillRect(R() * w, R() * h, 2 + R() * 5, 1 + R() * 2);
    }
    // the trim stripe running the limb
    g.fillStyle = stripe; g.globalAlpha = 0.75;
    g.fillRect(w * 0.44, 0, 10, h); g.globalAlpha = 1;
  });
}
export function H01Body({ pal }) {
  pal = pal || getBeingPalette("h01");
  const suit = suitTex("h01_suit_v1", "#3f88bb", "#2b6693", "#12293c", "#4ac8ff");
  const rig = useBeingRig();
  const visor = useRef(); const panel = useRef();
  useFrame((s) => {
    const t = s.clock.elapsedTime;
    if (visor.current) visor.current.material.opacity = 0.55 + Math.sin(t * 0.8) * 0.14;
    // one orange life-sign on the chest, ticking steadily
    if (panel.current) panel.current.material.opacity = 0.4 + Math.abs(Math.sin(t * 1.6)) * 0.6;
  });
  const cloth = { map: suit, roughness: 0.85 };
  const dark = { color: H01.deep, roughness: 0.8 };
  return (
    <group>
      {[-1, 1].map((sd) => (
        <group key={"l" + sd} ref={sd < 0 ? rig.legL : rig.legR} position={[sd * 0.135, 0, 0]}>
          <mesh position={[0, 0.36, 0]}><capsuleGeometry args={[0.088, 0.32, 4, 10]} /><meshStandardMaterial {...cloth} /></mesh>
          <mesh position={[0, 0.085, 0.045]} scale={[1.1, 0.7, 1.5]}><sphereGeometry args={[0.095, 10, 8]} /><meshStandardMaterial {...dark} /></mesh>
          <mesh position={[0, 0.20, 0]}><torusGeometry args={[0.092, 0.020, 6, 14]} /><meshStandardMaterial color={H01.trim} metalness={0.4} roughness={0.5} /></mesh>
        </group>
      ))}
      <mesh position={[0, 0.86, 0]} scale={[1.12, 1.12, 0.88]} castShadow>
        <capsuleGeometry args={[0.215, 0.24, 5, 12]} /><meshStandardMaterial {...cloth} />
      </mesh>
      {/* the chest panel — readouts, and one warm light */}
      <mesh position={[0, 0.90, 0.185]} rotation={[0.04, 0, 0]}>
        <boxGeometry args={[0.20, 0.15, 0.035]} /><meshStandardMaterial {...dark} />
      </mesh>
      <mesh ref={panel} position={[0, 0.925, 0.206]}>
        <circleGeometry args={[0.030, 14]} /><meshBasicMaterial color={H01.warm} transparent opacity={0.9} toneMapped={false} />
      </mesh>
      {[-1, 1].map((sd) => (
        <mesh key={"d" + sd} position={[sd * 0.055, 0.862, 0.206]}>
          <boxGeometry args={[0.055, 0.012, 0.004]} /><meshBasicMaterial color={H01.trim} toneMapped={false} />
        </mesh>
      ))}
      {/* the life-support pack */}
      <mesh position={[0, 0.92, -0.20]} castShadow>
        <boxGeometry args={[0.30, 0.34, 0.14]} /><meshStandardMaterial {...dark} />
      </mesh>
      {[-1, 1].map((sd) => (
        <group key={"a" + sd} ref={sd < 0 ? rig.armL : rig.armR} position={[sd * 0.275, 1.00, 0]}>
          <mesh scale={[1, 0.9, 1]}><sphereGeometry args={[0.105, 12, 10]} /><meshStandardMaterial {...cloth} /></mesh>
          <mesh position={[sd * 0.035, -0.26, 0.01]} rotation={[0, 0, sd * -0.10]}>
            <capsuleGeometry args={[0.072, 0.28, 4, 10]} /><meshStandardMaterial {...cloth} />
          </mesh>
          {/* open, empty hands — the diplomat carries nothing */}
          <mesh position={[sd * 0.062, -0.475, 0.045]}><sphereGeometry args={[0.080, 10, 8]} /><meshStandardMaterial {...dark} /></mesh>
        </group>
      ))}
      {/* THE HELMET — a lit visor and a wide collar ring */}
      <group ref={rig.head} position={[0, 1.30, 0.01]}>
        <mesh castShadow><sphereGeometry args={[0.205, 16, 14]} /><meshStandardMaterial {...cloth} /></mesh>
        <mesh position={[0, 0.005, 0.075]} scale={[1, 0.92, 1]}>
          <sphereGeometry args={[0.175, 16, 14, 0, Math.PI * 2, 0, Math.PI / 1.9]} />
          <meshStandardMaterial color="#0b1a26" metalness={0.4} roughness={0.12} />
        </mesh>
        <mesh ref={visor} position={[0, 0.005, 0.145]} rotation={[0.1, 0, 0]}>
          <circleGeometry args={[0.135, 20]} />
          <meshBasicMaterial color={H01.glass} transparent opacity={0.6} toneMapped={false} depthWrite={false} />
        </mesh>
        <mesh position={[0, -0.185, 0]}><torusGeometry args={[0.150, 0.030, 8, 18]} /><meshStandardMaterial color={H01.trim} metalness={0.5} roughness={0.45} /></mesh>
        <mesh position={[0, 0.175, -0.02]}><cylinderGeometry args={[0.030, 0.042, 0.085, 8]} /><meshStandardMaterial color={H01.trim} metalness={0.6} roughness={0.4} /></mesh>
      </group>
    </group>
  );
}

// ── H-09 NEUTRON STAR · Humanoid · Space · Lyf 3 / Lyt 5 ─────
// "A variant of the H-01 ilk, these beings tend to choose
//  militancy as their first option."
// SAME LINEAGE, OPPOSITE ANSWER — and the model has to say so.
// The card gives it a white suit lined in hot orange, a GOLD
// HALO behind the helmet, and hands that are not open. Where
// H-01 has a chest readout, this has a halo; where H-01 carries
// nothing, this is squared up. Two Lyt higher, three Lyf lower.
const H09 = { suit: "#f0ece0", deep: "#3a332a", line: "#ff7a2e", halo: "#ffb04a", glass: "#1a2a3a" };
export function H09Body({ pal }) {
  pal = pal || getBeingPalette("h09");
  const suit = suitTex("h09_suit_v1", "#fbf8f0", "#e6e0d2", "#9c948a", "#ff7a2e");
  const rig = useBeingRig();
  const halo = useRef(); const visor = useRef();
  useFrame((s) => {
    const t = s.clock.elapsedTime;
    if (halo.current) {
      halo.current.rotation.z = t * 0.16;
      halo.current.material.opacity = 0.34 + Math.sin(t * 0.9) * 0.12;
    }
    if (visor.current) visor.current.material.opacity = 0.45 + Math.sin(t * 1.7) * 0.16;
  });
  const cloth = { map: suit, roughness: 0.9 };
  const dark = { color: H09.deep, roughness: 0.85 };
  const line = { color: H09.line, emissive: H09.line, emissiveIntensity: 0.5, roughness: 0.6 };
  return (
    <group>
      {[-1, 1].map((sd) => (
        <group key={"l" + sd} ref={sd < 0 ? rig.legL : rig.legR} position={[sd * 0.135, 0, 0]}>
          <mesh position={[0, 0.36, 0]}><capsuleGeometry args={[0.088, 0.32, 4, 10]} /><meshStandardMaterial {...cloth} /></mesh>
          <mesh position={[0, 0.085, 0.045]} scale={[1.1, 0.7, 1.5]}><sphereGeometry args={[0.095, 10, 8]} /><meshStandardMaterial {...dark} /></mesh>
          <mesh position={[sd * 0.088, 0.38, 0]}><boxGeometry args={[0.012, 0.34, 0.06]} /><meshStandardMaterial {...line} /></mesh>
        </group>
      ))}
      <mesh position={[0, 0.86, 0]} scale={[1.10, 1.12, 0.86]} castShadow>
        <capsuleGeometry args={[0.215, 0.24, 5, 12]} /><meshStandardMaterial {...cloth} />
      </mesh>
      {/* the orange lines the card runs down the chest */}
      {[-1, 0, 1].map((k) => (
        <mesh key={"s" + k} position={[k * 0.075, 0.88, 0.190]} rotation={[0.05, 0, 0]}>
          <boxGeometry args={[0.020, 0.30, 0.010]} /><meshStandardMaterial {...line} />
        </mesh>
      ))}
      <mesh position={[0, 0.92, -0.20]} castShadow>
        <boxGeometry args={[0.30, 0.34, 0.14]} /><meshStandardMaterial {...dark} />
      </mesh>
      {/* arms squared, hands CLOSED — the militant answer */}
      {[-1, 1].map((sd) => (
        <group key={"a" + sd} ref={sd < 0 ? rig.armL : rig.armR} position={[sd * 0.275, 1.00, 0]}>
          <mesh scale={[1, 0.9, 1]}><sphereGeometry args={[0.105, 12, 10]} /><meshStandardMaterial {...cloth} /></mesh>
          <mesh position={[sd * 0.02, -0.22, 0.06]} rotation={[0.5, 0, sd * -0.06]}>
            <capsuleGeometry args={[0.072, 0.26, 4, 10]} /><meshStandardMaterial {...cloth} />
          </mesh>
          <mesh position={[sd * 0.045, -0.40, 0.215]} scale={[1, 0.9, 1.05]}>
            <boxGeometry args={[0.115, 0.115, 0.115]} /><meshStandardMaterial {...dark} />
          </mesh>
          <mesh position={[sd * 0.048, -0.30, 0.145]}><boxGeometry args={[0.020, 0.16, 0.012]} /><meshStandardMaterial {...line} /></mesh>
        </group>
      ))}
      <group ref={rig.head} position={[0, 1.30, 0.01]}>
        {/* THE HALO — behind the helmet, turning. H-01 has a
            readout where this has a crown. */}
        <mesh ref={halo} position={[0, 0.03, -0.16]}>
          <ringGeometry args={[0.235, 0.310, 40]} />
          <meshBasicMaterial color={H09.halo} transparent opacity={0.4} side={THREE.DoubleSide} toneMapped={false} depthWrite={false} />
        </mesh>
        <mesh position={[0, 0.03, -0.155]}>
          <ringGeometry args={[0.238, 0.258, 40]} />
          <meshBasicMaterial color="#fff0c8" transparent opacity={0.75} side={THREE.DoubleSide} toneMapped={false} />
        </mesh>
        <mesh castShadow><sphereGeometry args={[0.205, 16, 14]} /><meshStandardMaterial {...cloth} /></mesh>
        <mesh position={[0, 0.005, 0.075]} scale={[1, 0.92, 1]}>
          <sphereGeometry args={[0.175, 16, 14, 0, Math.PI * 2, 0, Math.PI / 1.9]} />
          <meshStandardMaterial color={H09.glass} metalness={0.45} roughness={0.1} />
        </mesh>
        <mesh ref={visor} position={[0, 0.005, 0.145]} rotation={[0.1, 0, 0]}>
          <circleGeometry args={[0.130, 20]} />
          <meshBasicMaterial color="#8ac8ff" transparent opacity={0.5} toneMapped={false} depthWrite={false} />
        </mesh>
        <mesh position={[0, -0.185, 0]}><torusGeometry args={[0.150, 0.030, 8, 18]} /><meshStandardMaterial {...line} /></mesh>
      </group>
    </group>
  );
}

// ── LUCKY DRAGON · Dragon · Dragon · Lyf 1 / Lyt 7 ───────────
// "Rarely seen, these Bebs are born in the Unstable World and
//  have a fondness for Shade."
// Lyf ONE. Seven Lyt. The purest glass cannon in the roster and
// the model should look like it cannot afford to be touched: no
// bulk anywhere, and everything spent on the HORNS — two vast
// golden sweeps, with a flame crest burning between them.
// THE LUCKY DRAGON GREEN LADDER (caP'n, 3D-28): the Lord's green is
// the base of the lineage and the tier lightens toward the Beb —
// so the ladder reads as a colour as well as a silhouette. And the
// wings are PURPLE at Beb and Champion, which is the lineage's own
// hue asserting itself before the green settles into it.
const LKD_GREEN = {
  lord: "#4e7a72",       // the base — darkest, unchanged
  champion: "#71a396",   // half a step lighter than the Lord
  beb: "#96c7b4",        // lighter again: light, not washed out
};
const LKD_WING = { membrane: "#7c4bb8", bone: "#4a2a6e", glow: "#c9a0ff" };
const LKD = { face: "#9a6aa8", deep: "#3a1f42", horn: "#ffb02e", hornHi: "#ffe08a", frill: "#7c4bb8", gem: "#ffd66a" };
function lkdScaleTex() {
  return canvasTex("lkd_scale_v1", 512, 512, (g, w, h) => {
    const b = g.createLinearGradient(0, 0, 0, h);
    b.addColorStop(0, "#b184bd"); b.addColorStop(0.55, "#8a5c98"); b.addColorStop(1, "#3f2247");
    g.fillStyle = b; g.fillRect(0, 0, w, h);
    const R = mulberry32(8837);
    for (let row = 0; row < 24; row++) {
      const y = row * (h / 24);
      for (let i = 0; i < 24; i++) {
        const x = i * (w / 24) + (row % 2) * (w / 48);
        g.fillStyle = `rgba(255,225,255,${0.05 + R() * 0.10})`;
        g.beginPath(); g.ellipse(x, y, 11, 8, 0, 0, 7); g.fill();
        g.strokeStyle = `rgba(40,20,48,${0.20 + R() * 0.16})`; g.lineWidth = 1.2;
        g.beginPath(); g.ellipse(x, y, 11, 8, 0, 0, 7); g.stroke();
      }
    }
    for (let i = 0; i < 12; i++) leaf(g, R() * w, R() * h, 80 + R() * 110, 8 + R() * 7, (R() - .5) * 1.2, "#ffb02e", 0.14);
  });
}
const LKD_HORN = (() => {
  const out = [];
  for (let i = 0; i <= 6; i++) {
    const f = i / 6;
    out.push([
      0.10 + Math.sin(f * Math.PI * 0.62) * 0.44,
      0.10 + f * 0.66,
      -0.02 - f * 0.14,
      0.058 * (1 - f * 0.78),
    ]);
  }
  return out;
})();
export function LuckyDragonBody({ pal }) {
  pal = pal || getBeingPalette("lucky_dragon");
  const scale = lkdScaleTex();
  const eye = eyeDisc("lkd_eye_v1", "#fff2d0", "#ffb02e", "#5a2a08", "rgba(12,6,14,0.92)", true);
  const rig = useBeingRig();
  const crest = useRef([]); const frill = useRef();
  useFrame((s) => {
    const t = s.clock.elapsedTime;
    crest.current.forEach((m, i) => {
      if (!m) return;
      const k = 1 + Math.sin(t * 3.1 + i * 0.9) * 0.22;
      m.scale.set(1, k, 1);
      m.material.opacity = 0.65 + Math.sin(t * 2.4 + i * 1.1) * 0.30;
    });
    if (frill.current) frill.current.rotation.z = Math.sin(t * 0.6) * 0.05;
  });
  const hide = { map: scale, color: LKD_GREEN.beb, roughness: 0.8 };
  const horn = { color: LKD.horn, emissive: LKD.horn, emissiveIntensity: 0.30, roughness: 0.35, metalness: 0.2 };
  return (
    <group>
      {/* the teal frill fan behind — the card's backdrop, made real */}
      <group ref={frill} position={[0, 0.72, -0.24]}>
        {[-2, -1, 0, 1, 2].map((k) => (
          <mesh key={"f" + k} position={[k * 0.13, 0.10 + (2 - Math.abs(k)) * 0.09, 0]} rotation={[0, 0, -k * 0.28]}>
            <planeGeometry args={[0.16, 0.62 - Math.abs(k) * 0.08]} />
            <meshStandardMaterial color={LKD.frill} roughness={1} side={THREE.DoubleSide} transparent opacity={0.9} />
          </mesh>
        ))}
      </group>
      {/* a body with nothing spare on it — Lyf 1 */}
      <mesh position={[0, 0.42, -0.06]} scale={[0.80, 0.78, 1.15]} castShadow>
        <sphereGeometry args={[0.185, 14, 12]} /><meshStandardMaterial {...hide} />
      </mesh>
      {[-1, 1].map((sd) => (
        <group key={"l" + sd} ref={sd < 0 ? rig.legL : rig.legR} position={[sd * 0.115, 0, -0.02]}>
          <mesh position={[0, 0.24, 0]}><capsuleGeometry args={[0.048, 0.20, 4, 8]} /><meshStandardMaterial {...hide} /></mesh>
          <mesh position={[0, 0.055, 0.038]} scale={[1, 0.62, 1.4]}><sphereGeometry args={[0.055, 10, 8]} /><meshStandardMaterial color={LKD.deep} roughness={0.85} /></mesh>
        </group>
      ))}
      {[-1, 1].map((sd) => (
        <mesh key={"a" + sd} ref={sd < 0 ? rig.armL : rig.armR} position={[sd * 0.175, 0.46, 0.10]} rotation={[0.3, 0, sd * 0.4]}>
          <capsuleGeometry args={[0.036, 0.17, 4, 8]} /><meshStandardMaterial {...hide} />
        </mesh>
      ))}
      {/* the head is most of the animal */}
      <group ref={rig.head} position={[0, 0.72, 0.10]}>
        <mesh scale={[1.10, 0.94, 1.05]} castShadow>
          <sphereGeometry args={[0.200, 16, 14]} /><meshStandardMaterial {...hide} />
        </mesh>
        <mesh position={[0, -0.070, 0.170]} scale={[1.02, 0.72, 1.20]}>
          <sphereGeometry args={[0.125, 12, 10]} /><meshStandardMaterial {...hide} />
        </mesh>
        {[-1, 1].map((sd) => (
          <mesh key={"n" + sd} position={[sd * 0.042, -0.052, 0.270]}>
            <sphereGeometry args={[0.020, 8, 8]} /><meshStandardMaterial color={LKD.deep} roughness={0.6} />
          </mesh>
        ))}
        {[-1, 1].map((sd) => (
          <group key={"e" + sd} position={[sd * 0.108, 0.020, 0.140]} rotation={[0, sd * 0.40, 0]}>
            <mesh scale={[1, 1, 0.5]}><sphereGeometry args={[0.058, 10, 8]} /><meshStandardMaterial color="#100810" roughness={1} /></mesh>
            <mesh position={[0, 0, 0.032]}><circleGeometry args={[0.046, 20]} /><meshBasicMaterial map={eye} toneMapped={false} /></mesh>
            <mesh position={[0, 0, 0.030]}><ringGeometry args={[0.047, 0.058, 18]} /><meshBasicMaterial color={LKD.horn} transparent opacity={0.6} toneMapped={false} /></mesh>
          </group>
        ))}
        {/* the brow gem */}
        <mesh position={[0, 0.128, 0.150]}><octahedronGeometry args={[0.032, 0]} /><meshStandardMaterial color={LKD.gem} emissive={LKD.gem} emissiveIntensity={0.8} roughness={0.25} /></mesh>
        {/* THE HORNS — everything this creature has, it spent here */}
        {[-1, 1].map((sd) => (
          <group key={"h" + sd} position={[sd * 0.095, 0.105, -0.02]} scale={[sd, 1, 1]}>
            {LKD_HORN.map(([x, y, z, r], i) => (
              <mesh key={i} position={[x, y, z]}>
                <sphereGeometry args={[r, 9, 8]} />
                <meshStandardMaterial {...horn} color={i > 3 ? LKD.hornHi : LKD.horn} />
              </mesh>
            ))}
          </group>
        ))}
        {/* the flame crest burning between them */}
        {[-1, 0, 1].map((k, i) => (
          <mesh key={"c" + i} ref={(el) => (crest.current[i] = el)} position={[k * 0.062, 0.30 - Math.abs(k) * 0.06, -0.03]}>
            <coneGeometry args={[0.045, 0.30 - Math.abs(k) * 0.07, 6]} />
            <meshBasicMaterial color={i === 1 ? "#fff0b8" : LKD.horn} transparent opacity={0.8} toneMapped={false} depthWrite={false} />
          </mesh>
        ))}
      </group>
      <mesh position={[0, 0.6, 0]}>
        <sphereGeometry args={[0.72, 14, 10]} />
        <meshBasicMaterial color={pal.glow} transparent opacity={0.07} side={THREE.BackSide} toneMapped={false} depthWrite={false} />
      </mesh>
    </group>
  );
}

// ── PREMIER DRAGON · Dragon · Dragon · Lyf 4 / Lyt 4 ─────────
// "The First & Only Leader of the Unstable World. When born, the
//  surrounding area is briefly affected by Bloom, which can
//  awaken the power of Nihil within a sentient being."
// The card: a blazing golden SUNBURST of spines around the head,
// teal-blue eyes, a rose-mauve face, ornate blue-green frills.
// It is the only being in the roster whose signature is that it
// is CROWNED — and the Bloom is the second one: petals that open
// and close around it, because the world reacts to it being here.
// The Premier Dragon's hide is ONE colour across the lineage — the
// Lord's — because caP'n reads the three tiers as one being ageing,
// not three palettes. The Champion keeps its own green (its card is
// genuinely green) and is the deliberate exception.
const PRD_HIDE = "#6a5a72";
const PRD = { face: "#b06a8a", deep: "#3a1830", crown: "#ffd24a", crownHi: "#fff2b8", frill: "#4ad8d0", bloom: "#ff9ec0" };
function prdScaleTex() {
  return canvasTex("prd_scale_v1", 512, 512, (g, w, h) => {
    const b = g.createLinearGradient(0, 0, 0, h);
    b.addColorStop(0, "#c98aa4"); b.addColorStop(0.55, "#a45f80"); b.addColorStop(1, "#3f1a34");
    g.fillStyle = b; g.fillRect(0, 0, w, h);
    const R = mulberry32(6199);
    for (let row = 0; row < 22; row++) {
      const y = row * (h / 22);
      for (let i = 0; i < 22; i++) {
        const x = i * (w / 22) + (row % 2) * (w / 44);
        g.fillStyle = `rgba(255,235,245,${0.06 + R() * 0.10})`;
        g.beginPath(); g.ellipse(x, y, 12, 9, 0, 0, 7); g.fill();
        g.strokeStyle = `rgba(48,18,40,${0.20 + R() * 0.16})`; g.lineWidth = 1.2;
        g.beginPath(); g.ellipse(x, y, 12, 9, 0, 0, 7); g.stroke();
      }
    }
    // gold filigree — it was born decorated
    for (let i = 0; i < 22; i++) leaf(g, R() * w, R() * h, 70 + R() * 90, 5 + R() * 5, (R() - .5) * 1.6, "#ffd24a", 0.16);
  });
}
export function PremierDragonBody({ pal }) {
  pal = pal || getBeingPalette("premier_dragon");
  const scale = prdScaleTex();
  const eye = eyeDisc("prd_eye_v1", "#eafffd", "#4ad8d0", "#0e3a3a", "rgba(6,16,18,0.9)", true);
  const rig = useBeingRig();
  const crown = useRef([]); const petals = useRef([]);
  useFrame((s) => {
    const t = s.clock.elapsedTime;
    // the crown does not flicker like flame — it PULSES like a sun
    const k = 0.72 + Math.sin(t * 0.85) * 0.28;
    crown.current.forEach((m, i) => {
      if (!m) return;
      m.material.opacity = k * (0.7 + ((i * 5) % 4) * 0.1);
      m.scale.setScalar(0.96 + Math.sin(t * 0.85 + i * 0.4) * 0.07);
    });
    // THE BLOOM — petals opening and closing around it, always
    petals.current.forEach((m, i) => {
      if (!m) return;
      const p = (Math.sin(t * 0.5 + i * 1.05) + 1) / 2;
      m.scale.setScalar(0.55 + p * 0.75);
      m.material.opacity = 0.16 + p * 0.32;
      m.rotation.z += 0.0016;
    });
  });
  // caP'n (3D-28): the Beb takes the Lord's colour, so the lineage
  // reads as one being at three ages rather than three palettes.
  const hide = { map: scale, color: PRD_HIDE, roughness: 0.78 };
  return (
    <group>
      {/* the ornate frill fan */}
      {[-1, 1].map((sd) => (
        <group key={"fr" + sd} position={[sd * 0.20, 0.62, -0.22]}>
          {[0, 1, 2].map((i) => (
            <mesh key={i} position={[sd * i * 0.12, 0.16 - i * 0.06, -i * 0.03]} rotation={[0, sd * -0.3, sd * (0.3 + i * 0.25)]}>
              <planeGeometry args={[0.30 - i * 0.05, 0.44 - i * 0.07]} />
              <meshStandardMaterial color={PRD.frill} roughness={1} side={THREE.DoubleSide} transparent opacity={0.85} />
            </mesh>
          ))}
        </group>
      ))}
      <mesh position={[0, 0.44, -0.05]} scale={[0.92, 0.86, 1.20]} castShadow>
        <sphereGeometry args={[0.205, 14, 12]} /><meshStandardMaterial {...hide} />
      </mesh>
      {[-1, 1].map((sd) => (
        <group key={"l" + sd} ref={sd < 0 ? rig.legL : rig.legR} position={[sd * 0.125, 0, -0.02]}>
          <mesh position={[0, 0.25, 0]}><capsuleGeometry args={[0.055, 0.20, 4, 8]} /><meshStandardMaterial {...hide} /></mesh>
          <mesh position={[0, 0.06, 0.040]} scale={[1, 0.62, 1.4]}><sphereGeometry args={[0.062, 10, 8]} /><meshStandardMaterial color={PRD.deep} roughness={0.85} /></mesh>
        </group>
      ))}
      {[-1, 1].map((sd) => (
        <mesh key={"a" + sd} ref={sd < 0 ? rig.armL : rig.armR} position={[sd * 0.185, 0.48, 0.10]} rotation={[0.28, 0, sd * 0.38]}>
          <capsuleGeometry args={[0.040, 0.18, 4, 8]} /><meshStandardMaterial {...hide} />
        </mesh>
      ))}
      <group ref={rig.head} position={[0, 0.76, 0.10]}>
        <mesh scale={[1.08, 0.96, 1.05]} castShadow>
          <sphereGeometry args={[0.200, 16, 14]} /><meshStandardMaterial {...hide} />
        </mesh>
        <mesh position={[0, -0.068, 0.168]} scale={[1.0, 0.74, 1.18]}>
          <sphereGeometry args={[0.122, 12, 10]} /><meshStandardMaterial {...hide} />
        </mesh>
        {[-1, 1].map((sd) => (
          <group key={"e" + sd} position={[sd * 0.106, 0.024, 0.140]} rotation={[0, sd * 0.38, 0]}>
            <mesh scale={[1, 1, 0.5]}><sphereGeometry args={[0.058, 10, 8]} /><meshStandardMaterial color="#0c1414" roughness={1} /></mesh>
            <mesh position={[0, 0, 0.032]}><circleGeometry args={[0.046, 20]} /><meshBasicMaterial map={eye} toneMapped={false} /></mesh>
          </group>
        ))}
        <mesh position={[0, 0.130, 0.148]} rotation={[0.2, 0, 0]}>
          <torusGeometry args={[0.042, 0.013, 6, 14]} /><meshStandardMaterial color={PRD.crown} metalness={0.75} roughness={0.28} />
        </mesh>
        {/* THE CROWN — sixteen spines in a sunburst. This is the
            only being in the roster whose signature is being
            crowned, and it does not flicker; it pulses. */}
        {Array.from({ length: 16 }, (_, i) => {
          const a = (i / 16) * Math.PI * 2;
          const len = 0.26 + (i % 3) * 0.10;
          return (
            <mesh
              key={"cr" + i}
              ref={(el) => (crown.current[i] = el)}
              position={[Math.cos(a) * 0.215, 0.045 + Math.sin(a) * 0.215, -0.05]}
              rotation={[0, 0, a - Math.PI / 2]}
            >
              <coneGeometry args={[0.042, len, 4]} />
              <meshBasicMaterial color={i % 4 === 0 ? PRD.crownHi : PRD.crown} transparent opacity={0.8} toneMapped={false} depthWrite={false} />
            </mesh>
          );
        })}
      </group>
      {/* THE BLOOM — the world reacting to it being here at all */}
      {Array.from({ length: 6 }, (_, i) => {
        const a = (i / 6) * Math.PI * 2;
        return (
          <mesh key={"bl" + i} ref={(el) => (petals.current[i] = el)}
            position={[Math.cos(a) * 0.62, 0.10, Math.sin(a) * 0.62]}
            rotation={[-Math.PI / 2, 0, a]}>
            <circleGeometry args={[0.24, 14]} />
            <meshBasicMaterial color={PRD.bloom} transparent opacity={0.24} side={THREE.DoubleSide} toneMapped={false} depthWrite={false} />
          </mesh>
        );
      })}
      <mesh position={[0, 0.6, 0]}>
        <sphereGeometry args={[0.76, 14, 10]} />
        <meshBasicMaterial color={pal.glow} transparent opacity={0.08} side={THREE.BackSide} toneMapped={false} depthWrite={false} />
      </mesh>
    </group>
  );
}

// ════════════════════════════════════════════════════════════
//  THE OTHER EIGHT — CHAMPIONS (Batch 3D-24)
//  ------------------------------------------------------------
//  Every tier is its own card, so every tier is its own
//  translation — never a scaled Beb. And the Champion cards make
//  that unmistakable: four of these eight STAND UP. The Shadow
//  Fox stops being a pup and becomes a robed sorcerer; the
//  Antdroid stops crawling and takes a fan-halo; DG-0 becomes a
//  Farmer in vestments; the Serpent stops being cute and opens
//  its mouth. Ascension in this game is not "bigger", it is
//  "upright, dressed, and no longer safe".
// ════════════════════════════════════════════════════════════

// gold — worn by four of the eight Champions, one cached texture
function goldTex() {
  return canvasTex("ie_gold_v1", 256, 256, (g, w, h) => {
    const b = g.createLinearGradient(0, 0, w, h);
    b.addColorStop(0, "#ffe9a8"); b.addColorStop(0.42, "#e8b24a");
    b.addColorStop(0.7, "#a8761c"); b.addColorStop(1, "#ffd98a");
    g.fillStyle = b; g.fillRect(0, 0, w, h);
    const R = mulberry32(3931);
    for (let i = 0; i < 260; i++) {
      g.fillStyle = `rgba(${R() > 0.5 ? "255,246,214" : "80,52,8"},${0.05 + R() * 0.12})`;
      g.fillRect(R() * w, R() * h, 2 + R() * 9, 1 + R() * 2);
    }
  });
}
// heavy robe cloth — the Champions' common language
function robeCloth(key, hi, mid, lo, trim) {
  return canvasTex(key, 256, 256, (g, w, h) => {
    const b = g.createLinearGradient(0, 0, 0, h);
    b.addColorStop(0, hi); b.addColorStop(0.55, mid); b.addColorStop(1, lo);
    g.fillStyle = b; g.fillRect(0, 0, w, h);
    const R = mulberry32(hashId(key) + 13);
    for (let i = 0; i < 600; i++) {
      g.fillStyle = `rgba(${R() > 0.5 ? "255,255,255" : "0,0,0"},${0.02 + R() * 0.05})`;
      g.fillRect(R() * w, R() * h, 2 + R() * 6, 2 + R() * 5);
    }
    for (let i = 0; i < 9; i++) {
      const x = (i + 0.5) * (w / 9) + (R() - 0.5) * 8;
      const fg = g.createLinearGradient(x - 8, 0, x + 8, 0);
      fg.addColorStop(0, "rgba(0,0,0,0)"); fg.addColorStop(0.5, "rgba(0,0,0,0.34)"); fg.addColorStop(1, "rgba(0,0,0,0)");
      g.fillStyle = fg; g.fillRect(x - 8, 0, 16, h);
    }
    if (trim) {
      for (let i = 0; i < 30; i++) leaf(g, R() * w, R() * h, 40 + R() * 70, 3 + R() * 4, (R() - .5) * 2, trim, 0.14);
    }
  });
}

// ── SHADOW FOX CHAMPION · Lyf 6 / Lyt 3 ──────────────────────
// "Skulking the Shadow Forest, these fox prey on those who seek it."
// The pup STOOD UP. Same star-flecked pelt, same lit ears — but
// now on a robed body with a gold-trimmed collar and a pendant,
// and the read flips from "mischievous" to "waiting for you".
export function ShadowFoxChampionBody({ pal }) {
  pal = pal || getBeingPalette("shadow_fox");
  const coat = sfxCoatTex();
  const robe = robeCloth("sfxc_robe_v1", "#26222e", "#16141c", "#08080c", "#ffb26a");
  const gold = goldTex();
  const eye = eyeDisc("sfxc_eye_v1", "#fff4d8", "#ffb02e", "#5a2a06", "rgba(10,6,4,0.9)", true);
  const rig = useBeingRig();
  const earL = useRef(); const earR = useRef(); const pend = useRef();
  useFrame((s) => {
    const t = s.clock.elapsedTime;
    if (earL.current) earL.current.rotation.z = 0.20 + Math.sin(t * 1.4) * 0.06;
    if (earR.current) earR.current.rotation.z = -0.20 - Math.sin(t * 1.4 + 1.7) * 0.06;
    if (pend.current) {
      pend.current.rotation.z = Math.sin(t * 1.1) * 0.10;
      pend.current.rotation.x = Math.sin(t * 0.8 + 0.6) * 0.06;
    }
  });
  const cloth = { map: robe, roughness: 1 };
  const fur = { map: coat, roughness: 1, emissive: SFX.fleck, emissiveIntensity: 0.05 };
  const trim = { map: gold, metalness: 0.7, roughness: 0.3 };
  return (
    <group>
      {/* the robe — a cone to the floor, it does not show its feet */}
      <mesh position={[0, 0.56, 0]} castShadow>
        <coneGeometry args={[0.42, 1.14, 14]} /><meshStandardMaterial {...cloth} />
      </mesh>
      <mesh position={[0, 1.06, 0]} scale={[1.0, 0.96, 0.86]} castShadow>
        <sphereGeometry args={[0.285, 14, 12]} /><meshStandardMaterial {...cloth} />
      </mesh>
      {/* sleeves, and hands folded in front */}
      {[-1, 1].map((sd) => (
        <group key={"a" + sd} ref={sd < 0 ? rig.armL : rig.armR} position={[sd * 0.28, 1.02, 0.04]}>
          <mesh rotation={[0.35, 0, sd * 0.35]}><capsuleGeometry args={[0.078, 0.30, 4, 10]} /><meshStandardMaterial {...cloth} /></mesh>
          <mesh position={[sd * -0.10, -0.24, 0.16]}><sphereGeometry args={[0.066, 10, 8]} /><meshStandardMaterial {...fur} /></mesh>
        </group>
      ))}
      {/* THE COLLAR — high, gold-edged, the whole rank statement */}
      <mesh position={[0, 1.24, 0]} rotation={[0, 0, 0]}>
        <cylinderGeometry args={[0.235, 0.30, 0.28, 14, 1, true]} />
        <meshStandardMaterial {...cloth} side={THREE.DoubleSide} />
      </mesh>
      <mesh position={[0, 1.375, 0]}><torusGeometry args={[0.238, 0.022, 8, 20]} /><meshStandardMaterial {...trim} /></mesh>
      {/* the pendant on its chain */}
      <group ref={pend} position={[0, 1.16, 0.20]}>
        <mesh position={[0, -0.10, 0]}><octahedronGeometry args={[0.055, 0]} /><meshStandardMaterial {...trim} emissive={pal.main} emissiveIntensity={0.5} /></mesh>
        <mesh position={[0, -0.02, 0]}><cylinderGeometry args={[0.006, 0.006, 0.16, 5]} /><meshStandardMaterial {...trim} /></mesh>
      </group>
      {/* the head — still the fox, and the ears still burn */}
      <group ref={rig.head} position={[0, 1.56, 0.02]}>
        <mesh scale={[0.94, 0.92, 1.05]} castShadow><sphereGeometry args={[0.180, 14, 12]} /><meshStandardMaterial {...fur} /></mesh>
        <mesh position={[0, -0.058, 0.175]} scale={[0.62, 0.56, 1.70]}>
          <sphereGeometry args={[0.098, 12, 10]} /><meshStandardMaterial {...fur} />
        </mesh>
        <mesh position={[0, -0.052, 0.310]}><sphereGeometry args={[0.028, 8, 8]} /><meshStandardMaterial color="#241820" roughness={0.55} /></mesh>
        <group ref={earL} position={[-0.108, 0.160, -0.014]}>
          <mesh rotation={[-0.06, -0.14, 0]}><coneGeometry args={[0.072, 0.290, 9]} /><meshStandardMaterial {...fur} /></mesh>
          <mesh position={[0, 0.006, 0.034]} rotation={[-0.06, -0.14, 0]}><coneGeometry args={[0.046, 0.225, 8]} /><meshStandardMaterial color={SFX.inner} emissive={SFX.inner} emissiveIntensity={0.55} roughness={0.9} /></mesh>
        </group>
        <group ref={earR} position={[0.108, 0.160, -0.014]}>
          <mesh rotation={[-0.06, 0.14, 0]}><coneGeometry args={[0.072, 0.290, 9]} /><meshStandardMaterial {...fur} /></mesh>
          <mesh position={[0, 0.006, 0.034]} rotation={[-0.06, 0.14, 0]}><coneGeometry args={[0.046, 0.225, 8]} /><meshStandardMaterial color={SFX.inner} emissive={SFX.inner} emissiveIntensity={0.55} roughness={0.9} /></mesh>
        </group>
        {[-1, 1].map((sd) => (
          <group key={"e" + sd} position={[sd * 0.088, 0.030, 0.118]} rotation={[0, sd * 0.42, 0]}>
            <mesh scale={[1, 1, 0.5]}><sphereGeometry args={[0.048, 8, 8]} /><meshStandardMaterial color="#08080c" roughness={1} /></mesh>
            <mesh position={[0, 0, 0.028]}><circleGeometry args={[0.039, 18]} /><meshBasicMaterial map={eye} toneMapped={false} /></mesh>
          </group>
        ))}
      </group>
    </group>
  );
}

// ── INVERTED SERPENT CHAMPION · Lyf 4 / Lyt 5 ────────────────
// "Guardians of the Shadow Forest, these beings relentlessly
//  slither through the shadows patrolling for anyone who
//  threatens the safety of their homeland."
// The Beb was adorable and the Champion is NOT. The card opens
// its mouth: a broad pale hood, red eyes, a red throat and long
// fangs, over a fringe of coils. Same lineage, threat revealed.
export function InvertedSerpentChampionBody({ pal }) {
  pal = pal || getBeingPalette("inverted_serpent");
  const skin = insSkinTex();
  const eye = eyeDisc("insc_eye_v1", "#fff0f0", "#ff4a4a", "#4a0808", "rgba(8,2,2,0.94)", true);
  const rig = useBeingRig();
  const jaw = useRef(); const coils = useRef([]); const sway = useRef();
  useFrame((s) => {
    const t = s.clock.elapsedTime;
    if (sway.current) sway.current.rotation.z = Math.sin(t * 0.75) * 0.06;
    // the mouth never fully closes — it is mid-strike, always
    if (jaw.current) jaw.current.rotation.x = 0.55 + Math.sin(t * 1.7) * 0.14;
    coils.current.forEach((m, i) => {
      if (!m) return;
      m.rotation.z = Math.sin(t * (1.1 + i * 0.17) + i) * 0.24;
      m.rotation.x = Math.sin(t * (0.8 + i * 0.13) + i * 1.4) * 0.16;
    });
  });
  const body = { map: skin, roughness: 0.82, emissive: "#ffffff", emissiveIntensity: 0.05 };
  return (
    <group>
      {/* THE FRINGE — six coils hanging where a body should be */}
      {[-2.5, -1.5, -0.5, 0.5, 1.5, 2.5].map((k, i) => (
        <group key={"c" + i} ref={(el) => (coils.current[i] = el)} position={[k * 0.105, 0.62, -0.02 + (i % 2) * 0.08]}>
          {[0, 1, 2, 3].map((j) => (
            <mesh key={j} position={[0, -j * 0.145, Math.sin(j * 1.1) * 0.045]}>
              <sphereGeometry args={[0.072 - j * 0.013, 9, 8]} /><meshStandardMaterial {...body} />
            </mesh>
          ))}
        </group>
      ))}
      <group ref={sway} position={[0, 0.62, 0]}>
        <mesh position={[0, 0.16, 0]} scale={[1.05, 0.92, 0.95]} castShadow>
          <sphereGeometry args={[0.235, 14, 12]} /><meshStandardMaterial {...body} />
        </mesh>
        <group ref={rig.head} position={[0, 0.52, 0.03]}>
          {/* the HOOD — broad and flat, the cobra read */}
          <mesh scale={[1.25, 0.90, 1.05]} castShadow>
            <sphereGeometry args={[0.215, 16, 14]} /><meshStandardMaterial {...body} />
          </mesh>
          <mesh position={[0, 0.045, -0.05]} scale={[1.85, 0.46, 0.95]}>
            <sphereGeometry args={[0.190, 16, 12]} /><meshStandardMaterial {...body} />
          </mesh>
          {/* the upper jaw and the ROOF of the mouth */}
          <mesh position={[0, -0.055, 0.150]} scale={[1.0, 0.60, 1.05]}>
            <sphereGeometry args={[0.155, 14, 12]} /><meshStandardMaterial {...body} />
          </mesh>
          {[-1, 1].map((sd) => (
            <mesh key={"f" + sd} position={[sd * 0.072, -0.135, 0.195]} rotation={[0.25, 0, 0]}>
              <coneGeometry args={[0.026, 0.135, 7]} /><meshStandardMaterial color="#fffaf2" roughness={0.4} />
            </mesh>
          ))}
          {/* THE OPEN MOUTH — red, and it is the whole card */}
          <group ref={jaw} position={[0, -0.115, 0.075]}>
            <mesh position={[0, -0.045, 0.115]} scale={[0.92, 0.44, 1.15]}>
              <sphereGeometry args={[0.145, 12, 10]} /><meshStandardMaterial {...body} />
            </mesh>
            <mesh position={[0, -0.010, 0.100]} scale={[0.80, 0.32, 1.0]}>
              <sphereGeometry args={[0.130, 12, 10]} />
              <meshStandardMaterial color="#8e1a1a" roughness={0.7} emissive="#5a0a0a" emissiveIntensity={0.4} />
            </mesh>
            {[-1, 1].map((sd) => (
              <mesh key={"lf" + sd} position={[sd * 0.062, 0.030, 0.180]}>
                <coneGeometry args={[0.021, 0.100, 6]} /><meshStandardMaterial color="#fffaf2" roughness={0.4} />
              </mesh>
            ))}
          </group>
          {/* red eyes — the Beb's were dark and kind */}
          {[-1, 1].map((sd) => (
            <group key={"e" + sd} position={[sd * 0.135, 0.048, 0.140]} rotation={[0, sd * 0.34, 0]}>
              <mesh scale={[1, 1, 0.5]}><sphereGeometry args={[0.062, 10, 8]} /><meshStandardMaterial color="#1a0808" roughness={0.7} /></mesh>
              <mesh position={[0, 0, 0.034]}><circleGeometry args={[0.050, 18]} /><meshBasicMaterial map={eye} toneMapped={false} /></mesh>
            </group>
          ))}
        </group>
      </group>
      <mesh position={[0, 0.8, 0]}>
        <sphereGeometry args={[0.74, 14, 10]} />
        <meshBasicMaterial color={pal.glow} transparent opacity={0.06} side={THREE.BackSide} toneMapped={false} depthWrite={false} />
      </mesh>
    </group>
  );
}

// ── ANTDROID CHAMPION · Lyf 6 / Lyt 3 ────────────────────────
// "Born on planet Allioth where oceans are molten metal, this
//  Champion's body is comprised of this metal and it's circuits
//  are just as tough."
// Molten-metal oceans, so the shell went RED. It stands now, and
// it has been given a golden fan behind it and a triangle sigil
// on the chest — a droid that has become an icon of itself.
const ADC = { shell: "#b83a2a", deep: "#4a1410", gold: "#ffcf5c", tri: "#ffe08a", lens: "#0a0c10" };
export function AntdroidChampionBody({ pal }) {
  pal = pal || getBeingPalette("antdroid");
  const plate = plateTex("adc_plate_v1", "#d8543c", "#a8321f", "#3f100c", "rgba(255,220,190,0.5)");
  const gold = goldTex();
  const rig = useBeingRig();
  const fan = useRef(); const tri = useRef(); const antL = useRef(); const antR = useRef();
  useFrame((s) => {
    const t = s.clock.elapsedTime;
    if (fan.current) fan.current.rotation.z = Math.sin(t * 0.35) * 0.05;
    if (tri.current) tri.current.material.opacity = 0.6 + Math.abs(Math.sin(t * 1.3)) * 0.4;
    if (antL.current) antL.current.rotation.z = 0.5 + Math.sin(t * 2.1) * 0.10;
    if (antR.current) antR.current.rotation.z = -0.5 - Math.sin(t * 2.1 + 1.4) * 0.10;
  });
  const shell = { map: plate, metalness: 0.62, roughness: 0.4 };
  const trim = { map: gold, metalness: 0.75, roughness: 0.28 };
  return (
    <group>
      {/* THE FAN — a golden halo of blades behind the shoulders */}
      <group ref={fan} position={[0, 1.10, -0.22]}>
        {Array.from({ length: 13 }, (_, i) => {
          const a = -1.35 + (i / 12) * 2.7;
          return (
            <mesh key={i} position={[Math.sin(a) * 0.46, Math.cos(a) * 0.46, 0]} rotation={[0, 0, -a]}>
              <coneGeometry args={[0.052, 0.34 + (i % 3) * 0.09, 4]} /><meshStandardMaterial {...trim} />
            </mesh>
          );
        })}
        <mesh><circleGeometry args={[0.36, 26]} /><meshBasicMaterial color="#ffcf5c" transparent opacity={0.18} toneMapped={false} depthWrite={false} /></mesh>
      </group>
      {/* upright now: legs, thorax, and a segmented abdomen behind */}
      {[-1, 1].map((sd) => (
        <group key={"l" + sd} ref={sd < 0 ? rig.legL : rig.legR} position={[sd * 0.13, 0, 0]}>
          <mesh position={[0, 0.32, 0]}><capsuleGeometry args={[0.058, 0.30, 4, 10]} /><meshStandardMaterial {...shell} /></mesh>
          <mesh position={[0, 0.06, 0.035]} scale={[1, 0.6, 1.5]}><sphereGeometry args={[0.062, 10, 8]} /><meshStandardMaterial color={ADC.deep} metalness={0.5} roughness={0.6} /></mesh>
        </group>
      ))}
      {[0, 1, 2].map((i) => (
        <mesh key={"ab" + i} position={[0, 0.74 - i * 0.02, -0.30 - i * 0.16]} scale={[1 - i * 0.12, 1 - i * 0.12, 1]}>
          <sphereGeometry args={[0.165, 12, 10]} /><meshStandardMaterial {...shell} />
        </mesh>
      ))}
      <mesh position={[0, 0.88, 0]} scale={[1.05, 1.15, 0.85]} castShadow>
        <sphereGeometry args={[0.235, 14, 12]} /><meshStandardMaterial {...shell} />
      </mesh>
      {/* THE SIGIL — a triangle in a ring, burning */}
      <mesh position={[0, 0.92, 0.185]}><torusGeometry args={[0.098, 0.016, 8, 22]} /><meshStandardMaterial {...trim} /></mesh>
      <mesh ref={tri} position={[0, 0.92, 0.190]} rotation={[0, 0, 0]}>
        <circleGeometry args={[0.070, 3]} />
        <meshBasicMaterial color={ADC.tri} transparent opacity={0.85} toneMapped={false} />
      </mesh>
      {/* four working arms, because it is still an ant */}
      {[-1, 1].map((sd) => [0, 1].map((k) => (
        <mesh key={"a" + sd + k} ref={k === 0 ? (sd < 0 ? rig.armL : rig.armR) : undefined}
          position={[sd * 0.26, 1.02 - k * 0.20, 0.02 + k * 0.06]} rotation={[0.3 + k * 0.3, 0, sd * (0.55 + k * 0.25)]}>
          <capsuleGeometry args={[0.036 - k * 0.007, 0.26 - k * 0.05, 4, 8]} /><meshStandardMaterial {...shell} />
        </mesh>
      )))}
      <group ref={rig.head} position={[0, 1.28, 0.03]}>
        <mesh scale={[1.05, 1.0, 0.95]} castShadow><sphereGeometry args={[0.195, 16, 14]} /><meshStandardMaterial {...shell} /></mesh>
        {[-1, 1].map((sd) => (
          <mesh key={"e" + sd} position={[sd * 0.090, 0.020, 0.155]} scale={[1, 1.2, 0.6]}>
            <sphereGeometry args={[0.066, 12, 12]} /><meshStandardMaterial color={ADC.lens} metalness={0.35} roughness={0.07} />
          </mesh>
        ))}
        {[-1, 1].map((sd) => (
          <mesh key={"m" + sd} position={[sd * 0.052, -0.130, 0.140]} rotation={[1.25, 0, sd * 0.45]}>
            <coneGeometry args={[0.024, 0.110, 6]} /><meshStandardMaterial {...trim} />
          </mesh>
        ))}
        {/* the horns the Champion card gives it — curved, gold */}
        <group ref={antL} position={[-0.090, 0.150, 0.02]}>
          <mesh rotation={[0, 0, 0.5]}><capsuleGeometry args={[0.017, 0.28, 3, 6]} /><meshStandardMaterial {...trim} /></mesh>
        </group>
        <group ref={antR} position={[0.090, 0.150, 0.02]}>
          <mesh rotation={[0, 0, -0.5]}><capsuleGeometry args={[0.017, 0.28, 3, 6]} /><meshStandardMaterial {...trim} /></mesh>
        </group>
      </group>
    </group>
  );
}

// ── DG-0 FARMER · Lyf 7 / Lyt 2 ──────────────────────────────
// "Farmers of Hope, these beings bring peace and prosperity, if
//  you share their point of view, that is."
// The kindest flavour text in the game with a knife in the last
// clause, and the card matches: a tall figure in deep teal
// vestments, a wide ornate collar, a disc behind the head. It
// gave up the wings for a habit. Lyf SEVEN — it does not move.
export function DG0ChampionBody({ pal }) {
  pal = pal || getBeingPalette("dg0");
  const robe = robeCloth("dgc_robe_v1", "#2e7a76", "#1b4f4c", "#0c2422", "#d8c07a");
  const gold = goldTex();
  const plate = plateTex("dga_plate_v1", "#5cb0a8", "#3f8a84", "#16302e", "rgba(220,240,235,0.5)");
  const rig = useBeingRig();
  const halo = useRef(); const gem = useRef();
  useFrame((s) => {
    const t = s.clock.elapsedTime;
    if (halo.current) halo.current.material.opacity = 0.30 + Math.sin(t * 0.55) * 0.12;
    if (gem.current) gem.current.material.opacity = 0.55 + Math.sin(t * 1.2) * 0.28;
  });
  const cloth = { map: robe, roughness: 1 };
  const trim = { map: gold, metalness: 0.72, roughness: 0.3 };
  return (
    <group>
      <mesh position={[0, 0.60, 0]} castShadow>
        <coneGeometry args={[0.44, 1.22, 16]} /><meshStandardMaterial {...cloth} />
      </mesh>
      {/* the hem band, and the vertical orphrey down the front */}
      <mesh position={[0, 0.10, 0]}><cylinderGeometry args={[0.435, 0.44, 0.10, 16, 1, true]} /><meshStandardMaterial {...trim} side={THREE.DoubleSide} /></mesh>
      <mesh position={[0, 0.62, 0.30]} rotation={[0.06, 0, 0]}>
        <planeGeometry args={[0.13, 1.02]} /><meshStandardMaterial {...trim} side={THREE.DoubleSide} />
      </mesh>
      <mesh position={[0, 1.12, 0]} scale={[1.0, 0.94, 0.86]} castShadow>
        <sphereGeometry args={[0.285, 14, 12]} /><meshStandardMaterial {...cloth} />
      </mesh>
      {/* THE COLLAR — wide, ornate, the Farmer's whole rank */}
      <mesh position={[0, 1.30, 0]} rotation={[0, 0, 0]}>
        <coneGeometry args={[0.44, 0.30, 16, 1, true]} />
        <meshStandardMaterial {...cloth} side={THREE.DoubleSide} />
      </mesh>
      <mesh position={[0, 1.16, 0]}><torusGeometry args={[0.415, 0.026, 8, 24]} /><meshStandardMaterial {...trim} /></mesh>
      {Array.from({ length: 12 }, (_, i) => {
        const a = (i / 12) * Math.PI * 2;
        return (
          <mesh key={"cs" + i} position={[Math.cos(a) * 0.40, 1.20, Math.sin(a) * 0.40]} rotation={[0, -a, 0.3]}>
            <coneGeometry args={[0.026, 0.13, 4]} /><meshStandardMaterial {...trim} />
          </mesh>
        );
      })}
      {/* sleeves down, hands together — a farmer's rest */}
      {[-1, 1].map((sd) => (
        <group key={"a" + sd} ref={sd < 0 ? rig.armL : rig.armR} position={[sd * 0.29, 1.06, 0.05]}>
          <mesh rotation={[0.42, 0, sd * 0.28]}><capsuleGeometry args={[0.082, 0.34, 4, 10]} /><meshStandardMaterial {...cloth} /></mesh>
          <mesh position={[sd * -0.10, -0.30, 0.20]}><sphereGeometry args={[0.062, 10, 8]} /><meshStandardMaterial map={plate} metalness={0.5} roughness={0.45} /></mesh>
        </group>
      ))}
      {/* the red gem it kept from the Angel */}
      <mesh position={[0, 1.10, 0.245]}><sphereGeometry args={[0.042, 12, 10]} /><meshStandardMaterial color={DGA.gem} emissive={DGA.gem} emissiveIntensity={0.9} roughness={0.25} /></mesh>
      <mesh ref={gem} position={[0, 1.10, 0.245]}><sphereGeometry args={[0.090, 10, 8]} /><meshBasicMaterial color={DGA.gem} transparent opacity={0.6} toneMapped={false} depthWrite={false} /></mesh>
      {/* the dome, and the disc behind it */}
      <group ref={rig.head} position={[0, 1.60, 0.01]}>
        <mesh ref={halo} position={[0, 0.02, -0.15]}>
          <circleGeometry args={[0.335, 32]} />
          <meshBasicMaterial color={DGA.wingHi} transparent opacity={0.32} side={THREE.DoubleSide} toneMapped={false} depthWrite={false} />
        </mesh>
        <mesh position={[0, 0.02, -0.145]}><ringGeometry args={[0.325, 0.352, 32]} /><meshStandardMaterial {...trim} /></mesh>
        <mesh scale={[1.0, 0.95, 1.0]} castShadow>
          <sphereGeometry args={[0.180, 16, 14]} /><meshStandardMaterial map={plate} metalness={0.5} roughness={0.32} />
        </mesh>
        {[-1, 1].map((sd) => (
          <mesh key={"x" + sd} position={[0, 0.035, 0.170]} rotation={[0, 0, sd * 0.78]}>
            <boxGeometry args={[0.110, 0.019, 0.012]} /><meshStandardMaterial color={DGA.deep} roughness={0.6} />
          </mesh>
        ))}
        {[-1, 1].map((sd) => (
          <mesh key={"o" + sd} position={[sd * 0.085, -0.045, 0.152]}>
            <circleGeometry args={[0.028, 14]} /><meshBasicMaterial color={DGA.wingHi} transparent opacity={0.8} toneMapped={false} />
          </mesh>
        ))}
      </group>
    </group>
  );
}

// ── H-01 QUASAR · Lyf 7 / Lyt 2 ──────────────────────────────
// "Self-proclaimed Sons of the Suns, these humans have superior
//  suits capable of defending these soldiers against even
//  Champion level threats."
// Lyf SEVEN — the highest defence in the roster, and the card
// spends the whole budget on the suit: dark navy plate, a red
// stripe down the sternum, GOLD SOLAR VANES flaring off the
// hips, and a ring of sun behind the helmet. "Sons of the Suns."
const H01C = { suit: "#141a2e", plate: "#2a3450", gold: "#ffcf5c", red: "#c9302a", glass: "#dfeaff" };
export function H01ChampionBody({ pal }) {
  pal = pal || getBeingPalette("h01");
  const suit = suitTex("h01c_suit_v1", "#33405f", "#1c2440", "#0a0e1c", "#c9302a");
  const gold = goldTex();
  const rig = useBeingRig();
  const halo = useRef(); const vanes = useRef([]);
  useFrame((s) => {
    const t = s.clock.elapsedTime;
    if (halo.current) halo.current.material.opacity = 0.34 + Math.sin(t * 0.7) * 0.14;
    vanes.current.forEach((m, i) => {
      if (m) m.rotation.z = (i % 2 ? 1 : -1) * (0.4 + Math.sin(t * 0.6 + i) * 0.06);
    });
  });
  const cloth = { map: suit, roughness: 0.75, metalness: 0.25 };
  const trim = { map: gold, metalness: 0.78, roughness: 0.26 };
  return (
    <group>
      {[-1, 1].map((sd) => (
        <group key={"l" + sd} ref={sd < 0 ? rig.legL : rig.legR} position={[sd * 0.145, 0, 0]}>
          <mesh position={[0, 0.38, 0]}><capsuleGeometry args={[0.095, 0.34, 4, 10]} /><meshStandardMaterial {...cloth} /></mesh>
          <mesh position={[0, 0.09, 0.05]} scale={[1.15, 0.7, 1.5]}><sphereGeometry args={[0.100, 10, 8]} /><meshStandardMaterial color="#0a0e1c" roughness={0.7} /></mesh>
        </group>
      ))}
      {/* THE SOLAR VANES — four gold blades off the hips */}
      {[-1, 1].map((sd) => [0, 1].map((k) => (
        <mesh key={"v" + sd + k} ref={(el) => (vanes.current[(sd > 0 ? 2 : 0) + k] = el)}
          position={[sd * (0.26 + k * 0.10), 0.72 - k * 0.16, -0.04 - k * 0.05]}
          rotation={[0, sd * -0.3, sd * 0.4]}>
          <coneGeometry args={[0.09, 0.62 - k * 0.14, 4]} /><meshStandardMaterial {...trim} />
        </mesh>
      )))}
      <mesh position={[0, 0.94, 0]} scale={[1.18, 1.14, 0.92]} castShadow>
        <capsuleGeometry args={[0.230, 0.26, 5, 12]} /><meshStandardMaterial {...cloth} />
      </mesh>
      {/* the red sternum stripe */}
      <mesh position={[0, 0.96, 0.215]} rotation={[0.04, 0, 0]}>
        <boxGeometry args={[0.052, 0.44, 0.016]} />
        <meshStandardMaterial color={H01C.red} emissive={H01C.red} emissiveIntensity={0.35} roughness={0.5} />
      </mesh>
      <mesh position={[0, 0.74, 0]}><torusGeometry args={[0.235, 0.030, 8, 20]} /><meshStandardMaterial {...trim} /></mesh>
      <mesh position={[0, 1.00, -0.22]} castShadow><boxGeometry args={[0.32, 0.36, 0.15]} /><meshStandardMaterial color="#0a0e1c" roughness={0.7} /></mesh>
      {[-1, 1].map((sd) => (
        <group key={"a" + sd} ref={sd < 0 ? rig.armL : rig.armR} position={[sd * 0.30, 1.08, 0]}>
          <mesh scale={[1.1, 0.95, 1.1]}><sphereGeometry args={[0.115, 12, 10]} /><meshStandardMaterial {...trim} /></mesh>
          <mesh position={[sd * 0.035, -0.28, 0.01]} rotation={[0, 0, sd * -0.10]}>
            <capsuleGeometry args={[0.078, 0.30, 4, 10]} /><meshStandardMaterial {...cloth} />
          </mesh>
          <mesh position={[sd * 0.062, -0.50, 0.04]}><sphereGeometry args={[0.085, 10, 8]} /><meshStandardMaterial color="#0a0e1c" roughness={0.7} /></mesh>
        </group>
      ))}
      <group ref={rig.head} position={[0, 1.40, 0.01]}>
        {/* the sun behind the helm */}
        <mesh ref={halo} position={[0, 0.03, -0.16]}>
          <ringGeometry args={[0.245, 0.335, 40]} />
          <meshBasicMaterial color={H01C.gold} transparent opacity={0.4} side={THREE.DoubleSide} toneMapped={false} depthWrite={false} />
        </mesh>
        <mesh position={[0, 0.03, -0.155]}><ringGeometry args={[0.248, 0.268, 40]} /><meshStandardMaterial {...trim} /></mesh>
        <mesh castShadow><sphereGeometry args={[0.200, 16, 14]} /><meshStandardMaterial color="#c8d2e0" metalness={0.55} roughness={0.32} /></mesh>
        <mesh position={[0, 0.005, 0.075]} scale={[1, 0.9, 1]}>
          <sphereGeometry args={[0.172, 16, 14, 0, Math.PI * 2, 0, Math.PI / 1.9]} />
          <meshStandardMaterial color="#0b1226" metalness={0.45} roughness={0.1} />
        </mesh>
        <mesh position={[0, 0.005, 0.145]} rotation={[0.1, 0, 0]}>
          <circleGeometry args={[0.130, 20]} />
          <meshBasicMaterial color={H01C.glass} transparent opacity={0.5} toneMapped={false} depthWrite={false} />
        </mesh>
        <mesh position={[0, -0.185, 0]}><torusGeometry args={[0.150, 0.032, 8, 18]} /><meshStandardMaterial {...trim} /></mesh>
      </group>
    </group>
  );
}

// ── H-09 PULSAR · Lyf 4 / Lyt 5 ──────────────────────────────
// "Self-proclaimed Generals of the Stars, these humans wield Star
//  level blasters that can decimate most Champions in mere
//  seconds."
// The Beb was militant; the Champion is a GENERAL. The card puts
// it in hot amber and stands it inside a wedge of light, rays
// thrown out to either side. It is lit like an announcement.
export function H09ChampionBody({ pal }) {
  pal = pal || getBeingPalette("h09");
  const suit = suitTex("h09c_suit_v1", "#ffbe5a", "#e8862a", "#7a3a08", "#fff0c8");
  const gold = goldTex();
  const rig = useBeingRig();
  const rays = useRef([]); const visor = useRef();
  useFrame((s) => {
    const t = s.clock.elapsedTime;
    rays.current.forEach((m, i) => {
      if (!m) return;
      const p = 0.5 + Math.sin(t * 0.8 + i * 0.7) * 0.5;
      m.material.opacity = 0.10 + p * 0.22;
      m.scale.y = 0.85 + p * 0.30;
    });
    if (visor.current) visor.current.material.opacity = 0.45 + Math.sin(t * 1.9) * 0.18;
  });
  const cloth = { map: suit, roughness: 0.8 };
  const trim = { map: gold, metalness: 0.75, roughness: 0.3 };
  const dark = { color: "#3a2410", roughness: 0.8 };
  return (
    <group>
      {/* the star-rays it is announced by — behind, wide, breathing */}
      {[-2, -1, 1, 2].map((k, i) => (
        <mesh key={"r" + i} ref={(el) => (rays.current[i] = el)}
          position={[k * 0.30, 0.95, -0.30]} rotation={[0, 0, -k * 0.28]}>
          <planeGeometry args={[0.14, 1.9]} />
          <meshBasicMaterial color={H09.halo} transparent opacity={0.18} side={THREE.DoubleSide} toneMapped={false} depthWrite={false} />
        </mesh>
      ))}
      {[-1, 1].map((sd) => (
        <group key={"l" + sd} ref={sd < 0 ? rig.legL : rig.legR} position={[sd * 0.135, 0, 0]}>
          <mesh position={[0, 0.37, 0]}><capsuleGeometry args={[0.088, 0.33, 4, 10]} /><meshStandardMaterial {...cloth} /></mesh>
          <mesh position={[0, 0.085, 0.045]} scale={[1.1, 0.7, 1.5]}><sphereGeometry args={[0.095, 10, 8]} /><meshStandardMaterial {...dark} /></mesh>
        </group>
      ))}
      <mesh position={[0, 0.90, 0]} scale={[1.12, 1.12, 0.88]} castShadow>
        <capsuleGeometry args={[0.220, 0.25, 5, 12]} /><meshStandardMaterial {...cloth} />
      </mesh>
      {/* a general's rig: shoulder boards and a chest bar */}
      {[-1, 1].map((sd) => (
        <mesh key={"sb" + sd} position={[sd * 0.235, 1.10, 0]} rotation={[0, 0, sd * -0.22]}>
          <boxGeometry args={[0.17, 0.045, 0.20]} /><meshStandardMaterial {...trim} />
        </mesh>
      ))}
      <mesh position={[0, 0.99, 0.205]}><boxGeometry args={[0.19, 0.035, 0.014]} /><meshStandardMaterial {...trim} /></mesh>
      <mesh position={[0, 0.92, 0.205]}><boxGeometry args={[0.13, 0.030, 0.012]} /><meshStandardMaterial {...trim} /></mesh>
      <mesh position={[0, 0.96, -0.21]} castShadow><boxGeometry args={[0.30, 0.34, 0.14]} /><meshStandardMaterial {...dark} /></mesh>
      {[-1, 1].map((sd) => (
        <group key={"a" + sd} ref={sd < 0 ? rig.armL : rig.armR} position={[sd * 0.275, 1.04, 0]}>
          <mesh scale={[1, 0.9, 1]}><sphereGeometry args={[0.105, 12, 10]} /><meshStandardMaterial {...cloth} /></mesh>
          <mesh position={[sd * 0.03, -0.26, 0.02]} rotation={[0.15, 0, sd * -0.08]}>
            <capsuleGeometry args={[0.072, 0.28, 4, 10]} /><meshStandardMaterial {...cloth} />
          </mesh>
          <mesh position={[sd * 0.055, -0.46, 0.06]} scale={[1, 0.95, 1]}>
            <boxGeometry args={[0.105, 0.105, 0.115]} /><meshStandardMaterial {...dark} />
          </mesh>
        </group>
      ))}
      <group ref={rig.head} position={[0, 1.36, 0.01]}>
        <mesh castShadow><sphereGeometry args={[0.200, 16, 14]} /><meshStandardMaterial color="#f6f2e6" roughness={0.5} metalness={0.2} /></mesh>
        <mesh position={[0, 0.005, 0.075]} scale={[1, 0.9, 1]}>
          <sphereGeometry args={[0.172, 16, 14, 0, Math.PI * 2, 0, Math.PI / 1.9]} />
          <meshStandardMaterial color="#221a10" metalness={0.45} roughness={0.1} />
        </mesh>
        <mesh ref={visor} position={[0, 0.005, 0.145]} rotation={[0.1, 0, 0]}>
          <circleGeometry args={[0.128, 20]} />
          <meshBasicMaterial color="#ffd48a" transparent opacity={0.5} toneMapped={false} depthWrite={false} />
        </mesh>
        <mesh position={[0, -0.185, 0]}><torusGeometry args={[0.150, 0.032, 8, 18]} /><meshStandardMaterial {...trim} /></mesh>
      </group>
    </group>
  );
}

// ── LUCKY DRAGON CHAMPION · Lyf 3 / Lyt 6 ────────────────────
// "Found only in barren wastelands of Shade, these dealers will
//  grant almost any wish, but at what cost?"
// A DEALER now, not a pup. The Beb's two horns have closed into
// one enormous golden CRESCENT above the head — a moon it wears,
// and the shape a wish is granted under.
export function LuckyDragonChampionBody({ pal }) {
  pal = pal || getBeingPalette("lucky_dragon");
  const scale = lkdScaleTex();
  const gold = goldTex();
  const eye = eyeDisc("lkdc_eye_v1", "#fff2d0", "#ffb02e", "#5a2a08", "rgba(12,6,14,0.92)", true);
  const rig = useBeingRig();
  const moon = useRef(); const frills = useRef([]); const wings = useRef([]);
  useFrame((s) => {
    const t = s.clock.elapsedTime;
    // a slow, held wing beat — it is a dealer, not a hunter
    wings.current.forEach((w, i) => {
      if (!w) return;
      const sd = i ? 1 : -1;
      w.rotation.y = sd * (-0.45 + Math.sin(t * 0.8) * 0.16);
      w.rotation.z = sd * Math.sin(t * 0.8 + 0.5) * 0.10;
    });
    if (moon.current) {
      moon.current.rotation.z = Math.sin(t * 0.4) * 0.05;
      moon.current.position.y = 0.34 + Math.sin(t * 0.8) * 0.018;
    }
    frills.current.forEach((m, i) => {
      if (m) m.rotation.z = (i % 2 ? 1 : -1) * (0.5 + Math.sin(t * 1.1 + i) * 0.12);
    });
  });
  const hide = { map: scale, color: LKD_GREEN.champion, roughness: 0.78 };
  const trim = { map: gold, metalness: 0.78, roughness: 0.26 };
  const wingBone = { color: LKD_WING.bone, roughness: 0.7, metalness: 0.15 };
  return (
    <group>
      {/* PROPER WINGS (3D-28) — caP'n: "just give it proper wings
          half the size of the lord". The house CDWing primitive at
          0.55 scale, membrane purple. It is a Dragon-ilk Champion
          and it was the only dragon in the roster without wings. */}
      {[-1, 1].map((sd) => (
        <group key={"wg" + sd} ref={(el) => (wings.current[sd > 0 ? 1 : 0] = el)}
          position={[sd * 0.20, 0.72, -0.14]} rotation={[0, sd * -0.45, 0]} scale={[sd * 0.55, 0.55, 0.55]}>
          <CDWing boneMat={wingBone} membrane={LKD_WING.membrane} mOpacity={0.6} glow={LKD_WING.glow} />
        </group>
      ))}
      <mesh position={[0, 0.50, -0.05]} scale={[0.86, 0.84, 1.15]} castShadow>
        <sphereGeometry args={[0.215, 14, 12]} /><meshStandardMaterial {...hide} />
      </mesh>
      {[-1, 1].map((sd) => (
        <group key={"l" + sd} ref={sd < 0 ? rig.legL : rig.legR} position={[sd * 0.125, 0, -0.02]}>
          <mesh position={[0, 0.28, 0]}><capsuleGeometry args={[0.052, 0.24, 4, 8]} /><meshStandardMaterial {...hide} /></mesh>
          <mesh position={[0, 0.06, 0.040]} scale={[1, 0.62, 1.4]}><sphereGeometry args={[0.058, 10, 8]} /><meshStandardMaterial color={LKD.deep} roughness={0.85} /></mesh>
        </group>
      ))}
      {[-1, 1].map((sd) => (
        <mesh key={"a" + sd} ref={sd < 0 ? rig.armL : rig.armR} position={[sd * 0.195, 0.56, 0.10]} rotation={[0.3, 0, sd * 0.42]}>
          <capsuleGeometry args={[0.040, 0.20, 4, 8]} /><meshStandardMaterial {...hide} />
        </mesh>
      ))}
      <group ref={rig.head} position={[0, 0.86, 0.08]}>
        <mesh scale={[1.10, 0.96, 1.05]} castShadow>
          <sphereGeometry args={[0.215, 16, 14]} /><meshStandardMaterial {...hide} />
        </mesh>
        <mesh position={[0, -0.072, 0.180]} scale={[1.02, 0.74, 1.20]}>
          <sphereGeometry args={[0.132, 12, 10]} /><meshStandardMaterial {...hide} />
        </mesh>
        {[-1, 1].map((sd) => (
          <group key={"e" + sd} position={[sd * 0.115, 0.022, 0.148]} rotation={[0, sd * 0.40, 0]}>
            <mesh scale={[1, 1, 0.5]}><sphereGeometry args={[0.062, 10, 8]} /><meshStandardMaterial color="#100810" roughness={1} /></mesh>
            <mesh position={[0, 0, 0.034]}><circleGeometry args={[0.050, 20]} /><meshBasicMaterial map={eye} toneMapped={false} /></mesh>
          </group>
        ))}
        {/* the jaw frills the card fans out either side */}
        {[-1, 1].map((sd) => [0, 1].map((k) => (
          <mesh key={"fr" + sd + k} ref={(el) => (frills.current[(sd > 0 ? 2 : 0) + k] = el)}
            position={[sd * (0.185 + k * 0.05), -0.06 - k * 0.07, 0.03]} rotation={[0, sd * -0.3, sd * 0.5]}>
            <coneGeometry args={[0.048, 0.22 - k * 0.05, 5]} /><meshStandardMaterial {...hide} />
          </mesh>
        )))}
        <mesh position={[0, 0.145, 0.155]}><octahedronGeometry args={[0.036, 0]} /><meshStandardMaterial color={LKD.gem} emissive={LKD.gem} emissiveIntensity={0.85} roughness={0.25} /></mesh>
        {/* THE CRESCENT — one moon, worn. Twenty beads on an arc. */}
        <group ref={moon} position={[0, 0.34, -0.02]}>
          {Array.from({ length: 19 }, (_, i) => {
            const a = -1.16 + (i / 18) * 2.32;
            const r = 0.40;
            const th = 0.050 * (1 - Math.pow(Math.abs(i / 18 - 0.5) * 2, 2.1) * 0.86);
            return (
              <mesh key={i} position={[Math.sin(a) * r, Math.cos(a) * r * 0.62, 0]}>
                <sphereGeometry args={[Math.max(0.010, th), 9, 8]} />
                <meshStandardMaterial {...trim} emissive={LKD.horn} emissiveIntensity={0.28} />
              </mesh>
            );
          })}
          <mesh><circleGeometry args={[0.30, 26]} /><meshBasicMaterial color={LKD.hornHi} transparent opacity={0.10} toneMapped={false} depthWrite={false} /></mesh>
        </group>
      </group>
      <mesh position={[0, 0.7, 0]}>
        <sphereGeometry args={[0.80, 14, 10]} />
        <meshBasicMaterial color={pal.glow} transparent opacity={0.07} side={THREE.BackSide} toneMapped={false} depthWrite={false} />
      </mesh>
    </group>
  );
}

// ── PREMIER DRAGON CHAMPION · Lyf 5 / Lyt 4 ──────────────────
// "As the first Dragon that Ascended to Champion, it's power is
//  only rivaled by its talent."
// The Beb's sunburst crown has cooled into something older and
// more ornate: a long green-gold head with WHISKERS, and the
// Bloom has moved inside — a burning orange heart under the jaw,
// throwing its light up onto the face.
export function PremierDragonChampionBody({ pal }) {
  pal = pal || getBeingPalette("premier_dragon");
  const scale = prdScaleTex();
  const gold = goldTex();
  const eye = eyeDisc("prdc_eye_v1", "#eafffd", "#4ad8d0", "#0e3a3a", "rgba(6,16,18,0.9)", true);
  const rig = useBeingRig();
  const heart = useRef(); const whisk = useRef([]);
  useFrame((s) => {
    const t = s.clock.elapsedTime;
    if (heart.current) {
      const k = 0.55 + Math.abs(Math.sin(t * 0.95)) * 0.45;
      heart.current.material.opacity = k;
      heart.current.scale.setScalar(0.92 + Math.sin(t * 0.95) * 0.12);
    }
    whisk.current.forEach((m, i) => {
      if (m) m.rotation.z = (i % 2 ? 1 : -1) * 0.35 + Math.sin(t * 0.9 + i * 1.3) * 0.13;
    });
  });
  const hide = { map: scale, color: "#7fae7a", roughness: 0.72 };
  const trim = { map: gold, metalness: 0.78, roughness: 0.26 };
  return (
    <group>
      <mesh position={[0, 0.50, -0.05]} scale={[0.94, 0.88, 1.18]} castShadow>
        <sphereGeometry args={[0.225, 14, 12]} /><meshStandardMaterial {...hide} />
      </mesh>
      {[-1, 1].map((sd) => (
        <group key={"l" + sd} ref={sd < 0 ? rig.legL : rig.legR} position={[sd * 0.135, 0, -0.02]}>
          <mesh position={[0, 0.28, 0]}><capsuleGeometry args={[0.058, 0.24, 4, 8]} /><meshStandardMaterial {...hide} /></mesh>
          <mesh position={[0, 0.06, 0.042]} scale={[1, 0.62, 1.4]}><sphereGeometry args={[0.064, 10, 8]} /><meshStandardMaterial color={PRD.deep} roughness={0.85} /></mesh>
        </group>
      ))}
      {[-1, 1].map((sd) => (
        <mesh key={"a" + sd} ref={sd < 0 ? rig.armL : rig.armR} position={[sd * 0.205, 0.56, 0.10]} rotation={[0.28, 0, sd * 0.4]}>
          <capsuleGeometry args={[0.044, 0.20, 4, 8]} /><meshStandardMaterial {...hide} />
        </mesh>
      ))}
      {/* THE HEART — the Bloom, moved inside and burning under the jaw */}
      <mesh position={[0, 0.70, 0.185]}><sphereGeometry args={[0.058, 12, 10]} /><meshStandardMaterial color="#ff7a1a" emissive="#ff9a2e" emissiveIntensity={1.1} roughness={0.3} /></mesh>
      <mesh ref={heart} position={[0, 0.70, 0.185]}>
        <sphereGeometry args={[0.150, 12, 10]} />
        <meshBasicMaterial color="#ffb04a" transparent opacity={0.6} toneMapped={false} depthWrite={false} />
      </mesh>
      {Array.from({ length: 9 }, (_, i) => {
        const a = (i / 9) * Math.PI * 2;
        return (
          <mesh key={"hr" + i} position={[Math.cos(a) * 0.135, 0.70 + Math.sin(a) * 0.135, 0.178]} rotation={[0, 0, a - Math.PI / 2]}>
            <coneGeometry args={[0.020, 0.09, 4]} />
            <meshBasicMaterial color="#ffd24a" transparent opacity={0.6} toneMapped={false} depthWrite={false} />
          </mesh>
        );
      })}
      <group ref={rig.head} position={[0, 0.98, 0.06]}>
        <mesh scale={[1.02, 0.94, 1.20]} castShadow>
          <sphereGeometry args={[0.210, 16, 14]} /><meshStandardMaterial {...hide} />
        </mesh>
        <mesh position={[0, -0.062, 0.215]} scale={[0.90, 0.70, 1.35]}>
          <sphereGeometry args={[0.130, 12, 10]} /><meshStandardMaterial {...hide} />
        </mesh>
        {[-1, 1].map((sd) => (
          <group key={"e" + sd} position={[sd * 0.112, 0.030, 0.165]} rotation={[0, sd * 0.36, 0]}>
            <mesh scale={[1, 1, 0.5]}><sphereGeometry args={[0.058, 10, 8]} /><meshStandardMaterial color="#0c1414" roughness={1} /></mesh>
            <mesh position={[0, 0, 0.032]}><circleGeometry args={[0.046, 20]} /><meshBasicMaterial map={eye} toneMapped={false} /></mesh>
          </group>
        ))}
        {/* THE WHISKERS — long, gold-tipped, the eastern-dragon read */}
        {[-1, 1].map((sd) => [0, 1].map((k) => (
          <group key={"w" + sd + k} ref={(el) => (whisk.current[(sd > 0 ? 2 : 0) + k] = el)}
            position={[sd * (0.10 + k * 0.03), -0.05 - k * 0.04, 0.30]}>
            {[0, 1, 2, 3, 4].map((j) => (
              <mesh key={j} position={[sd * (0.05 + j * 0.075), -j * 0.045 + Math.sin(j) * 0.03, -j * 0.05]}>
                <sphereGeometry args={[Math.max(0.006, 0.020 - j * 0.003), 6, 5]} />
                <meshStandardMaterial {...(j > 2 ? trim : hide)} />
              </mesh>
            ))}
          </group>
        )))}
        {/* horns swept back, and the brow ring */}
        {[-1, 1].map((sd) => (
          <mesh key={"h" + sd} position={[sd * 0.115, 0.150, -0.09]} rotation={[-0.7, 0, sd * 0.45]}>
            <coneGeometry args={[0.042, 0.32, 6]} /><meshStandardMaterial {...trim} />
          </mesh>
        ))}
        <mesh position={[0, 0.150, 0.150]} rotation={[0.25, 0, 0]}>
          <torusGeometry args={[0.046, 0.014, 6, 14]} /><meshStandardMaterial {...trim} />
        </mesh>
      </group>
    </group>
  );
}

// ════════════════════════════════════════════════════════════
//  THE OTHER EIGHT — LORDS (Batch 3D-25) · THE LADDER COMPLETE
//  ------------------------------------------------------------
//  Twelve lineages, three tiers, thirty-six bodies. These are the
//  last eight, and the Lord cards have one thing in common that
//  the Bebs and Champions do not: they are all SAINTS OR KINGS.
//  Haloes, crowns, thrones, crosses, robes of office. Terror's
//  Lord tier is not "the strongest form", it is "the form that
//  other beings kneel to" — and two of them have stopped being
//  bodies at all.
//
//  Two stat lines are worth staring at. H-01 Super Nova is Lyf 1
//  / Lyt 9 — it gave up its body for reach. H-09 Hyper Nova's
//  card stops printing Lyf/Lyt entirely and prints ANIMA 0 /
//  KHAO 10, the only card in the roster that changes what it is
//  measured in. Both are built to look like what happened to them.
// ════════════════════════════════════════════════════════════

// a halo, since six of the eight wear one. Cached per key/colour.
function Halo({ r = 0.34, color = "#ffd24a", op = 0.34, y = 0.03, z = -0.16, ringRef }) {
  return (
    <group position={[0, y, z]}>
      <mesh ref={ringRef}>
        <ringGeometry args={[r * 0.72, r, 40]} />
        <meshBasicMaterial color={color} transparent opacity={op} side={THREE.DoubleSide} toneMapped={false} depthWrite={false} />
      </mesh>
      <mesh position={[0, 0, 0.004]}>
        <ringGeometry args={[r * 0.73, r * 0.79, 40]} />
        <meshBasicMaterial color="#fff2c8" transparent opacity={op * 1.9} side={THREE.DoubleSide} toneMapped={false} />
      </mesh>
    </group>
  );
}

// ── SHADOW FOX LORD · Lyf 6 / Lyt 4 ──────────────────────────
// "Rulers of the Shadow Spheres, these beings tend to find
//  loyalty amongst only the most malevolent of D'aeterni."
// The Champion wore rank; the Lord wears SAINTHOOD. Gold disc
// behind the head, a crescent riding its top edge, a high black
// collar — and the green sprig still pinned to the chest, which
// is the only soft thing left on it and the reason it is worse.
export function ShadowFoxLordBody({ pal }) {
  pal = pal || getBeingPalette("shadow_fox");
  const coat = sfxCoatTex();
  const robe = robeCloth("sfxl_robe_v1", "#2a2634", "#141220", "#06060a", "#ffb26a");
  const gold = goldTex();
  const eye = eyeDisc("sfxl_eye_v1", "#fffbe8", "#ffcf5c", "#6a3a08", "rgba(10,6,4,0.9)", true);
  const rig = useBeingRig();
  const halo = useRef(); const earL = useRef(); const earR = useRef();
  useFrame((s) => {
    const t = s.clock.elapsedTime;
    if (halo.current) halo.current.material.opacity = 0.30 + Math.sin(t * 0.5) * 0.10;
    if (earL.current) earL.current.rotation.z = 0.16 + Math.sin(t * 1.0) * 0.04;
    if (earR.current) earR.current.rotation.z = -0.16 - Math.sin(t * 1.0 + 1.9) * 0.04;
  });
  const cloth = { map: robe, roughness: 1 };
  const fur = { map: coat, color: "#8e8a96", roughness: 1, emissive: SFX.fleck, emissiveIntensity: 0.05 };
  const trim = { map: gold, metalness: 0.75, roughness: 0.27 };
  return (
    <group>
      <mesh position={[0, 0.62, 0]} castShadow>
        <coneGeometry args={[0.48, 1.26, 16]} /><meshStandardMaterial {...cloth} />
      </mesh>
      <mesh position={[0, 1.18, 0]} scale={[1.02, 0.96, 0.86]} castShadow>
        <sphereGeometry args={[0.305, 14, 12]} /><meshStandardMaterial {...cloth} />
      </mesh>
      {[-1, 1].map((sd) => (
        <group key={"a" + sd} ref={sd < 0 ? rig.armL : rig.armR} position={[sd * 0.31, 1.12, 0.04]}>
          <mesh rotation={[0.38, 0, sd * 0.30]}><capsuleGeometry args={[0.084, 0.34, 4, 10]} /><meshStandardMaterial {...cloth} /></mesh>
          <mesh position={[sd * -0.10, -0.28, 0.18]}><sphereGeometry args={[0.068, 10, 8]} /><meshStandardMaterial {...fur} /></mesh>
        </group>
      ))}
      {/* THE HIGH COLLAR — it rises past the jaw */}
      <mesh position={[0, 1.46, -0.02]} rotation={[-0.10, 0, 0]}>
        <cylinderGeometry args={[0.255, 0.335, 0.42, 16, 1, true]} />
        <meshStandardMaterial {...cloth} side={THREE.DoubleSide} />
      </mesh>
      <mesh position={[0, 1.66, -0.03]} rotation={[-0.10, 0, 0]}>
        <torusGeometry args={[0.258, 0.024, 8, 22]} /><meshStandardMaterial {...trim} />
      </mesh>
      {/* the green sprig. The only soft thing left on it. */}
      <mesh position={[0.16, 1.24, 0.245]} rotation={[0.2, 0, -0.5]}>
        <coneGeometry args={[0.030, 0.135, 5]} /><meshStandardMaterial color="#5a8a3a" roughness={1} />
      </mesh>
      <mesh position={[0.135, 1.20, 0.245]} rotation={[0.2, 0, -0.9]}>
        <coneGeometry args={[0.022, 0.095, 5]} /><meshStandardMaterial color="#4a7a30" roughness={1} />
      </mesh>
      <group ref={rig.head} position={[0, 1.82, 0.03]}>
        {/* the disc, and the crescent riding its top edge */}
        <mesh ref={halo} position={[0, 0.04, -0.17]}>
          <circleGeometry args={[0.40, 34]} />
          <meshBasicMaterial color="#ffcf5c" transparent opacity={0.32} side={THREE.DoubleSide} toneMapped={false} depthWrite={false} />
        </mesh>
        <mesh position={[0, 0.04, -0.165]}><ringGeometry args={[0.385, 0.415, 34]} /><meshStandardMaterial {...trim} /></mesh>
        {Array.from({ length: 11 }, (_, i) => {
          const a = -0.95 + (i / 10) * 1.9;
          return (
            <mesh key={"cm" + i} position={[Math.sin(a) * 0.44, 0.04 + Math.cos(a) * 0.44, -0.16]}>
              <sphereGeometry args={[0.030 * (1 - Math.abs(i / 10 - 0.5) * 1.2), 8, 7]} />
              <meshStandardMaterial {...trim} emissive="#ffcf5c" emissiveIntensity={0.3} />
            </mesh>
          );
        })}
        <mesh scale={[0.96, 0.94, 1.05]} castShadow><sphereGeometry args={[0.195, 16, 14]} /><meshStandardMaterial {...fur} /></mesh>
        <mesh position={[0, -0.062, 0.190]} scale={[0.62, 0.56, 1.70]}>
          <sphereGeometry args={[0.104, 12, 10]} /><meshStandardMaterial {...fur} />
        </mesh>
        <mesh position={[0, -0.056, 0.335]}><sphereGeometry args={[0.030, 8, 8]} /><meshStandardMaterial color="#241820" roughness={0.55} /></mesh>
        <group ref={earL} position={[-0.116, 0.170, -0.014]}>
          <mesh rotation={[-0.05, -0.12, 0]}><coneGeometry args={[0.076, 0.315, 9]} /><meshStandardMaterial {...fur} /></mesh>
          <mesh position={[0, 0.006, 0.036]} rotation={[-0.05, -0.12, 0]}><coneGeometry args={[0.048, 0.245, 8]} /><meshStandardMaterial color={SFX.inner} emissive={SFX.inner} emissiveIntensity={0.6} roughness={0.9} /></mesh>
        </group>
        <group ref={earR} position={[0.116, 0.170, -0.014]}>
          <mesh rotation={[-0.05, 0.12, 0]}><coneGeometry args={[0.076, 0.315, 9]} /><meshStandardMaterial {...fur} /></mesh>
          <mesh position={[0, 0.006, 0.036]} rotation={[-0.05, 0.12, 0]}><coneGeometry args={[0.048, 0.245, 8]} /><meshStandardMaterial color={SFX.inner} emissive={SFX.inner} emissiveIntensity={0.6} roughness={0.9} /></mesh>
        </group>
        {[-1, 1].map((sd) => (
          <group key={"e" + sd} position={[sd * 0.094, 0.032, 0.128]} rotation={[0, sd * 0.42, 0]}>
            <mesh scale={[1, 1, 0.5]}><sphereGeometry args={[0.050, 8, 8]} /><meshStandardMaterial color="#08080c" roughness={1} /></mesh>
            <mesh position={[0, 0, 0.030]}><circleGeometry args={[0.041, 18]} /><meshBasicMaterial map={eye} toneMapped={false} /></mesh>
          </group>
        ))}
      </group>
    </group>
  );
}

// ── INVERTED SERPENT LORD · Lyf 4 / Lyt 6 ────────────────────
// "The Shadow Forest was and will always be home to these beings;
//  they will protect it at any cost."
// The Champion was a mouth. The Lord is a PRIEST — teal-green,
// robed, haloed, standing still. And the two serpents it used to
// BE now coil out of its collar on their own, which is the whole
// image: the thing that guards the forest has separated from the
// thing that bites for it.
const ISL = { skin: "#5e9a86", deep: "#1a3830", robe: "#132a26", snake: "#7fd8a8", eye: "#dff6ea" };
export function InvertedSerpentLordBody({ pal }) {
  pal = pal || getBeingPalette("inverted_serpent");
  const robe = robeCloth("isl_robe_v1", "#22463e", "#132a26", "#07110f", "#7fd8a8");
  const gold = goldTex();
  const eye = eyeDisc("isl_eye_v1", "#ffffff", "#dff6ea", "#2a4a40", "rgba(10,20,18,0.72)", true);
  const rig = useBeingRig();
  const halo = useRef(); const snakes = useRef([]);
  useFrame((s) => {
    const t = s.clock.elapsedTime;
    if (halo.current) halo.current.material.opacity = 0.28 + Math.sin(t * 0.45) * 0.10;
    // the two of them move independently of the body and of each other
    snakes.current.forEach((g0, i) => {
      if (!g0) return;
      g0.rotation.y = Math.sin(t * (0.7 + i * 0.23) + i * 2.4) * 0.55;
      g0.rotation.z = (i ? 1 : -1) * 0.3 + Math.sin(t * (0.9 + i * 0.17) + i) * 0.20;
    });
  });
  const cloth = { map: robe, roughness: 1 };
  const flesh = { color: ISL.skin, roughness: 0.72, emissive: ISL.snake, emissiveIntensity: 0.06 };
  const trim = { map: gold, metalness: 0.75, roughness: 0.28 };
  return (
    <group>
      <mesh position={[0, 0.60, 0]} castShadow>
        <coneGeometry args={[0.47, 1.22, 16]} /><meshStandardMaterial {...cloth} />
      </mesh>
      <mesh position={[0, 1.14, 0]} scale={[1.02, 0.96, 0.86]} castShadow>
        <sphereGeometry args={[0.300, 14, 12]} /><meshStandardMaterial {...cloth} />
      </mesh>
      {[-1, 1].map((sd) => (
        <group key={"a" + sd} ref={sd < 0 ? rig.armL : rig.armR} position={[sd * 0.30, 1.08, 0.04]}>
          <mesh rotation={[0.40, 0, sd * 0.30]}><capsuleGeometry args={[0.082, 0.32, 4, 10]} /><meshStandardMaterial {...cloth} /></mesh>
          <mesh position={[sd * -0.10, -0.27, 0.18]}><sphereGeometry args={[0.062, 10, 8]} /><meshStandardMaterial {...flesh} /></mesh>
        </group>
      ))}
      <mesh position={[0, 1.34, 0]}><torusGeometry args={[0.255, 0.026, 8, 22]} /><meshStandardMaterial {...trim} /></mesh>
      {/* THE TWO SERPENTS — out of the collar, on their own time */}
      {[-1, 1].map((sd, i) => (
        <group key={"sn" + i} ref={(el) => (snakes.current[i] = el)} position={[sd * 0.15, 1.30, 0.16]}>
          {[0, 1, 2, 3, 4, 5].map((j) => (
            <mesh key={j} position={[sd * Math.sin(j * 0.9) * 0.13, j * 0.088, Math.cos(j * 0.9) * 0.075]}>
              <sphereGeometry args={[Math.max(0.018, 0.052 - j * 0.005), 9, 8]} />
              <meshStandardMaterial color={ISL.snake} roughness={0.6} emissive={ISL.snake} emissiveIntensity={0.22} />
            </mesh>
          ))}
          <mesh position={[sd * Math.sin(5.4) * 0.14, 0.545, Math.cos(5.4) * 0.08]} scale={[1.25, 0.8, 1.35]}>
            <sphereGeometry args={[0.042, 10, 9]} />
            <meshStandardMaterial color={ISL.snake} roughness={0.55} emissive={ISL.snake} emissiveIntensity={0.3} />
          </mesh>
        </group>
      ))}
      <group ref={rig.head} position={[0, 1.66, 0.02]}>
        <mesh ref={halo} position={[0, 0.03, -0.17]}>
          <circleGeometry args={[0.38, 34]} />
          <meshBasicMaterial color="#ffd98a" transparent opacity={0.30} side={THREE.DoubleSide} toneMapped={false} depthWrite={false} />
        </mesh>
        <mesh position={[0, 0.03, -0.165]}><ringGeometry args={[0.365, 0.392, 34]} /><meshStandardMaterial {...trim} /></mesh>
        {/* the flat serpent head, worn like a mitre */}
        <mesh scale={[1.22, 0.94, 1.10]} castShadow>
          <sphereGeometry args={[0.200, 16, 14]} /><meshStandardMaterial {...flesh} />
        </mesh>
        <mesh position={[0, 0.048, -0.05]} scale={[1.70, 0.42, 0.95]}>
          <sphereGeometry args={[0.180, 16, 12]} /><meshStandardMaterial {...flesh} />
        </mesh>
        <mesh position={[0, -0.058, 0.170]} scale={[0.95, 0.60, 1.05]}>
          <sphereGeometry args={[0.130, 12, 10]} /><meshStandardMaterial {...flesh} />
        </mesh>
        {[-1, 1].map((sd) => (
          <group key={"e" + sd} position={[sd * 0.115, 0.030, 0.150]} rotation={[0, sd * 0.32, 0]}>
            <mesh scale={[1, 1, 0.5]}><sphereGeometry args={[0.056, 10, 8]} /><meshStandardMaterial color="#0f1c18" roughness={0.8} /></mesh>
            <mesh position={[0, 0, 0.032]}><circleGeometry args={[0.046, 18]} /><meshBasicMaterial map={eye} toneMapped={false} /></mesh>
          </group>
        ))}
      </group>
    </group>
  );
}

// ── ANTDROID LORD · Lyf 7 / Lyt 3 ────────────────────────────
// "It is an Ant's duty to conquer. Since the initial unfolding of
//  Time, they have, and until the final folding of Time, they will."
// The card drops the red armour for cold slate and gives it a
// CROWN OF SPIKES and two enormous orange lamps for eyes. The
// face has gone skull-like. It is the only Lord here that looks
// less alive than its Champion, which is the point — conquest as
// a duty outlasts the thing doing it.
const ADL = { shell: "#3f5257", deep: "#131c1f", crown: "#2a3a3e", lamp: "#ff5a10", gold: "#c9a84c" };
export function AntdroidLordBody({ pal }) {
  pal = pal || getBeingPalette("antdroid");
  const plate = plateTex("adl_plate_v1", "#546c72", "#38484d", "#0f1618", "rgba(190,220,225,0.45)");
  const rig = useBeingRig();
  const lamps = useRef([]); const antL = useRef(); const antR = useRef();
  useFrame((s) => {
    const t = s.clock.elapsedTime;
    // the lamps do not blink and do not breathe in step
    lamps.current.forEach((m, i) => {
      if (m) m.material.opacity = 0.62 + Math.sin(t * (0.9 + i * 0.31) + i * 2) * 0.28;
    });
    if (antL.current) antL.current.rotation.z = 0.62 + Math.sin(t * 1.2) * 0.05;
    if (antR.current) antR.current.rotation.z = -0.62 - Math.sin(t * 1.2 + 1.5) * 0.05;
  });
  const shell = { map: plate, metalness: 0.55, roughness: 0.5 };
  const dark = { color: ADL.deep, metalness: 0.5, roughness: 0.62 };
  const trim = { color: ADL.gold, metalness: 0.75, roughness: 0.32 };
  return (
    <group>
      {/* long thin legs — it is TALL and it is thin */}
      {[-1, 1].map((sd) => [0, 1, 2].map((k) => (
        <group key={"l" + sd + k} position={[sd * (0.16 + k * 0.035), 0, 0.12 - k * 0.20]}>
          <mesh position={[sd * 0.10, 0.60, 0]} rotation={[0, 0, sd * -0.28]}>
            <capsuleGeometry args={[0.024, 0.44, 3, 7]} /><meshStandardMaterial {...shell} />
          </mesh>
          <mesh position={[sd * 0.20, 0.20, 0.03]} rotation={[0, 0, sd * 0.18]}>
            <capsuleGeometry args={[0.017, 0.38, 3, 7]} /><meshStandardMaterial {...dark} />
          </mesh>
        </group>
      )))}
      <mesh position={[0, 0.94, -0.02]} scale={[0.86, 1.05, 0.82]} castShadow>
        <sphereGeometry args={[0.215, 14, 12]} /><meshStandardMaterial {...shell} />
      </mesh>
      {[0, 1, 2].map((i) => (
        <mesh key={"ab" + i} position={[0, 0.86 - i * 0.05, -0.26 - i * 0.15]} scale={[1 - i * 0.16, 1 - i * 0.16, 1]}>
          <sphereGeometry args={[0.145, 12, 10]} /><meshStandardMaterial {...shell} />
        </mesh>
      ))}
      {[-1, 1].map((sd) => (
        <mesh key={"a" + sd} ref={sd < 0 ? rig.armL : rig.armR} position={[sd * 0.235, 1.06, 0.04]} rotation={[0.3, 0, sd * 0.5]}>
          <capsuleGeometry args={[0.030, 0.28, 4, 8]} /><meshStandardMaterial {...shell} />
        </mesh>
      ))}
      <group ref={rig.head} position={[0, 1.34, 0.02]}>
        {/* a narrow skull, not the Beb's round shell */}
        <mesh scale={[0.92, 1.10, 0.90]} castShadow>
          <sphereGeometry args={[0.190, 16, 14]} /><meshStandardMaterial {...shell} />
        </mesh>
        <mesh position={[0, -0.135, 0.075]} scale={[0.72, 0.62, 0.85]}>
          <sphereGeometry args={[0.130, 12, 10]} /><meshStandardMaterial {...dark} />
        </mesh>
        {/* THE LAMPS — two orange suns where a face used to be */}
        {[-1, 1].map((sd, i) => (
          <group key={"e" + i} position={[sd * 0.088, 0.030, 0.150]}>
            <mesh scale={[1, 1, 0.5]}><sphereGeometry args={[0.078, 12, 12]} /><meshStandardMaterial color="#1a0c04" roughness={0.5} /></mesh>
            <mesh position={[0, 0, 0.030]}><circleGeometry args={[0.066, 18]} /><meshBasicMaterial color="#ff8a2e" toneMapped={false} /></mesh>
            <mesh ref={(el) => (lamps.current[i] = el)} position={[0, 0, 0.034]}>
              <circleGeometry args={[0.105, 20]} />
              <meshBasicMaterial color={ADL.lamp} transparent opacity={0.7} toneMapped={false} depthWrite={false} />
            </mesh>
          </group>
        ))}
        {/* mandible teeth, bared */}
        {[-2, -1, 0, 1, 2].map((k) => (
          <mesh key={"t" + k} position={[k * 0.030, -0.185, 0.135]} rotation={[Math.PI, 0, 0]}>
            <coneGeometry args={[0.016, 0.070, 5]} /><meshStandardMaterial color="#c4c0b4" roughness={0.6} />
          </mesh>
        ))}
        {/* THE CROWN — seven spikes off the skull */}
        {[-3, -2, -1, 0, 1, 2, 3].map((k) => (
          <mesh key={"c" + k} position={[k * 0.058, 0.190 - Math.abs(k) * 0.020, -0.03 - Math.abs(k) * 0.012]} rotation={[0, 0, k * 0.20]}>
            <coneGeometry args={[0.030, 0.24 - Math.abs(k) * 0.035, 4]} /><meshStandardMaterial {...shell} />
          </mesh>
        ))}
        <group ref={antL} position={[-0.075, 0.155, 0.06]}>
          <mesh rotation={[0, 0, 0.62]}><capsuleGeometry args={[0.011, 0.30, 3, 6]} /><meshStandardMaterial {...trim} /></mesh>
        </group>
        <group ref={antR} position={[0.075, 0.155, 0.06]}>
          <mesh rotation={[0, 0, -0.62]}><capsuleGeometry args={[0.011, 0.30, 3, 6]} /><meshStandardMaterial {...trim} /></mesh>
        </group>
      </group>
    </group>
  );
}

// ── DG-0 L'NFINI'S SHIELD · Lyf 8 / Lyt 2 ────────────────────
// "If seen, its usually in response to an Apocalypse level
//  situation. Origins unknown, but have numerous kingdoms set
//  within H-01 territory."
// Lyf EIGHT — the hardest thing in the roster. The card is a
// crusader: a pale helm with a BLACK CROSS for a face, a red
// inverted triangle at the heart on a gold chain. The Angel
// healed, the Farmer fed, and this one only ever arrives when
// it is already too late.
const DGL = { helm: "#e8e0cc", dark: "#1a1e22", red: "#c9302a", gold: "#d8b24a", robe: "#20262b" };
export function DG0LordBody({ pal }) {
  pal = pal || getBeingPalette("dg0");
  const robe = robeCloth("dgl_robe_v1", "#2c343a", "#1a1e22", "#0a0c0e", "#d8b24a");
  const gold = goldTex();
  const rig = useBeingRig();
  const heart = useRef();
  useFrame((s) => {
    const t = s.clock.elapsedTime;
    // one slow beat. It is not in a hurry; it never is.
    if (heart.current) {
      const k = 0.45 + Math.pow(Math.max(0, Math.sin(t * 0.7)), 3) * 0.55;
      heart.current.material.opacity = k;
      heart.current.scale.setScalar(0.94 + k * 0.14);
    }
  });
  const cloth = { map: robe, roughness: 1 };
  const trim = { map: gold, metalness: 0.76, roughness: 0.28 };
  const helm = { color: DGL.helm, metalness: 0.35, roughness: 0.42 };
  return (
    <group>
      <mesh position={[0, 0.60, 0]} castShadow>
        <coneGeometry args={[0.46, 1.24, 16]} /><meshStandardMaterial {...cloth} />
      </mesh>
      <mesh position={[0, 1.18, 0]} scale={[1.14, 1.00, 0.88]} castShadow>
        <sphereGeometry args={[0.315, 14, 12]} /><meshStandardMaterial {...cloth} />
      </mesh>
      {/* the pauldrons — square, heavy, gold-edged */}
      {[-1, 1].map((sd) => (
        <group key={"p" + sd} position={[sd * 0.335, 1.26, 0]}>
          <mesh rotation={[0, 0, sd * -0.18]} scale={[1.1, 0.62, 1.0]}>
            <boxGeometry args={[0.24, 0.28, 0.30]} /><meshStandardMaterial {...cloth} />
          </mesh>
          <mesh position={[0, -0.10, 0]} rotation={[0, 0, sd * -0.18]}>
            <boxGeometry args={[0.26, 0.035, 0.32]} /><meshStandardMaterial {...trim} />
          </mesh>
        </group>
      ))}
      {[-1, 1].map((sd) => (
        <group key={"a" + sd} ref={sd < 0 ? rig.armL : rig.armR} position={[sd * 0.33, 1.10, 0.02]}>
          <mesh rotation={[0.2, 0, sd * 0.12]}><capsuleGeometry args={[0.080, 0.34, 4, 10]} /><meshStandardMaterial {...cloth} /></mesh>
          <mesh position={[sd * 0.03, -0.31, 0.05]}><sphereGeometry args={[0.075, 10, 8]} /><meshStandardMaterial {...helm} /></mesh>
        </group>
      ))}
      {/* THE HEART — a red inverted triangle on a gold chain */}
      <mesh position={[0, 1.34, 0.20]}><torusGeometry args={[0.115, 0.012, 6, 22]} /><meshStandardMaterial {...trim} /></mesh>
      <mesh position={[0, 1.14, 0.245]} rotation={[0, 0, Math.PI]}>
        <coneGeometry args={[0.105, 0.185, 3]} />
        <meshStandardMaterial color={DGL.red} emissive={DGL.red} emissiveIntensity={0.65} roughness={0.35} />
      </mesh>
      <mesh ref={heart} position={[0, 1.14, 0.245]}>
        <sphereGeometry args={[0.165, 12, 10]} />
        <meshBasicMaterial color="#ff6a5a" transparent opacity={0.5} toneMapped={false} depthWrite={false} />
      </mesh>
      {/* THE HELM — pale, and a black cross for a face */}
      <group ref={rig.head} position={[0, 1.70, 0.01]}>
        <mesh scale={[0.94, 1.10, 0.96]} castShadow>
          <sphereGeometry args={[0.200, 16, 14]} /><meshStandardMaterial {...helm} />
        </mesh>
        <mesh position={[0, -0.135, 0.02]} scale={[0.94, 0.55, 0.96]}>
          <sphereGeometry args={[0.190, 14, 12]} /><meshStandardMaterial {...helm} />
        </mesh>
        <mesh position={[0, 0.035, 0.180]}><boxGeometry args={[0.062, 0.30, 0.020]} /><meshStandardMaterial color={DGL.dark} roughness={0.6} /></mesh>
        <mesh position={[0, 0.095, 0.180]}><boxGeometry args={[0.24, 0.062, 0.020]} /><meshStandardMaterial color={DGL.dark} roughness={0.6} /></mesh>
        <mesh position={[0, 0.215, 0]}><cylinderGeometry args={[0.042, 0.062, 0.09, 10]} /><meshStandardMaterial {...trim} /></mesh>
        <mesh position={[0, -0.20, 0]}><torusGeometry args={[0.165, 0.030, 8, 20]} /><meshStandardMaterial {...trim} /></mesh>
      </group>
    </group>
  );
}

// ── H-01 SUPER NOVA · Lyf 1 / Lyt 9 ──────────────────────────
// "Souls fused with technology; this is H-01's greatest
//  achievement. This proud race used this power to obliterate an
//  entire galaxy in the Q-2 quadrant of the Unstable World."
// LYF ONE. LYT NINE. The diplomat's descendant obliterated a
// galaxy, and the card shows why: there is barely a suit left.
// It is a standing column of light with a star over its head and
// a body you can see through. The Lyf-1 IS the design.
export function H01LordBody({ pal }) {
  pal = pal || getBeingPalette("h01");
  const rig = useBeingRig();
  const core = useRef(); const star = useRef(); const rays = useRef([]);
  // the star SPINS as a group and BREATHES as a disc — two refs,
  // because a group has no .material and setting one throws every
  // frame. (3D-26: this is the bug that shipped; see the note below.)
  const starDisc = useRef();
  useFrame((s) => {
    const t = s.clock.elapsedTime;
    if (core.current) {
      core.current.material.opacity = 0.42 + Math.sin(t * 0.9) * 0.14;
      core.current.scale.setScalar(1 + Math.sin(t * 0.9) * 0.03);
    }
    if (star.current) star.current.rotation.z = t * 0.25;
    if (starDisc.current) starDisc.current.material.opacity = 0.65 + Math.sin(t * 1.4) * 0.28;
    rays.current.forEach((m, i) => {
      if (!m) return;
      const p = 0.5 + Math.sin(t * 0.6 + i * 0.8) * 0.5;
      m.material.opacity = 0.06 + p * 0.16;
      m.scale.y = 0.9 + p * 0.35;
    });
  });
  const lit = (o, c = "#fff6d8") => (
    <meshBasicMaterial color={c} transparent opacity={o} toneMapped={false} depthWrite={false} />
  );
  return (
    <group>
      {/* the pillar of light it stands in */}
      {[-1.6, -0.7, 0.7, 1.6].map((k, i) => (
        <mesh key={"r" + i} ref={(el) => (rays.current[i] = el)} position={[k * 0.16, 1.0, -0.22]} rotation={[0, 0, -k * 0.10]}>
          <planeGeometry args={[0.16, 2.6]} />
          {lit(0.12, "#ffe9a8")}
        </mesh>
      ))}
      {/* the body — a suit that has mostly burned off */}
      {[-1, 1].map((sd) => (
        <group key={"l" + sd} ref={sd < 0 ? rig.legL : rig.legR} position={[sd * 0.125, 0, 0]}>
          <mesh position={[0, 0.36, 0]}><capsuleGeometry args={[0.078, 0.34, 4, 10]} />
            <meshStandardMaterial color="#f4efdd" emissive="#ffe9a8" emissiveIntensity={0.55} roughness={0.5} transparent opacity={0.9} />
          </mesh>
        </group>
      ))}
      <mesh position={[0, 0.94, 0]} scale={[1.02, 1.20, 0.86]} castShadow>
        <capsuleGeometry args={[0.195, 0.28, 5, 12]} />
        <meshStandardMaterial color="#fbf7e8" emissive="#ffe9a8" emissiveIntensity={0.6} roughness={0.45} transparent opacity={0.92} />
      </mesh>
      {/* the core you can see through it */}
      <mesh ref={core} position={[0, 0.98, 0]}>
        <sphereGeometry args={[0.30, 16, 14]} />{lit(0.45)}
      </mesh>
      {[-1, 1].map((sd) => (
        <group key={"a" + sd} ref={sd < 0 ? rig.armL : rig.armR} position={[sd * 0.235, 1.06, 0.02]}>
          <mesh rotation={[0, 0, sd * -0.42]}><capsuleGeometry args={[0.062, 0.34, 4, 10]} />
            <meshStandardMaterial color="#fbf7e8" emissive="#ffe9a8" emissiveIntensity={0.6} roughness={0.45} transparent opacity={0.9} />
          </mesh>
          <mesh position={[sd * 0.145, -0.30, 0.02]}><sphereGeometry args={[0.085, 10, 8]} />{lit(0.55)}</mesh>
        </group>
      ))}
      <group ref={rig.head} position={[0, 1.42, 0.01]}>
        <mesh castShadow><sphereGeometry args={[0.165, 16, 14]} />
          <meshStandardMaterial color="#fffdf4" emissive="#fff2c8" emissiveIntensity={0.85} roughness={0.35} />
        </mesh>
        <mesh><sphereGeometry args={[0.245, 14, 12]} />{lit(0.28)}</mesh>
        {/* THE STAR above the head — this is what it became */}
        <group ref={star} position={[0, 0.40, -0.02]}>
          <mesh ref={starDisc}><circleGeometry args={[0.115, 20]} />{lit(0.8)}</mesh>
          {Array.from({ length: 8 }, (_, i) => {
            const a = (i / 8) * Math.PI * 2;
            return (
              <mesh key={i} position={[Math.cos(a) * 0.155, Math.sin(a) * 0.155, 0]} rotation={[0, 0, a - Math.PI / 2]}>
                <coneGeometry args={[0.036, i % 2 ? 0.30 : 0.17, 3]} />{lit(0.6)}
              </mesh>
            );
          })}
        </group>
      </group>
    </group>
  );
}

// ── H-09 HYPER NOVA · Anima 0 / Khao 10 ──────────────────────
// "The stars bend to these being's will; their technology is
//  infused with their soul, which is how they overthrew the Z4Z
//  in quadrant S-3 of the Unstable World."
// ⚑ THE ONLY CARD IN THE ROSTER THAT CHANGES ITS OWN UNITS. Every
// other being prints Lyf/Lyt. This one prints ANIMA 0 / KHAO 10 —
// it does not have a body to defend any more, it has will. So the
// model has no head above the collar: a vast orange RING where a
// neck should be, dark armour under it, and fire at both hands.
const H09L = { armor: "#151a2a", ring: "#ff6a1a", ringHi: "#ffc46a", fire: "#ff8a2e" };
export function H09LordBody({ pal }) {
  pal = pal || getBeingPalette("h09");
  const suit = suitTex("h09l_suit_v1", "#2a3350", "#151a2a", "#070912", "#ff6a1a");
  const rig = useBeingRig();
  const ring = useRef(); const fire = useRef([]); const dot = useRef();
  useFrame((s) => {
    const t = s.clock.elapsedTime;
    if (ring.current) {
      ring.current.rotation.z = t * 0.22;
      ring.current.material.opacity = 0.72 + Math.sin(t * 0.8) * 0.20;
    }
    fire.current.forEach((m, i) => {
      if (!m) return;
      const p = (t * 0.9 + i * 0.33) % 1;
      m.position.y = -0.30 + p * 0.42;
      m.scale.setScalar(1 - p * 0.55);
      m.material.opacity = 0.7 * (1 - p);
    });
    if (dot.current) dot.current.material.opacity = 0.55 + Math.sin(t * 2.1) * 0.35;
  });
  const cloth = { map: suit, roughness: 0.7, metalness: 0.3 };
  return (
    <group>
      {[-1, 1].map((sd) => (
        <group key={"l" + sd} ref={sd < 0 ? rig.legL : rig.legR} position={[sd * 0.14, 0, 0]}>
          <mesh position={[0, 0.37, 0]}><capsuleGeometry args={[0.090, 0.34, 4, 10]} /><meshStandardMaterial {...cloth} /></mesh>
          <mesh position={[0, 0.085, 0.045]} scale={[1.1, 0.7, 1.5]}><sphereGeometry args={[0.096, 10, 8]} /><meshStandardMaterial color="#070912" roughness={0.75} /></mesh>
        </group>
      ))}
      <mesh position={[0, 0.94, 0]} scale={[1.16, 1.16, 0.88]} castShadow>
        <capsuleGeometry args={[0.230, 0.26, 5, 12]} /><meshStandardMaterial {...cloth} />
      </mesh>
      {/* the one white point at the belly the card puts there */}
      <mesh ref={dot} position={[0, 0.80, 0.215]}>
        <circleGeometry args={[0.030, 14]} />
        <meshBasicMaterial color="#ffffff" transparent opacity={0.8} toneMapped={false} />
      </mesh>
      {[-1, 1].map((sd) => (
        <group key={"a" + sd} ref={sd < 0 ? rig.armL : rig.armR} position={[sd * 0.30, 1.02, 0.03]}>
          <mesh rotation={[0.22, 0, sd * -0.30]}><capsuleGeometry args={[0.080, 0.36, 4, 10]} /><meshStandardMaterial {...cloth} /></mesh>
          {/* FIRE at the hands — it does not carry a weapon */}
          <mesh position={[sd * 0.17, -0.32, 0.06]}><sphereGeometry args={[0.080, 10, 8]} />
            <meshBasicMaterial color={H09L.fire} transparent opacity={0.55} toneMapped={false} depthWrite={false} />
          </mesh>
          {[0, 1, 2].map((k) => (
            <mesh key={"f" + k} ref={(el) => (fire.current[(sd > 0 ? 3 : 0) + k] = el)} position={[sd * 0.17, -0.30, 0.06]}>
              <coneGeometry args={[0.048, 0.16, 5]} />
              <meshBasicMaterial color={k % 2 ? "#ffd27a" : H09L.fire} transparent opacity={0.6} toneMapped={false} depthWrite={false} />
            </mesh>
          ))}
        </group>
      ))}
      {/* THE RING — where a head should be, and there isn't one */}
      <group ref={rig.head} position={[0, 1.34, 0]}>
        <mesh rotation={[-Math.PI / 2, 0, 0]}>
          <torusGeometry args={[0.36, 0.075, 10, 40]} />
          <meshStandardMaterial color={H09L.ring} emissive={H09L.ring} emissiveIntensity={0.9} roughness={0.4} />
        </mesh>
        <mesh ref={ring} rotation={[-Math.PI / 2, 0, 0]}>
          <ringGeometry args={[0.30, 0.50, 44]} />
          <meshBasicMaterial color={H09L.ringHi} transparent opacity={0.8} side={THREE.DoubleSide} toneMapped={false} depthWrite={false} />
        </mesh>
        {/* under it: a dark hollow where the helmet used to go */}
        <mesh position={[0, 0.10, 0]} scale={[1, 0.75, 1]}>
          <sphereGeometry args={[0.185, 14, 12]} />
          <meshStandardMaterial color="#05070e" roughness={0.9} />
        </mesh>
        <mesh position={[0, 0.02, 0]}>
          <cylinderGeometry args={[0.185, 0.235, 0.14, 16, 1, true]} />
          <meshStandardMaterial {...cloth} side={THREE.DoubleSide} />
        </mesh>
      </group>
    </group>
  );
}

// ── LUCKY DRAGON LORD · Lyf 3 / Lyt 7 ────────────────────────
// "The Greatest Ever, Vavixzu the D'aeternus, was tricked by
//  these dragons; now, these beings claim kingdoms within
//  Vavixzu's Shade, further obscuring fate in their favor."
// It tricked the Greatest Ever. The Champion's borrowed crescent
// has become a full heraldic display: heavy black horns, gold
// filigree fanning either side of the head like a coat of arms,
// and a jewel it did not pay for.
export function LuckyDragonLordBody({ pal }) {
  pal = pal || getBeingPalette("lucky_dragon");
  const scale = lkdScaleTex();
  const gold = goldTex();
  const eye = eyeDisc("lkdl_eye_v1", "#fff2d0", "#ff8a2e", "#4a1a06", "rgba(12,6,14,0.94)", true);
  const rig = useBeingRig();
  const fans = useRef([]); const gem = useRef();
  useFrame((s) => {
    const t = s.clock.elapsedTime;
    fans.current.forEach((m, i) => {
      if (m) m.rotation.z = (i % 2 ? 1 : -1) * (0.55 + Math.sin(t * 0.5 + i * 0.6) * 0.07);
    });
    if (gem.current) gem.current.material.opacity = 0.5 + Math.sin(t * 1.15) * 0.34;
  });
  const hide = { map: scale, color: LKD_GREEN.lord, roughness: 0.72 };
  const trim = { map: gold, metalness: 0.8, roughness: 0.24 };
  const horn = { color: "#18202a", metalness: 0.35, roughness: 0.5 };
  return (
    <group>
      <mesh position={[0, 0.54, -0.05]} scale={[0.92, 0.88, 1.16]} castShadow>
        <sphereGeometry args={[0.235, 14, 12]} /><meshStandardMaterial {...hide} />
      </mesh>
      {[-1, 1].map((sd) => (
        <group key={"l" + sd} ref={sd < 0 ? rig.legL : rig.legR} position={[sd * 0.135, 0, -0.02]}>
          <mesh position={[0, 0.30, 0]}><capsuleGeometry args={[0.056, 0.26, 4, 8]} /><meshStandardMaterial {...hide} /></mesh>
          <mesh position={[0, 0.06, 0.042]} scale={[1, 0.62, 1.4]}><sphereGeometry args={[0.062, 10, 8]} /><meshStandardMaterial color={LKD.deep} roughness={0.85} /></mesh>
        </group>
      ))}
      {[-1, 1].map((sd) => (
        <mesh key={"a" + sd} ref={sd < 0 ? rig.armL : rig.armR} position={[sd * 0.205, 0.60, 0.10]} rotation={[0.3, 0, sd * 0.42]}>
          <capsuleGeometry args={[0.042, 0.21, 4, 8]} /><meshStandardMaterial {...hide} />
        </mesh>
      ))}
      {/* THE HERALDRY — gold filigree fanning off both cheeks */}
      {[-1, 1].map((sd) => [0, 1, 2].map((k) => (
        <mesh key={"fn" + sd + k} ref={(el) => (fans.current[(sd > 0 ? 3 : 0) + k] = el)}
          position={[sd * (0.32 + k * 0.14), 0.98 + k * 0.10, -0.06]} rotation={[0, sd * -0.35, sd * 0.55]}>
          <coneGeometry args={[0.055 - k * 0.008, 0.42 - k * 0.07, 4]} /><meshStandardMaterial {...trim} />
        </mesh>
      )))}
      <group ref={rig.head} position={[0, 0.98, 0.07]}>
        <mesh scale={[1.14, 0.98, 1.08]} castShadow>
          <sphereGeometry args={[0.225, 16, 14]} /><meshStandardMaterial {...hide} />
        </mesh>
        <mesh position={[0, -0.076, 0.195]} scale={[1.02, 0.76, 1.22]}>
          <sphereGeometry args={[0.140, 12, 10]} /><meshStandardMaterial {...hide} />
        </mesh>
        {[-1, 1].map((sd) => (
          <group key={"e" + sd} position={[sd * 0.120, 0.024, 0.155]} rotation={[0, sd * 0.40, 0]}>
            <mesh scale={[1, 1, 0.5]}><sphereGeometry args={[0.064, 10, 8]} /><meshStandardMaterial color="#0c0a12" roughness={1} /></mesh>
            <mesh position={[0, 0, 0.036]}><circleGeometry args={[0.052, 20]} /><meshBasicMaterial map={eye} toneMapped={false} /></mesh>
          </group>
        ))}
        {/* the black horns — heavy, curling back and out */}
        {[-1, 1].map((sd) => (
          <group key={"h" + sd} position={[sd * 0.115, 0.150, -0.03]} scale={[sd, 1, 1]}>
            {[0, 1, 2, 3, 4].map((i) => {
              const f = i / 4;
              return (
                <mesh key={i} position={[0.055 + Math.sin(f * 1.5) * 0.24, f * 0.30, -0.03 - f * 0.20]}>
                  <sphereGeometry args={[0.062 * (1 - f * 0.68), 9, 8]} /><meshStandardMaterial {...horn} />
                </mesh>
              );
            })}
          </group>
        ))}
        {/* the jewel it did not pay for */}
        <mesh position={[0, 0.155, 0.165]}><octahedronGeometry args={[0.046, 0]} /><meshStandardMaterial color="#ff4a4a" emissive="#ff4a4a" emissiveIntensity={0.9} roughness={0.22} /></mesh>
        <mesh ref={gem} position={[0, 0.155, 0.165]}><sphereGeometry args={[0.105, 10, 8]} /><meshBasicMaterial color="#ff8a6a" transparent opacity={0.5} toneMapped={false} depthWrite={false} /></mesh>
      </group>
    </group>
  );
}

// ── PREMIER DRAGON LORD · Lyf 5 / Lyt 5 ──────────────────────
// "The First & Last, Zavixu the A'eternus, deemed these dragons
//  Worthy through rigorous trials and now have kingdoms of their
//  own within Zavixu's realm."
// Perfectly balanced, 5 and 5, and the only Lord on the roster
// who is SITTING DOWN. It has kingdoms; it does not need to
// stand up for you. A horned patriarch on a seat of stone with
// fire behind it and its hands at rest.
export function PremierDragonLordBody({ pal }) {
  pal = pal || getBeingPalette("premier_dragon");
  const scale = prdScaleTex();
  const robe = robeCloth("prdl_robe_v1", "#1e4a44", "#123029", "#08110f", "#ffd24a");
  const gold = goldTex();
  const eye = eyeDisc("prdl_eye_v1", "#fff0d0", "#ffb02e", "#4a2206", "rgba(10,6,4,0.92)", true);
  const rig = useBeingRig();
  const flame = useRef([]); const mist = useRef([]);
  useFrame((s) => {
    const t = s.clock.elapsedTime;
    flame.current.forEach((m, i) => {
      if (!m) return;
      const p = (t * 0.55 + i * 0.28) % 1;
      m.scale.set(1 - p * 0.4, 1 + p * 0.7, 1 - p * 0.4);
      m.material.opacity = 0.5 * (1 - p);
      m.position.y = 0.30 + p * 1.05;
    });
    mist.current.forEach((m, i) => {
      if (!m) return;
      const a = t * 0.22 + i * 1.6;
      m.position.set(Math.cos(a) * 0.72, 0.18 + Math.sin(t * 0.5 + i) * 0.07, Math.sin(a) * 0.72);
      m.material.opacity = 0.16 + Math.sin(t * 0.8 + i) * 0.09;
    });
  });
  const hide = { map: scale, color: PRD_HIDE, roughness: 0.76 };
  const cloth = { map: robe, roughness: 1 };
  const trim = { map: gold, metalness: 0.78, roughness: 0.26 };
  return (
    <group>
      {/* THE SEAT — stone, and it was here first */}
      <mesh position={[0, 0.20, -0.10]} receiveShadow castShadow>
        <boxGeometry args={[0.86, 0.40, 0.62]} /><meshStandardMaterial color="#3f3a36" roughness={1} />
      </mesh>
      <mesh position={[0, 0.62, -0.40]} receiveShadow castShadow>
        <boxGeometry args={[0.80, 0.62, 0.16]} /><meshStandardMaterial color="#37332f" roughness={1} />
      </mesh>
      {[-1, 1].map((sd) => (
        <mesh key={"ar" + sd} position={[sd * 0.44, 0.48, -0.10]}>
          <boxGeometry args={[0.10, 0.18, 0.58]} /><meshStandardMaterial color="#37332f" roughness={1} />
        </mesh>
      ))}
      {/* the fire behind the throne */}
      {[0, 1, 2, 3, 4].map((i) => (
        <mesh key={"fl" + i} ref={(el) => (flame.current[i] = el)} position={[(i - 2) * 0.22, 0.30, -0.62]}>
          <coneGeometry args={[0.10, 0.46, 6]} />
          <meshBasicMaterial color={i % 2 ? "#ffb04a" : "#ff6a1a"} transparent opacity={0.45} toneMapped={false} depthWrite={false} />
        </mesh>
      ))}
      {/* seated: thighs forward on the seat, shins down */}
      {[-1, 1].map((sd) => (
        <group key={"l" + sd} ref={sd < 0 ? rig.legL : rig.legR} position={[sd * 0.175, 0.42, 0.06]}>
          <mesh rotation={[Math.PI / 2, 0, 0]} position={[0, 0, 0.10]}>
            <capsuleGeometry args={[0.082, 0.26, 4, 10]} /><meshStandardMaterial {...cloth} />
          </mesh>
          <mesh position={[0, -0.22, 0.235]}><capsuleGeometry args={[0.072, 0.22, 4, 10]} /><meshStandardMaterial {...cloth} /></mesh>
          <mesh position={[0, -0.375, 0.285]} scale={[1.1, 0.6, 1.5]}><sphereGeometry args={[0.078, 10, 8]} /><meshStandardMaterial color={PRD.deep} roughness={0.85} /></mesh>
        </group>
      ))}
      <mesh position={[0, 0.78, -0.02]} scale={[1.12, 1.02, 0.90]} castShadow>
        <sphereGeometry args={[0.290, 14, 12]} /><meshStandardMaterial {...cloth} />
      </mesh>
      <mesh position={[0, 0.62, 0.16]} rotation={[0.1, 0, 0]}>
        <planeGeometry args={[0.16, 0.52]} /><meshStandardMaterial {...trim} side={THREE.DoubleSide} />
      </mesh>
      {/* hands at rest on the arms of the seat */}
      {[-1, 1].map((sd) => (
        <group key={"a" + sd} ref={sd < 0 ? rig.armL : rig.armR} position={[sd * 0.30, 0.84, 0.02]}>
          <mesh rotation={[0.25, 0, sd * 0.18]}><capsuleGeometry args={[0.072, 0.30, 4, 10]} /><meshStandardMaterial {...cloth} /></mesh>
          <mesh position={[sd * 0.13, -0.28, 0.06]}><sphereGeometry args={[0.070, 10, 8]} /><meshStandardMaterial {...hide} /></mesh>
        </group>
      ))}
      <group ref={rig.head} position={[0, 1.20, 0.04]}>
        <mesh scale={[1.05, 0.98, 1.16]} castShadow>
          <sphereGeometry args={[0.215, 16, 14]} /><meshStandardMaterial {...hide} />
        </mesh>
        <mesh position={[0, -0.066, 0.215]} scale={[0.92, 0.72, 1.32]}>
          <sphereGeometry args={[0.132, 12, 10]} /><meshStandardMaterial {...hide} />
        </mesh>
        {[-1, 1].map((sd) => (
          <group key={"e" + sd} position={[sd * 0.112, 0.030, 0.165]} rotation={[0, sd * 0.36, 0]}>
            <mesh scale={[1, 1, 0.5]}><sphereGeometry args={[0.058, 10, 8]} /><meshStandardMaterial color="#100c08" roughness={1} /></mesh>
            <mesh position={[0, 0, 0.032]}><circleGeometry args={[0.046, 20]} /><meshBasicMaterial map={eye} toneMapped={false} /></mesh>
          </group>
        ))}
        {/* the great horns, and the circlet under them */}
        {[-1, 1].map((sd) => (
          <group key={"h" + sd} position={[sd * 0.125, 0.140, -0.04]} scale={[sd, 1, 1]}>
            {[0, 1, 2, 3, 4].map((i) => {
              const f = i / 4;
              return (
                <mesh key={i} position={[0.05 + Math.sin(f * 1.35) * 0.20, f * 0.36, -0.02 - f * 0.16]}>
                  <sphereGeometry args={[0.058 * (1 - f * 0.66), 9, 8]} />
                  <meshStandardMaterial color="#241c26" metalness={0.3} roughness={0.55} />
                </mesh>
              );
            })}
          </group>
        ))}
        <mesh position={[0, 0.135, 0.02]} rotation={[Math.PI / 2, 0, 0]}>
          <torusGeometry args={[0.195, 0.020, 8, 22]} /><meshStandardMaterial {...trim} />
        </mesh>
      </group>
      {/* the teal mist the card wraps the throne in */}
      {[0, 1, 2, 3].map((i) => (
        <mesh key={"m" + i} ref={(el) => (mist.current[i] = el)}>
          <sphereGeometry args={[0.22, 10, 8]} />
          <meshBasicMaterial color={PRD.frill} transparent opacity={0.18} toneMapped={false} depthWrite={false} />
        </mesh>
      ))}
    </group>
  );
}

// ────────────────────────────────────────────────────────────
// THE FOUR BEINGS — primitives in canon palettes. On-canon per
// BeingSprite.js: Tiger ghost-teal prowler, Dragon crimson hulk,
// Z4Z clockwork knight, Xulan floating hooded vessel.
// ────────────────────────────────────────────────────────────

export function BeingMesh({ beingId, pal, tier = TIERS.BEB }) {
  pal = pal || getBeingPalette(beingId);
  if (beingId === "chaos_dragon") {
    // The Ascent worn as flesh (3D-9). Tier picks the body; callers
    // that don't pass tier get the Beb, exactly like the tiger.
    if (tier === TIERS.LORD) return <ChaosDragonLordBody pal={pal} />;
    if (tier === TIERS.CHAMPION) return <ChaosDragonChampionBody pal={pal} />;
    return <ChaosDragonBody pal={pal} />;
  }
  if (beingId === "z4z") {
    // The Ascent worn as flesh (3D-9). Tier picks the body; callers
    // that don't pass tier get the Demon (Beb), like every lineage.
    if (tier === TIERS.LORD) return <Z4ZLordBody pal={pal} />;
    if (tier === TIERS.CHAMPION) return <Z4ZChampionBody pal={pal} />;
    return <Z4ZBody pal={pal} />;
  }
  if (beingId === "xulan") {
    // The Ascent worn as flesh (3D-9). Tier picks the body; callers
    // that don't pass tier get the Wanderer (Beb), like every lineage.
    if (tier === TIERS.LORD) return <XulanLordBody pal={pal} />;
    if (tier === TIERS.CHAMPION) return <XulanChampionBody pal={pal} />;
    return <XulanBody pal={pal} />;
  }
  // ── THE OTHER EIGHT (3D-23) — Beb only for now. Champion and
  // Lord fall through to the Beb body exactly as the Dragon, Z4Z
  // and Xulan did between 3D-8 and 3D-9; the Ascent still READS
  // on them because PlayerBody wears the ring and the crown.
  // Sixteen bodies remain and each is an ADD, never a refactor.
  const OTHER8 = {
    shadow_fox:       [ShadowFoxBody, ShadowFoxChampionBody, ShadowFoxLordBody],
    inverted_serpent: [InvertedSerpentBody, InvertedSerpentChampionBody, InvertedSerpentLordBody],
    antdroid:         [AntdroidBody, AntdroidChampionBody, AntdroidLordBody],
    dg0:              [DG0Body, DG0ChampionBody, DG0LordBody],
    h01:              [H01Body, H01ChampionBody, H01LordBody],
    h09:              [H09Body, H09ChampionBody, H09LordBody],
    lucky_dragon:     [LuckyDragonBody, LuckyDragonChampionBody, LuckyDragonLordBody],
    premier_dragon:   [PremierDragonBody, PremierDragonChampionBody, PremierDragonLordBody],
  }[beingId];
  if (OTHER8) {
    const [Beb, Champ, Lord] = OTHER8;
    // Pick first, render second — JSX cannot take an expression as a
    // tag, and a capitalised local is what makes the choice legal.
    // Lord is not built for these eight yet and falls back to the
    // Champion, then the Beb; PlayerBody still wears the crown.
    const Body = tier === TIERS.LORD ? (Lord || Champ || Beb)
      : tier === TIERS.CHAMPION ? (Champ || Beb)
      : Beb;
    return <Body pal={pal} />;
  }
  // spectral_tiger (default) — the Ascent worn as flesh (3D-7/3D-8).
  // Tier picks the body; callers that don't pass tier get the Beb,
  // so every existing call site renders exactly what it did before.
  if (tier === TIERS.LORD) return <SpectralTigerLordBody pal={pal} />;
  if (tier === TIERS.CHAMPION) return <SpectralTigerChampionBody pal={pal} />;
  return <SpectralTigerBody pal={pal} />;
}

// ════════════════════════════════════════════════════════════
//  THE PUBLIC DOOR
// ════════════════════════════════════════════════════════════
//  <IEBeing being="spectral_tiger" tier="lord" />
//
//  being : one of BEINGS (defaults to the Spectral Tiger)
//  tier  : one of TIERS (defaults to Beb, so an omitted tier is
//          always the safe, lowest form)
//  pal   : optional palette override. Do not use it. It exists
//          only so Annihilation's existing call sites keep
//          working; canon colours come from this file.
//
//  Renders at the origin, facing +Z, standing in its neutral
//  pose and idling. Everything else — scale, placement, posing,
//  animation, lighting — belongs to the host game.
// ════════════════════════════════════════════════════════════
export function IEBeing({ being = BEINGS.SPECTRAL_TIGER, tier = TIERS.BEB, pal }) {
  return <BeingMesh beingId={being} tier={tier} pal={pal} />;
}

export default IEBeing;
